// Authed admin console (admin.html) — WI-P4-084.
//
// An owner/admin signs in and manages the whole workspace from one place:
//   Overview   — workspace identity + usage summary
//   Team       — members, invites (create/revoke), roles
//   API keys   — issue / rotate / revoke (secret shown exactly once)
//   Integrations — signed outbound events to another app + its reply key (WI-P4-117)
//   Widget     — branding/colors/welcome/launcher/origins editor (WI-P4-078)
//   Install    — embed snippet + endpoints for the developer handing-off
//   Switch from Drift — replace the Drift tag, keep page code working (WI-P4-120)
//   Compliance — retention policy, encryption and audit posture
//
// The bearer token lives in memory (and sessionStorage so refresh stays signed
// in — never the password). The token is only ever sent to brand.apiBase via
// the api.js wrapper.
import "../../shared/design-tokens/tokens.css";
import "./platform.css";
import { mountIcons } from "./icons.js";
import { brand, applyBrand } from "./brand.js";
import {
  ownerLogin,
  getAdminWidgetConfig,
  putWidgetConfig,
  getWorkspace,
  listMembers,
  changePassword,
  sendMemberPasswordReset,
  listInvites,
  createInvite,
  revokeInvite,
  changeMemberRole,
  removeMember,
  listKeys,
  createKey,
  listApplications,
  createApplication,
  listApplicationAccess,
  setApplicationAccess,
  rotateKey,
  revokeKey,
  listUsage,
  getUsageSummary,
  getRetentionPolicy,
  getAssistPolicy,
  putAssistPolicy,
  listAIProviders,
  addAIProvider,
  updateAIProvider,
  removeAIProvider,
  setDefaultAIProvider,
  testAIProvider,
  listProviderModels,
  listSavedProviderModels,
  getBillingState,
  createBillingCheckout,
  createBillingPortal,
  listWebhooks,
  getNotificationSettings,
  putNotificationSettings,
  createWebhook,
  updateWebhook,
  deleteWebhook,
  rotateWebhookSecret,
  testWebhook,
  listWebhookDeliveries,
  retryWebhookDelivery,
  importDrift,
  apiRoot,
} from "./api.js";
import { parseTeammatesCSV, parseContactsCSV, chunk, outcomeLabel, outcomeTone } from "./driftImport.js";

const TOKEN_KEY = "iris.admin.token";
const EMAIL_KEY = "iris.admin.email";
// Session role as minted by the server: "admin" for owners and admins,
// "support_agent" for operators AND viewers (viewers differ only by scope:
// no chat:messages.write / chat:channels.manage). Admin is for admins;
// everyone else is sent to the Operator Portal (the "wrong door" view), with
// view-only wording when the session's scopes say it cannot reply.
const ROLE_KEY = "iris.admin.role";
const ACCESS_KEY = "iris.admin.access";
const ADMIN_ROLE = "admin";
const VIEW_ONLY_ACCESS = "view-only";
const SCOPE_MESSAGES_WRITE = "chat:messages.write";
const SCOPE_CHANNELS_MANAGE = "chat:channels.manage";

// A non-admin session whose scope list is present but lacks both write
// scopes is a viewer. No scope list (older server) reads as an operator.
function accessFromScopes(role, scopes) {
  if (role === ADMIN_ROLE || !Array.isArray(scopes) || scopes.length === 0) return "";
  const have = new Set(scopes.map((s) => String(s).toLowerCase()));
  return have.has(SCOPE_MESSAGES_WRITE) || have.has(SCOPE_CHANNELS_MANAGE) ? "" : VIEW_ONLY_ACCESS;
}

const DEFAULT_CONFIG = {
  branding: {
    display_name: "",
    logo_url: "",
    primary_color: "#0f766e",
    accent_color: "#bb5a3c",
    widget_title: "",
  },
  welcome_message: "",
  composer_note: null, // null = product default, "" = hidden, text = custom
  offline_email_capture: true, // ask visitors for an e-mail when nobody is online
  offline_message: "", // "" = product default
  launcher_position: "bottom-right",
  allowed_origins: [],
  is_published: false,
  updated_at: "",
};

// Same text as the server's widgetconfig.DefaultComposerNote (used when the
// workspace never set its own note).
const DEFAULT_COMPOSER_NOTE = "Please don't share patient details in chat.";
const COMPOSER_NOTE_MAX = 200;
const OFFLINE_MESSAGE_MAX = 300;
const DEFAULT_OFFLINE_MESSAGE = "We're away right now. Leave your e-mail and we'll reply there.";

const LAUNCHER_OPTIONS = [
  { id: "bottom-right", label: "Bottom right" },
  { id: "bottom-left", label: "Bottom left" },
];

const ORG_ROLES = ["admin", "operator", "viewer"];

const SECTIONS = [
  { id: "overview", label: "Overview", icon: "layout-dashboard" },
  { id: "applications", label: "Applications", icon: "boxes" },
  { id: "usage", label: "Usage", icon: "activity" },
  { id: "billing", label: "Billing", icon: "credit-card" },
  { id: "team", label: "Team", icon: "users" },
  { id: "keys", label: "API keys", icon: "key-round" },
  { id: "integrations", label: "Integrations", icon: "plug" },
  { id: "widget", label: "Widget", icon: "palette" },
  { id: "install", label: "Install", icon: "code" },
  { id: "switch", label: "Switch from Drift", icon: "arrow-left-right" },
  { id: "assist", label: "Assist", icon: "sparkles" },
  { id: "compliance", label: "Compliance", icon: "shield" },
  { id: "account", label: "Account", icon: "user-cog" },
];

const state = {
  view: "login", // login | console | wrongDoor
  assist: null, // null = not loaded | see ensureAssistPolicy (policy + AI provider card state)
  integrations: null, // null = not loaded | see ensureIntegrations (webhook endpoints + inline test/delivery state)
  alerts: null, // null = not loaded | { available, settings, recipients, emailAvailable, slackAvailable, loadError }
  section: sectionFromHash() || "overview",
  // auth
  email: sessionStorage.getItem(EMAIL_KEY) || "",
  token: sessionStorage.getItem(TOKEN_KEY) || "",
  role: sessionStorage.getItem(ROLE_KEY) || roleFromToken(sessionStorage.getItem(TOKEN_KEY) || ""),
  viewOnly: sessionStorage.getItem(ACCESS_KEY) === VIEW_ONLY_ACCESS,
  loginError: "",
  loginBusy: false,

  // cached console data (cleared on sign-out)
  workspace: null,
  usage: null,
  usageSummary: null,
  billing: null,
  billingAvailable: null, // null = unknown | false = route not mounted (hide nav item)
  members: null,
  invites: null,
  keys: null,
  retention: null,
  applications: null,
  applicationAccess: null,

  // section-level transient ui state
  busy: false,
  sectionError: "",
  notice: "", // success banner text
  revealedSecret: null, // { title, secret, hint? } — shown exactly once
  inviteLink: null, // { email, url } when the API returns a raw invite token
  resetLink: null, // { email, url } when the API returns a raw password-reset link (no mailer)
  accountBusy: false,
  accountError: "",
  accountNotice: "",

  // Switch from Drift → Import (WI-P4-120): parsed files, last report
  driftImport: null, // null = pristine | see newDriftImportState()

  // widget editor (unchanged behavior from the focused editor)
  config: null,
  widgetBinding: "",
  loading: false,
  saveBusy: false,
  loadError: "",
  saveError: "",
  saveOk: false,
};

const panel = document.getElementById("panel");
const topMeta = document.getElementById("topMeta");

const $ = (id) => document.getElementById(id);
const esc = (s) =>
  String(s == null ? "" : s).replace(
    /[&<>"']/g,
    (c) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[c],
  );

function slugify(s) {
  return String(s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 28);
}

function isEmail(v) {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(v).trim());
}

function sectionFromHash() {
  const h = (window.location.hash || "").replace(/^#/, "");
  return SECTIONS.some((s) => s.id === h) ? h : "";
}

function normHex(v, fallback) {
  const s = String(v || "").trim();
  if (/^#[0-9a-fA-F]{6}$/.test(s)) return s.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(s)) {
    return (
      "#" +
      s
        .slice(1)
        .split("")
        .map((c) => c + c)
        .join("")
    ).toLowerCase();
  }
  return fallback;
}

function isHexish(v) {
  return /^#?[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/.test(String(v).trim());
}

// Dates: one style everywhere — "Oct 20, 2026", with a short time only where
// the time matters, never seconds.
function formatStamp(iso) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function formatDay(iso) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { dateStyle: "medium" });
}

// "2026-10" -> "October 2026"
function formatMonth(ym) {
  const m = /^(\d{4})-(\d{2})$/.exec(String(ym || ""));
  if (!m) return ym || "";
  return new Date(Number(m[1]), Number(m[2]) - 1, 1).toLocaleString(undefined, { month: "long", year: "numeric" });
}

// "in 7 days" / "tomorrow" / "today" / "expired"
function relativeDays(iso) {
  const t = new Date(iso).getTime();
  if (isNaN(t)) return iso;
  const days = Math.ceil((t - Date.now()) / 86400000);
  if (days < 0) return "expired";
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  return `in ${days} days`;
}

// Plain-word labels for every enum the API hands us. Unknown values fall back
// to a capitalised, de-underscored version rather than the raw token.
const LABELS = {
  plan: { trial: "Trial", developer: "Developer", team: "Team", business: "Business", compliance: "Compliance" },
  status: { active: "Active", trial: "Trial", suspended: "Suspended", pending: "Pending", inactive: "Inactive", revoked: "Revoked" },
  role: { owner: "Owner", admin: "Admin", operator: "Operator", support_agent: "Operator", viewer: "Viewer" },
  install_status: { not_configured: "Not installed yet", configured: "Snippet configured", verified: "Installed" },
  access_mode: { workspace: "All apps", restricted: "Only selected" },
  key_type: { secret: "Secret (server)", publishable: "Publishable (browser)" },
  subscription: { active: "Active", trialing: "Trial", past_due: "Past due", canceled: "Canceled", unpaid: "Unpaid" },
  delivery: { pending: "Waiting", delivered: "Delivered", failed: "Failed", dead_letter: "Gave up", skipped: "Skipped" },
};

function label(kind, value) {
  const v = String(value == null ? "" : value);
  if (!v) return "—";
  const known = LABELS[kind] && LABELS[kind][v];
  if (known) return known;
  const words = v.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// Short description under role pickers, in the words a support lead uses.
const ROLE_HINT = "Admins set things up · Operators answer conversations · Viewers can only read.";

function roleOptions(selected) {
  return ORG_ROLES.map((r) => `<option value="${r}" ${r === selected ? "selected" : ""}>${esc(label("role", r))}</option>`).join("");
}

function copyIdButton(id, what = "ID") {
  if (!id) return "";
  return `<button class="ad-mini" type="button" data-copy="${esc(id)}" title="${esc(id)}"><i data-lucide="copy"></i>Copy ${esc(what)}</button>`;
}

function tableWrap(html) {
  return `<div class="ad-tablewrap">${html}</div>`;
}

// Read the (unverified) payload of a session JWT. Only used to pick a view —
// the server enforces every permission; a forged claim just shows a different
// screen whose requests then fail.
function claimsFromToken(token) {
  try {
    const part = String(token || "").split(".")[1];
    if (!part) return {};
    const json = atob(part.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(part.length / 4) * 4, "="));
    return JSON.parse(json) || {};
  } catch {
    return {};
  }
}

function roleFromToken(token) {
  return String(claimsFromToken(token).role || "");
}

// Owners and admins both mint the "admin" session role. An unknown/empty role
// (older token) is allowed through: the first FORBIDDEN read redirects it.
function isAdminRole() {
  return !state.role || state.role === ADMIN_ROLE;
}

function errCode(e) {
  return (e && e.code ? String(e.code) : "").toUpperCase();
}

function isUnauthorized(e) {
  const code = errCode(e);
  return code === "UNAUTHORIZED" || code === "HTTP_401";
}

function isForbidden(e) {
  const code = errCode(e);
  return code === "FORBIDDEN" || code === "HTTP_403";
}

// Bounce to login on an expired/invalid token; returns true when handled.
function authGuard(e) {
  if (isUnauthorized(e)) {
    doSignOut("Your session expired. Please sign in again.");
    return true;
  }
  return false;
}

// Section reads are admin-only on the server, so a 403 on any of them means
// this session isn't an admin (stale build, or the role changed mid-session):
// show the wrong-door screen instead of a broken page. Returns true when handled.
function readGuard(e) {
  if (authGuard(e)) return true;
  if (isForbidden(e)) {
    showWrongDoor();
    return true;
  }
  return false;
}

function showWrongDoor() {
  state.view = "wrongDoor";
  state.loginBusy = false;
  state.busy = false;
  state.sectionError = "";
  state.notice = "";
  render();
}

// Operator Portal sign-in with the current email prefilled (the Portal reads ?email=).
function portalUrl(email = state.email) {
  if (!brand.consoleUrl) return "";
  try {
    const url = new URL(brand.consoleUrl, window.location.href);
    if (email) url.searchParams.set("email", email);
    return url.toString();
  } catch {
    return brand.consoleUrl;
  }
}

// ---- login view --------------------------------------------------------------

const loginView = () => `
  <span class="pf-eyebrow pf-eyebrow--teal"><span class="spark"></span>Admin console</span>
  <h1>Sign in to Admin</h1>
  <p class="sub">For workspace owners and admins.${
    brand.consoleUrl
      ? ` If you answer conversations, use the <a href="${esc(brand.consoleUrl)}" style="color:var(--uc-teal-700);font-weight:600">Operator Portal</a> instead.`
      : ""
  }</p>
  ${state.loginError ? `<div class="formerr" role="alert"><i data-lucide="shield-alert"></i><span>${esc(state.loginError)}</span></div>` : ""}
  <label class="fld" for="f_email"><span class="cap">Email</span><input class="inp" id="f_email" type="email" placeholder="you@company.com" value="${esc(state.email)}" autocomplete="email"><span class="field-err" id="emailErr"></span></label>
  <label class="fld" for="f_pass"><span class="cap">Password</span><input class="inp" id="f_pass" type="password" placeholder="Your password" autocomplete="current-password"><span class="field-err" id="passErr"></span></label>
  <div class="ad__actions" style="border:none;padding:0;margin-top:8px">
    <span class="saved-at">No account? <a href="signup.html" style="color:var(--uc-teal-700)">Create a workspace</a> · <a href="reset.html" style="color:var(--uc-teal-700)">Forgot password?</a></span>
    <div class="grow"></div>
    <button class="pf-btn pf-btn--primary pf-btn--lg" id="loginBtn" type="button">Sign in<i data-lucide="log-in"></i></button>
  </div>`;

// Non-admins who sign in here (operators, viewers) get a clear pointer to the
// Operator Portal instead of a console full of 403s. The server mints the
// same role for both; view-only access is read from the session's scopes.
function wrongDoorView() {
  const viewOnly = Boolean(state.viewOnly);
  const portal = portalUrl();
  const who = `<b>${esc(state.email || "a teammate")}</b>`;
  const intro = viewOnly
    ? `You're signed in as ${who} with view-only access.`
    : `You're signed in as ${who} with the operator role.`;
  const portalLine = viewOnly
    ? "You can read conversations in the <b>Operator Portal</b> — that's the place for you."
    : "Operators answer conversations in the <b>Operator Portal</b> — that's the place for you.";
  return `
  <span class="pf-eyebrow pf-eyebrow--teal"><span class="spark"></span>Admin</span>
  <h1>This is the admin area</h1>
  <p class="sub">${intro} Admin is where owners and admins set ${esc(brand.name)} up. ${portalLine}</p>
  <div class="ad-card" style="border-color:var(--uc-teal-600)">
    <div class="card-head"><i data-lucide="headset"></i>Operator Portal</div>
    ${
      portal
        ? `<p class="ad-empty" style="margin:0 0 14px">Sign in there with the same email and password.</p>
           <a class="pf-btn pf-btn--primary pf-btn--lg" href="${esc(portal)}"><i data-lucide="headset"></i>Open the Operator Portal</a>`
        : `<p class="ad-empty" style="margin:0">Ask your workspace admin for the Operator Portal link — you sign in there with the same email and password.</p>`
    }
  </div>
  <div class="ad__actions" style="border:none;padding:0;margin-top:8px">
    <span class="saved-at">Expecting to manage the workspace? Ask an owner to make you an admin.</span>
    <div class="grow"></div>
    <button class="pf-btn pf-btn--ghost" id="signOut" type="button"><i data-lucide="log-out"></i>Sign out</button>
  </div>`;
}

// ---- console chrome ----------------------------------------------------------

function navView() {
  const sections = SECTIONS.filter((s) => s.id !== "billing" || state.billingAvailable !== false);
  return `
    <div class="navsep">Workspace</div>
    ${sections
      .map(
        (s) =>
          `<button data-nav="${s.id}" class="${state.section === s.id ? "on" : ""}"><i data-lucide="${s.icon}"></i>${esc(s.label)}</button>`,
      )
      .join("")}`;
}

function banners() {
  return `
  ${state.sectionError ? `<div class="formerr" role="alert"><i data-lucide="shield-alert"></i><span>${esc(state.sectionError)}</span></div>` : ""}
  ${state.notice ? `<div class="formok"><i data-lucide="check"></i><span>${esc(state.notice)}</span></div>` : ""}`;
}

function loadingView(label) {
  return `<div class="ad__loading"><span class="btnspin"></span>${esc(label)}</div>`;
}

// ---- overview ----------------------------------------------------------------

function overviewView() {
  const w = state.workspace;
  return `
  <span class="pf-eyebrow pf-eyebrow--teal"><span class="spark"></span>Overview</span>
  <h1>${esc((w && w.name) || "Your workspace")}</h1>
  <p class="sub">Set up ${esc(brand.name)}, invite your team, and see how conversations are going.</p>
  ${banners()}
  ${twoPlacesCard()}
  <div class="ad-card">
    <div class="card-head"><i data-lucide="building-2"></i>Workspace</div>
    ${w ? workspaceFacts(w) : loadingView("Loading workspace…")}
  </div>
  <div class="ad-card">
    <div class="card-head"><i data-lucide="activity"></i>Usage — last 30 days</div>
    ${usageBars()}
  </div>
  <div class="ad-card">
    <div class="card-head"><i data-lucide="list-checks"></i>Setup checklist</div>
    ${setupChecklist()}
  </div>`;
}

function workspaceFacts(w) {
  const good = w.status === "active" || w.status === "trial";
  return `
    <div class="ad-kv">
      <div class="kv"><b>Name</b><span>${esc(w.name)}</span></div>
      <div class="kv"><b>Handle</b><span>${w.slug ? esc(w.slug) : "—"}</span></div>
      <div class="kv"><b>Plan</b><span>${w.plan ? esc(label("plan", w.plan)) : "—"}</span></div>
      <div class="kv"><b>Status</b><span><span class="ad-pill ${good ? "ad-pill--ok" : "ad-pill--warn"}">${esc(label("status", w.status))}</span></span></div>
      <div class="kv"><b>Created</b><span>${esc(formatDay(w.created_at))}</span></div>
    </div>`;
}

function usageBars() {
  if (state.usage === null) return loadingView("Loading usage…");
  const cutoff = Date.now() - 30 * 24 * 3600 * 1000;
  // Only prefix the app name when there is more than one app to tell apart.
  const multiApp = Array.isArray(state.applications) && state.applications.length > 1;
  const totals = new Map();
  for (const row of state.usage) {
    const t = new Date(row.day).getTime();
    if (!isNaN(t) && t < cutoff) continue;
    const name = multiApp && row.application_name ? `${row.application_name} · ${metricLabel(row.metric)}` : metricLabel(row.metric);
    totals.set(name, (totals.get(name) || 0) + Number(row.value || 0));
  }
  if (totals.size === 0) {
    return `<div class="ad-empty">No usage recorded yet. Once your widget or API starts handling conversations, daily metrics roll up here.</div>`;
  }
  const max = Math.max(...totals.values(), 1);
  return `<div class="usagebars">${[...totals.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(
      ([metric, value]) => `
      <div class="ub"><span>${esc(metric)}</span>
        <span class="track"><span class="fill" style="width:${Math.max(3, Math.round((value / max) * 100))}%"></span></span>
        <span class="val">${value.toLocaleString()}</span></div>`,
    )
    .join("")}</div>`;
}

function setupChecklist() {
  const apps = Array.isArray(state.applications) ? state.applications : [];
  const installed = apps.some((app) => (app.environments || []).some((env) => env.install_status === "verified"));
  const branding = state.config && state.config.branding;
  const messages = Array.isArray(state.usage)
    ? state.usage.filter((r) => r.metric === "message.sent").reduce((n, r) => n + Number(r.value || 0), 0)
    : 0;
  const portal = portalUrl();
  const items = [
    {
      done: !!(state.members && state.members.length > 1) || !!(state.invites && state.invites.length),
      label: "Invite your team",
      go: "team",
    },
    {
      done: !!(branding && (branding.display_name || branding.logo_url)),
      label: "Brand your chat widget",
      go: "widget",
    },
    {
      done: !!(state.config && state.config.is_published),
      label: "Publish the widget",
      go: "widget",
    },
    { done: installed, label: "Install on your website", go: "install" },
    {
      done: messages > 0,
      label: "Open the Portal and reply to a test conversation",
      href: portal,
    },
  ];
  return `<div class="usagebars">${items
    .map(
      (i) => `
    <div class="ub" style="grid-template-columns:22px 1fr auto">
      <i data-lucide="${i.done ? "check-circle-2" : "circle"}" style="width:16px;height:16px;color:${i.done ? "var(--uc-forest-500)" : "var(--uc-neutral-400)"}"></i>
      <span style="color:var(--uc-fg1)">${esc(i.label)}</span>
      ${
        i.go
          ? `<button class="ad-mini" data-nav="${i.go}">Open<i data-lucide="arrow-right"></i></button>`
          : i.href
            ? `<a class="ad-mini" href="${esc(i.href)}" target="_blank" rel="noopener">Open<i data-lucide="external-link"></i></a>`
            : `<span class="muted" style="font-size:12px">Portal link not set</span>`
      }
    </div>`,
    )
    .join("")}</div>`;
}

// One explanation of the two surfaces, on Overview only. The header carries
// the Portal link on every page.
function twoPlacesCard() {
  const portal = portalUrl();
  return `
  <div class="ad-card">
    <div class="card-head"><i data-lucide="split"></i>Two places to work<span class="grow"></span>${
      portal ? "" : `<span class="ad-pill ad-pill--warn">Portal link not set</span>`
    }</div>
    <p class="ad-empty" style="margin:0"><b>Admin</b> (here) is where owners and admins set ${esc(brand.name)} up. <b>The Operator Portal</b> is where your team answers conversations — share that link with operators.</p>
    <div class="ad__actions" style="border:none;padding:0;margin-top:12px">
      <button class="ad-mini" data-nav="team"><i data-lucide="users"></i>Invite your team</button>
      <div class="grow"></div>
      ${
        portal
          ? `<a class="ad-mini" href="${esc(portal)}" target="_blank" rel="noopener"><i data-lucide="headset"></i>Open the Operator Portal</a>`
          : `<span class="muted" style="font-size:12px">Ask your ${esc(brand.name)} contact for the Portal link.</span>`
      }
    </div>
  </div>`;
}

// ---- usage ---------------------------------------------------------------------

const METRIC_LABELS = {
  "conversation.opened": "Conversations opened",
  "message.sent": "Messages sent",
};

function metricLabel(metric) {
  return METRIC_LABELS[metric] || metric;
}

function usageView() {
  return `
  <span class="pf-eyebrow pf-eyebrow--teal"><span class="spark"></span>Usage</span>
  <h1>Usage & projections</h1>
  <p class="sub">Month-to-date usage with a straight-line month-end projection against your plan limits. Days roll over at midnight UTC.</p>
  ${banners()}
  ${usageSummaryCards()}
  <div class="ad-card">
    <div class="card-head"><i data-lucide="activity"></i>Last 30 days</div>
    ${usageBars()}
  </div>`;
}

function usageSummaryCards() {
  if (state.usageSummary === null) return loadingView("Loading usage summary…");
  if (state.usageSummary === undefined) {
    return `<div class="ad-card"><div class="ad-empty">Usage summary isn't available on this deployment yet.</div></div>`;
  }
  const s = state.usageSummary;
  const ops = s.operators || { active: 0 };
  const daysElapsed = Number(s.days_elapsed) || 0;
  const daysInMonth = Number(s.days_in_month) || 0;
  const opsActive = Number(ops.active) || 0;
  const opsLimit = ops.limit != null ? Number(ops.limit) : null;
  const monthPct = daysInMonth ? Math.round((daysElapsed / daysInMonth) * 100) : 0;
  return `
  <div class="ad-card">
    <div class="card-head"><i data-lucide="calendar"></i>${esc(formatMonth(s.month))}</div>
    <div class="ad-kv">
      <div class="kv"><b>Plan</b><span>${s.plan ? esc(label("plan", s.plan)) : "—"}</span></div>
      <div class="kv"><b>Month progress</b><span>Day ${daysElapsed} of ${daysInMonth} (${monthPct}%)</span></div>
      <div class="kv"><b>Active members</b><span>${opsActive}${opsLimit != null ? ` of ${opsLimit}` : ""}${seatPill(ops)}</span></div>
    </div>
  </div>
  <div class="ad-card">
    <div class="card-head"><i data-lucide="trending-up"></i>Month-to-date & projected</div>
    ${usageSummaryTable(s)}
  </div>`;
}

function seatPill(ops) {
  if (ops.limit == null) return "";
  if (ops.active > ops.limit) return ` <span class="ad-pill ad-pill--warn">Over plan</span>`;
  return "";
}

function usageSummaryTable(s) {
  const metrics = s.metrics || [];
  if (!metrics.length) {
    return `<div class="ad-empty">No metered usage yet this month.</div>`;
  }
  const rows = metrics
    .map((m) => {
      let pill = `<span class="ad-pill ad-pill--dim">No limit</span>`;
      if (m.limit != null) {
        if (m.month_to_date > m.limit) pill = `<span class="ad-pill ad-pill--warn">Over limit</span>`;
        else if (m.projected > m.limit) pill = `<span class="ad-pill ad-pill--warn">Projected over</span>`;
        else pill = `<span class="ad-pill ad-pill--ok">On track</span>`;
      }
      return `
      <tr>
        <td>${esc(metricLabel(m.metric))}</td>
        <td class="mono">${Number(m.month_to_date).toLocaleString()}</td>
        <td class="mono">${Number(m.projected).toLocaleString()}</td>
        <td class="mono">${m.limit != null ? Number(m.limit).toLocaleString() : "—"}</td>
        <td>${pill}</td>
      </tr>`;
    })
    .join("");
  return tableWrap(`
  <table class="ad-table">
    <thead><tr><th>Metric</th><th>Month to date</th><th>Projected</th><th>Plan limit</th><th>Status</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>`);
}

// ---- billing -------------------------------------------------------------------

function billingView() {
  return `
  <span class="pf-eyebrow pf-eyebrow--teal"><span class="spark"></span>Billing</span>
  <h1>Plan & billing</h1>
  <p class="sub">Your plan and invoices.</p>
  ${banners()}
  ${billingCards()}`;
}

function currentPlanLabel() {
  return label("plan", (state.workspace && state.workspace.plan) || "trial");
}

function billingCards() {
  if (state.billing === null) return `<div class="ad-card">${loadingView("Loading billing…")}</div>`;
  if (state.billing === undefined) {
    return `<div class="ad-card"><div class="card-head"><i data-lucide="badge-check"></i>Your plan</div><div class="ad-empty">You're on the <b>${esc(currentPlanLabel())}</b> plan. Online billing opens at launch — to change plans before then, contact your ${esc(brand.name)} account contact.</div></div>`;
  }
  const b = state.billing;
  const sub = b.subscription;
  const statusPill = sub
    ? sub.status === "active" || sub.status === "trialing"
      ? `<span class="ad-pill ad-pill--ok">${esc(label("subscription", sub.status))}</span>`
      : `<span class="ad-pill ad-pill--warn">${esc(label("subscription", sub.status))}</span>`
    : `<span class="ad-pill ad-pill--dim">None</span>`;
  return `
  <div class="ad-card">
    <div class="card-head"><i data-lucide="badge-check"></i>Subscription</div>
    <div class="ad-kv">
      <div class="kv"><b>Plan</b><span>${sub && sub.plan_id ? esc(label("plan", sub.plan_id)) : esc(currentPlanLabel())}</span></div>
      <div class="kv"><b>Status</b><span>${statusPill}</span></div>
      <div class="kv"><b>Renews</b><span>${sub && sub.current_period_end ? esc(formatDay(sub.current_period_end)) : "—"}</span></div>
    </div>
    ${
      sub && sub.has_billing_account && b.configured
        ? `<div class="ad__actions" style="margin-top:18px;padding-top:16px"><div class="grow"></div><button class="ad-mini" id="portalBtn" type="button"><i data-lucide="external-link"></i>Manage billing</button></div>`
        : ""
    }
  </div>
  <div class="ad-card">
    <div class="card-head"><i data-lucide="arrow-up-circle"></i>Change plan</div>
    ${
      !b.configured
        ? `<div class="ad-empty">Online plan changes open at launch. To change plans before then, contact your ${esc(brand.name)} account contact.</div>`
        : (b.purchasable_plans || []).length === 0
          ? `<div class="ad-empty">No self-serve plans are available right now.</div>`
          : `<div class="usagebars">${b.purchasable_plans
              .map(
                (p) => `
            <div class="ub" style="grid-template-columns:1fr auto">
              <span style="color:var(--uc-fg1)">${esc(label("plan", p))}</span>
              <button class="ad-mini" data-checkout-plan="${esc(p)}" ${sub && sub.plan_id === p && sub.status === "active" ? "disabled" : ""}>
                ${sub && sub.plan_id === p && sub.status === "active" ? "Current plan" : `Upgrade<i data-lucide="arrow-right"></i>`}
              </button>
            </div>`,
              )
              .join("")}</div>`
    }
  </div>`;
}

function wireBilling() {
  const portal = $("portalBtn");
  if (portal)
    portal.onclick = async () => {
      try {
        const r = await createBillingPortal(state.token);
        if (r && r.url) window.location.href = r.url;
      } catch (e) {
        if (authGuard(e)) return;
        state.sectionError = mapBillingError(e);
        render();
      }
    };
  panel.querySelectorAll("[data-checkout-plan]").forEach((btn) => {
    btn.onclick = async () => {
      btn.disabled = true;
      try {
        const r = await createBillingCheckout(state.token, btn.getAttribute("data-checkout-plan"));
        if (r && r.url) window.location.href = r.url;
      } catch (e) {
        btn.disabled = false;
        if (authGuard(e)) return;
        state.sectionError = mapBillingError(e);
        render();
      }
    };
  });
}

function mapBillingError(e) {
  const code = errCode(e);
  if (code === "BILLING_NOT_CONFIGURED") return "Online billing isn't open yet.";
  if (code === "NO_BILLING_ACCOUNT") return "No billing account exists yet — complete a checkout first.";
  return e.message || "Billing request failed.";
}

// ---- team ----------------------------------------------------------------------

function teamView() {
  return `
  <span class="pf-eyebrow pf-eyebrow--teal"><span class="spark"></span>Team</span>
  <h1>Members & invites</h1>
  <p class="sub">Invite teammates to your workspace. Admins set things up here; operators answer conversations in the Operator Portal.</p>
  ${banners()}
  ${state.inviteLink ? inviteLinkCard() : ""}
  ${state.resetLink ? resetLinkCard() : ""}
  <div class="ad-card">
    <div class="card-head"><i data-lucide="mail-plus"></i>Invite a teammate</div>
    <div class="ad__grid">
      <label class="fld" for="inv_email"><span class="cap">Email</span><input class="inp" id="inv_email" type="email" placeholder="teammate@company.com"><span class="field-err" id="invErr"></span></label>
      <label class="fld" for="inv_role"><span class="cap">Role</span>
        <select class="sel" id="inv_role">${roleOptions("operator")}</select>
        <span class="fldhint">${ROLE_HINT}</span>
      </label>
    </div>
    <div class="ad__actions" style="border:none;padding:0;margin-top:4px">
      <div class="grow"></div>
      <button class="pf-btn pf-btn--primary" id="invBtn" type="button"><i data-lucide="send"></i>Send invite</button>
    </div>
  </div>
  <div class="ad-card">
    <div class="card-head"><i data-lucide="hourglass"></i>Pending invites</div>
    ${invitesTable()}
  </div>
  <div class="ad-card">
    <div class="card-head"><i data-lucide="users"></i>Members</div>
    ${membersTable()}
  </div>`;
}

function inviteLinkCard() {
  const l = state.inviteLink;
  return `
  <div class="ad-card" style="border-color:var(--uc-teal-600)">
    <div class="card-head"><i data-lucide="link"></i>Invite link for ${esc(l.email)}<span class="grow"></span><button class="ad-mini" id="dismissInvLink"><i data-lucide="x"></i>Dismiss</button></div>
    <p class="ad-empty" style="margin:0 0 8px">No email transport is configured in this environment, so share this link with your teammate directly. It is shown once.</p>
    <div class="ad-secret"><code>${esc(l.url)}</code><button class="ad-mini" data-copy="${esc(l.url)}"><i data-lucide="copy"></i>Copy</button></div>
  </div>`;
}

function resetLinkCard() {
  const l = state.resetLink;
  return `
  <div class="ad-card" style="border-color:var(--uc-teal-600)">
    <div class="card-head"><i data-lucide="key-round"></i>Password reset link for ${esc(l.email)}<span class="grow"></span><button class="ad-mini" id="dismissResetLink"><i data-lucide="x"></i>Dismiss</button></div>
    <p class="ad-empty" style="margin:0 0 8px">No email transport is configured in this environment, so hand this link to your teammate directly. It works once and expires in about an hour.</p>
    <div class="ad-secret"><code>${esc(l.url)}</code><button class="ad-mini" data-copy="${esc(l.url)}"><i data-lucide="copy"></i>Copy</button></div>
  </div>`;
}

function invitesTable() {
  if (state.invites === null) return loadingView("Loading invites…");
  if (!state.invites.length) return `<div class="ad-empty">No pending invites.</div>`;
  return tableWrap(`<table class="ad-table"><thead><tr><th>Email</th><th>Role</th><th>Expires</th><th></th></tr></thead><tbody>
    ${state.invites
      .map(
        (i) => `<tr>
        <td>${esc(i.email)}</td>
        <td><span class="ad-pill ad-pill--dim">${esc(label("role", i.org_role))}</span></td>
        <td class="muted" title="${esc(formatDay(i.expires_at))}">${esc(relativeDays(i.expires_at))}</td>
        <td class="actions"><button class="ad-mini danger" data-revoke-invite="${esc(i.invitation_id)}"><i data-lucide="trash-2"></i>Revoke</button></td>
      </tr>`,
      )
      .join("")}</tbody></table>`);
}

function membersTable() {
  if (state.members === null) return loadingView("Loading members…");
  if (!state.members.length) return `<div class="ad-empty">No members yet.</div>`;
  return tableWrap(`<table class="ad-table"><thead><tr><th>Teammate</th><th>Role</th><th>Status</th><th></th></tr></thead><tbody>
    ${state.members
      .map((m) => {
        const isOwner = m.org_role === "owner";
        const roleCell = isOwner
          ? `<span class="ad-pill ad-pill--ok">Owner</span>`
          : `<select class="sel sel--mini" data-role-user="${esc(m.user_id)}" data-role-name="${esc(m.display_name || m.email || "this teammate")}" data-role-current="${esc(m.org_role)}" aria-label="Role for ${esc(m.display_name || m.email || "teammate")}">${roleOptions(m.org_role)}</select>`;
        // A native (email + password) member can be sent a reset link; owners only
        // reset their own from Account. SSO-only members have no email here.
        const canReset = !!m.email && m.status === "active" && !isOwner;
        const who = m.display_name || m.email || "this teammate";
        // Name first, then the sign-in email; the id only when we have neither.
        const userCell = m.display_name || m.email
          ? `${esc(m.display_name || m.email)}<div class="muted">${m.display_name && m.email ? `${esc(m.email)} · ` : ""}<span style="white-space:nowrap">joined ${esc(formatDay(m.created_at))}</span></div>`
          : `<span class="mono">${esc(m.user_id.slice(0, 8))}…</span>`;
        return `<tr>
        <td>${userCell}</td>
        <td>${roleCell}</td>
        <td>${m.status === "active" ? `<span class="ad-pill ad-pill--ok">Active</span>` : `<span class="ad-pill ad-pill--dim">${esc(label("status", m.status))}</span>`}</td>
        <td class="actions">${
          canReset
            ? `<button class="ad-mini" data-reset-member="${esc(m.user_id)}" data-reset-email="${esc(m.email)}" title="Send password reset link" aria-label="Send password reset link to ${esc(who)}"><i data-lucide="key-round"></i></button> `
            : ""
        }${isOwner ? "" : `<button class="ad-mini danger" data-remove-member="${esc(m.user_id)}" aria-label="Remove ${esc(who)}"><i data-lucide="user-minus"></i>Remove</button>`}</td>
      </tr>`;
      })
      .join("")}</tbody></table>`);
}

// ---- applications / projects ----------------------------------------------------

function applicationsView() {
  const apps = state.applications;
  const access = state.applicationAccess;
  const restricted = Array.isArray(access) ? access.filter((item) => item.mode === "restricted").length : 0;
  return `
  <span class="pf-eyebrow pf-eyebrow--teal"><span class="spark"></span>Applications</span>
  <h1>Applications</h1>
  <p class="sub">Most workspaces have one app — the website or product your chat widget lives on. Add another only if you support more than one product.</p>
  ${banners()}
  <div class="ad-card">
    <div class="card-head"><i data-lucide="plus"></i>Add an application</div>
    <div class="ad__grid">
      <label class="fld" for="app_name"><span class="cap">Name</span><input class="inp" id="app_name" placeholder="Patient Portal"></label>
      <label class="fld" for="app_slug"><span class="cap">Short name</span><input class="inp mono" id="app_slug" placeholder="patient-portal"><span class="fldhint">Letters, numbers and hyphens. Fills in from the name.</span></label>
      <label class="fld span2" for="app_origins"><span class="cap">Website addresses</span><textarea class="inp mono" id="app_origins" placeholder="https://www.yourclinic.com"></textarea><span class="fldhint">Where the chat widget will appear. One per line. You can change these later under Widget.</span></label>
    </div>
    <div class="ad__actions" style="border:none;padding:0;margin-top:4px"><div class="grow"></div><button class="pf-btn pf-btn--primary" id="appCreateBtn"><i data-lucide="plus"></i>Add application</button></div>
  </div>
  <div class="ad-card">
    <div class="card-head"><i data-lucide="boxes"></i>Your applications<span class="grow"></span><span class="ad-pill ad-pill--dim">${Array.isArray(apps) ? apps.length : 0}</span></div>
    ${applicationsList()}
  </div>
  <div class="ad-card">
    <div class="card-head"><i data-lucide="user-round-cog"></i>Who can see which application<span class="grow"></span>${restricted ? `<span class="ad-pill ad-pill--dim">${restricted} restricted</span>` : ""}</div>
    <p class="ad-empty" style="margin:0 0 12px">Teammates set to <b>All apps</b> see every conversation. Teammates set to <b>Only selected</b> see conversations only from the ticked apps — with none ticked they see nothing.</p>
    ${applicationAccessTable()}
  </div>`;
}

function applicationsList() {
  if (state.applications === null) return loadingView("Loading applications…");
  if (!state.applications.length) return `<div class="ad-empty">No applications yet.</div>`;
  return `<div class="ad__stack">${state.applications.map((app) => {
    const environments = Array.isArray(app.environments) ? app.environments : [];
    return `<div class="ad-card" style="margin:0">
      <div class="card-head"><i data-lucide="box"></i>${esc(app.name)} ${app.is_default ? `<span class="ad-pill ad-pill--ok">Default</span>` : ""}<span class="grow"></span>${
        app.status === "active" ? "" : `<span class="ad-pill ad-pill--dim">${esc(label("status", app.status))}</span>`
      }${copyIdButton(app.application_id)}</div>
      ${app.is_default ? "" : `<div class="muted" style="margin:-6px 0 12px;font-size:12.5px">Short name <span class="mono">${esc(app.slug)}</span></div>`}
      ${tableWrap(`<table class="ad-table"><thead><tr><th>Environment</th><th>Website addresses</th><th>Install</th><th>Checked</th></tr></thead><tbody>${environments.map((env) => `<tr>
        <td>${esc(env.name)} ${env.is_default ? `<span class="ad-pill ad-pill--dim">Default</span>` : ""}<div style="margin-top:6px">${copyIdButton(env.environment_id)}</div></td>
        <td class="muted">${(env.allowed_origins || []).length ? (env.allowed_origins || []).map(esc).join("<br>") : "None yet"}</td>
        <td><span class="ad-pill ${env.install_status === "verified" ? "ad-pill--ok" : "ad-pill--dim"}">${esc(label("install_status", env.install_status))}</span></td>
        <td class="muted">${env.last_verified_at ? esc(formatDay(env.last_verified_at)) : "Never"}</td>
      </tr>`).join("")}</tbody></table>`)}
    </div>`;
  }).join("")}</div>`;
}

function applicationAccessTable() {
  if (state.applicationAccess === null) return loadingView("Loading access…");
  if (!state.applicationAccess.length) return `<div class="ad-empty">No active workspace members.</div>`;
  const apps = Array.isArray(state.applications) ? state.applications : [];
  return tableWrap(`<table class="ad-table"><thead><tr><th>Teammate</th><th>Access</th><th>Applications</th><th></th></tr></thead><tbody>${state.applicationAccess.map((member) => {
    const owner = member.org_role === "owner";
    const assigned = new Set(member.application_ids || []);
    const known = Array.isArray(state.members) ? state.members.find((m) => m.user_id === member.user_id) : null;
    const who = known && (known.display_name || known.email) ? esc(known.display_name || known.email) : `<span class="mono">${esc(member.user_id.slice(0, 8))}…</span>`;
    const locked = member.mode === "workspace";
    return `<tr><td>${who}<div class="muted">${esc(label("role", member.org_role))}</div></td>
      <td>${owner ? `<span class="ad-pill ad-pill--ok">All apps</span>` : `<select class="sel sel--mini" data-access-mode="${esc(member.user_id)}" aria-label="Access for ${who}"><option value="workspace" ${locked ? "selected" : ""}>All apps</option><option value="restricted" ${member.mode === "restricted" ? "selected" : ""}>Only selected</option></select>`}</td>
      <td>${owner ? `<span class="muted">Every app</span>` : `<div class="ad-checks ${locked ? "is-locked" : ""}" data-access-checks="${esc(member.user_id)}" title="${locked ? "Switch to 'Only selected' to choose" : ""}">${apps.map((app) => `<label><input type="checkbox" data-access-app="${esc(member.user_id)}" value="${esc(app.application_id)}" ${assigned.has(app.application_id) ? "checked" : ""} ${locked ? "disabled" : ""}> ${esc(app.name)}</label>`).join("")}</div>`}</td>
      <td class="actions">${owner ? "" : `<button class="ad-mini" data-save-access="${esc(member.user_id)}"><i data-lucide="save"></i>Save</button>`}</td></tr>`;
  }).join("")}</tbody></table>`);
}

// ---- api keys -------------------------------------------------------------------

function keysView() {
  return `
  <span class="pf-eyebrow pf-eyebrow--teal"><span class="spark"></span>API keys</span>
  <h1>API keys</h1>
  <p class="sub">Keys let your own software talk to ${esc(brand.name)}. You only need one if a developer is connecting another system. Each key is shown once.</p>
  ${banners()}
  ${state.revealedSecret ? secretCard() : ""}
  <div class="ad-card">
    <div class="card-head"><i data-lucide="key-round"></i>Create a key</div>
    <div class="ad__grid">
      <label class="fld" for="key_name"><span class="cap">Name</span><input class="inp" id="key_name" placeholder="Production backend"><span class="field-err" id="keyErr"></span></label>
      <label class="fld" for="key_type"><span class="cap">Type</span>
        <select class="sel" id="key_type"><option value="secret" selected>Secret — for your server (iris_sk_…)</option><option value="publishable">Publishable — safe in a browser (iris_pub_…)</option></select>
      </label>
      <label class="fld span2" for="key_binding"><span class="cap">App</span>
        <select class="sel" id="key_binding">${applicationEnvironmentOptions()}</select><span class="fldhint">The key works for this app only.</span>
      </label>
      <label class="ad-check span2" for="key_act_as">
        <input type="checkbox" id="key_act_as">
        <span><b>Reply as a team member</b>
        <span class="fldhint">For a trusted app, such as unipy, that answers customers for your team. The key can then read and answer conversations as any active owner, admin or operator it names, across every app they can see. Secret keys only. (Integrations has a one-click version.)</span></span>
      </label>
    </div>
    <div class="ad__actions" style="border:none;padding:0;margin-top:4px">
      <div class="grow"></div>
      <button class="pf-btn pf-btn--primary" id="keyBtn" type="button"><i data-lucide="plus"></i>Create key</button>
    </div>
  </div>
  <div class="ad-card">
    <div class="card-head"><i data-lucide="list"></i>Keys</div>
    ${keysTable()}
  </div>`;
}

function defaultApplicationEnvironmentBinding() {
  if (!Array.isArray(state.applications)) return "";
  const pairs = state.applications.flatMap((app) =>
    (app.environments || [])
      .filter((env) => env.status === "active")
      .map((env) => ({ app, env })),
  );
  const selected =
    pairs.find(({ app, env }) => app.is_default && env.is_default) || pairs[0];
  return selected
    ? `${selected.app.application_id}|${selected.env.environment_id}`
    : "";
}

function currentWidgetBinding() {
  if (state.widgetBinding) return state.widgetBinding;
  if (state.config?.application_id && state.config?.environment_id) {
    state.widgetBinding = `${state.config.application_id}|${state.config.environment_id}`;
    return state.widgetBinding;
  }
  state.widgetBinding = defaultApplicationEnvironmentBinding();
  return state.widgetBinding;
}

function parseApplicationEnvironmentBinding(value = currentWidgetBinding()) {
  const [applicationID = "", environmentID = ""] = String(value).split("|");
  return { applicationID, environmentID };
}

function applicationEnvironmentOptions(selectedValue = "") {
  if (!Array.isArray(state.applications) || !state.applications.length) return `<option value="">Default application / Production</option>`;
  const selected = selectedValue || defaultApplicationEnvironmentBinding();
  return state.applications.flatMap((app) => (app.environments || []).filter((env) => env.status === "active").map((env) => {
    const value = `${app.application_id}|${env.environment_id}`;
    return `<option value="${esc(value)}" ${value === selected ? "selected" : ""}>${esc(app.name)} · ${esc(env.name)}</option>`;
  })).join("");
}

function secretCard() {
  const s = state.revealedSecret;
  return `
  <div class="ad-card" style="border-color:var(--uc-teal-600)">
    <div class="card-head"><i data-lucide="eye"></i>${esc(s.title)}<span class="grow"></span><button class="ad-mini" id="dismissSecret"><i data-lucide="x"></i>I've stored it</button></div>
    <p class="ad-empty" style="margin:0 0 8px">Copy this secret now — for your security it is never shown again. Store it in your secret manager.</p>
    <div class="ad-secret"><code>${esc(s.secret)}</code><button class="ad-mini" data-copy="${esc(s.secret)}"><i data-lucide="copy"></i>Copy</button></div>
    ${s.hint ? `<span class="fldhint">${esc(s.hint)}</span>` : ""}
  </div>`;
}

function keysTable() {
  if (state.keys === null) return loadingView("Loading keys…");
  if (!state.keys.length) return `<div class="ad-empty">No keys yet — create your first key above.</div>`;
  return tableWrap(`<table class="ad-table"><thead><tr><th>Name</th><th>Starts with</th><th>Type</th><th>Last used</th><th>Status</th><th></th></tr></thead><tbody>
    ${state.keys
      .map((k) => {
        const revoked = !!k.revoked_at;
        return `<tr>
        <td>${esc(k.name)}</td>
        <td class="mono">${esc(k.key_prefix)}…</td>
        <td class="muted">${esc(label("key_type", k.key_type).replace(/ \(.*\)$/, ""))}${Array.isArray(k.scopes) && k.scopes.includes(SCOPE_OPERATORS_ACT_AS) ? " · replies as a team member" : ""}</td>
        <td class="muted">${k.last_used_at ? esc(formatDay(k.last_used_at)) : "Never"}</td>
        <td>${revoked ? `<span class="ad-pill ad-pill--dim">Revoked</span>` : `<span class="ad-pill ad-pill--ok">Active</span>`}</td>
        <td class="actions">
          ${revoked ? "" : `<button class="ad-mini" data-rotate-key="${esc(k.key_id)}"><i data-lucide="refresh-cw"></i>Rotate</button> <button class="ad-mini danger" data-revoke-key="${esc(k.key_id)}"><i data-lucide="trash-2"></i>Revoke</button>`}
        </td>
      </tr>`;
      })
      .join("")}</tbody></table>`);
}

// ---- integrations (WI-P4-117) ----------------------------------------------------
//
// Signed outbound events to another app (for example unipy), plus the key that
// lets that app reply as the teammate who wrote the reply. Owners/admins only.

// Plural for what an app is sent ("Sends: New messages"), singular for one delivery.
const WEBHOOK_EVENT_LABELS = {
  "conversation.created": ["New conversations", "New conversation"],
  "message.created": ["New messages", "New message"],
  "conversation.resolved": ["Resolves", "Resolved"],
  "conversation.reopened": ["Reopens", "Reopened"],
  "iris.ping": ["Test events", "Test event"],
};
const DEFAULT_WEBHOOK_EVENTS = ["conversation.created", "message.created", "conversation.resolved", "conversation.reopened"];
// The reply key reads and answers support conversations, and may act as a
// named teammate (Iris-Act-As header) so replies show the right person.
const SCOPE_OPERATORS_ACT_AS = "chat:operators.act-as";
const REPLY_KEY_NAME = "Reply as a team member";
const REPLY_KEY_SCOPES = ["chat:channels.read", "chat:channels.manage", "chat:messages.read", "chat:messages.write", SCOPE_OPERATORS_ACT_AS];

function eventLabel(type, one = false) {
  const known = WEBHOOK_EVENT_LABELS[type];
  return known ? known[one ? 1 : 0] : label("event", type);
}

function deliveryPill(status) {
  const tone = status === "delivered" ? "ok" : status === "failed" || status === "dead_letter" ? "warn" : "dim";
  return `<span class="ad-pill ad-pill--${tone}">${esc(label("delivery", status))}</span>`;
}

// "HTTP 401 BAD_SIGNATURE" (the server already words it), or the bare code.
function deliveryError(d) {
  if (!d) return "";
  if (d.last_error) return d.last_error;
  return d.last_status_code ? `HTTP ${d.last_status_code}` : "";
}

// "just now" / "5 min ago" / "3 h ago" / "2 days ago", then the date.
function timeAgo(iso) {
  const t = new Date(iso).getTime();
  if (isNaN(t)) return iso || "";
  const minutes = Math.round((Date.now() - t) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
  return formatDay(iso);
}

function lastDeliveryLine(d) {
  if (!d) return "Nothing sent yet";
  const when = d.delivered_at || d.last_attempt_at || d.created_at;
  const error = d.status === "delivered" ? "" : deliveryError(d);
  return `${deliveryPill(d.status)} ${esc(timeAgo(when))} · ${esc(eventLabel(d.event_type, true))}${error ? `<br><code>${esc(error)}</code>` : ""}`;
}

function webhookTestView(id) {
  const t = state.integrations.tests[id];
  if (!t) return "";
  if (t.error) return `<div class="formerr" role="alert" style="margin:14px 0 0"><i data-lucide="plug-zap"></i><span>${esc(t.error)}</span></div>`;
  const r = t.result || {};
  if (r.delivered) {
    const answer = r.receiver_status ? `the app answered: ${r.receiver_status}${r.receiver_reason ? ` (${r.receiver_reason})` : ""}` : `HTTP ${r.status_code || 200}`;
    return `<div class="formok" role="status" style="margin:14px 0 0"><i data-lucide="check"></i><span>Delivered — ${esc(answer)}</span></div>`;
  }
  const why = r.error || (r.status_code ? `HTTP ${r.status_code}` : "no answer");
  return `<div class="formerr" role="alert" style="margin:14px 0 0"><i data-lucide="plug-zap"></i><span>Not delivered: ${esc(why)}</span></div>`;
}

function webhookDeliveriesView(id) {
  const d = state.integrations.deliveries[id];
  if (!d || !d.open) return "";
  if (d.loading) return `<div style="margin-top:14px">${loadingView("Loading deliveries…")}</div>`;
  if (d.error) return `<div class="formerr" role="alert" style="margin:14px 0 0"><i data-lucide="shield-alert"></i><span>${esc(d.error)}</span></div>`;
  if (!d.items || !d.items.length) return `<div class="ad-empty" style="margin-top:10px">Nothing sent yet.</div>`;
  const canRetry = !state.viewOnly;
  return `<div style="margin-top:14px">${tableWrap(`<table class="ad-table"><thead><tr><th>Time</th><th>Event</th><th>Status</th><th>Tries</th><th>Result</th><th></th></tr></thead><tbody>
    ${d.items
      .map((item) => {
        const retryable = item.status === "failed" || item.status === "dead_letter";
        const result =
          item.status === "delivered"
            ? item.last_status_code
              ? `HTTP ${item.last_status_code}`
              : ""
            : deliveryError(item) || (item.next_retry_at ? `Next try ${formatStamp(item.next_retry_at)}` : "");
        return `<tr>
        <td class="muted">${esc(formatStamp(item.created_at))}</td>
        <td>${esc(eventLabel(item.event_type, true))}</td>
        <td>${deliveryPill(item.status)}</td>
        <td class="muted">${esc(item.attempt_count)}</td>
        <td class="mono">${esc(result)}</td>
        <td class="actions">${canRetry && retryable ? `<button class="ad-mini" data-wh-retry="${esc(id)}" data-wh-delivery="${esc(item.delivery_id)}"><i data-lucide="refresh-cw"></i>Retry</button>` : ""}</td>
      </tr>`;
      })
      .join("")}</tbody></table>`)}</div>`;
}

function webhookCard(ep) {
  const g = state.integrations;
  const id = ep.webhook_id;
  const stats = ep.deliveries || {};
  const events = (ep.event_types || []).map((t) => eventLabel(t)).join(", ") || "Nothing";
  const rotating = ep.previous_secret_expires_at && new Date(ep.previous_secret_expires_at).getTime() > Date.now();
  const open = !!(g.deliveries[id] && g.deliveries[id].open);
  const canEdit = !state.viewOnly;
  return `<div class="ad-card" style="margin:0">
    <div class="card-head"><i data-lucide="plug"></i>${esc(ep.name)}<span class="grow"></span>${
      ep.is_active ? `<span class="ad-pill ad-pill--ok">Active</span>` : `<span class="ad-pill ad-pill--warn">Paused</span>`
    }</div>
    <div class="ad-kv ad-kv--2">
      <div class="kv"><b>Address</b><code>${esc(ep.url)}</code></div>
      <div class="kv"><b>Sends</b><span>${esc(events)}</span></div>
      <div class="kv"><b>Last delivery</b><span>${lastDeliveryLine(stats.last_delivery)}</span></div>
      <div class="kv"><b>Queue</b><span>${esc(stats.pending || 0)} waiting · ${esc(stats.failed_recent || 0)} failed in the last 7 days</span></div>
      <div class="kv"><b>Signing secret</b><span>Ends in <code>…${esc(ep.secret_last4)}</code>${
        rotating ? `<br><span style="font-weight:500;color:var(--uc-fg2)">Old secret still accepted until ${esc(formatStamp(ep.previous_secret_expires_at))}</span>` : ""
      }</span></div>
      ${ep.is_active ? "" : `<div class="kv"><b>While paused</b><span style="font-weight:500">Events wait here and go out when you resume.</span></div>`}
    </div>
    ${webhookTestView(id)}
    <div class="ad__actions" style="border:none;padding:0;margin-top:14px;flex-wrap:wrap;gap:8px">
      ${
        canEdit
          ? `<button class="ad-mini" type="button" data-wh-test="${esc(id)}"><i data-lucide="send"></i>Send test event</button>
      <button class="ad-mini" type="button" data-wh-toggle="${esc(id)}">${ep.is_active ? `<i data-lucide="hourglass"></i>Pause` : `<i data-lucide="zap"></i>Resume`}</button>
      <button class="ad-mini" type="button" data-wh-rotate="${esc(id)}" ${g.storageReady ? "" : "disabled"}><i data-lucide="refresh-cw"></i>Rotate secret</button>`
          : ""
      }
      <button class="ad-mini" type="button" data-wh-deliveries="${esc(id)}" aria-expanded="${open}"><i data-lucide="list"></i>${open ? "Hide deliveries" : "Recent deliveries"}</button>
      <div class="grow"></div>
      ${canEdit ? `<button class="ad-mini danger" type="button" data-wh-remove="${esc(id)}"><i data-lucide="trash-2"></i>Remove</button>` : ""}
    </div>
    ${webhookDeliveriesView(id)}
  </div>`;
}

// ---- new chat alerts (WI-P4-120 slice 2) -------------------------------------------

function newChatAlertsCard() {
  const a = state.alerts;
  const head = `<div class="card-head"><i data-lucide="bell-ring"></i>New chat alerts<span class="grow"></span>${
    a && a.available && (a.settings.new_conversation_email || a.settings.new_conversation_slack)
      ? `<span class="ad-pill ad-pill--ok">On</span>`
      : `<span class="ad-pill ad-pill--dim">Off</span>`
  }</div>`;
  if (a === null) return `<div class="ad-card">${head}${loadingView("Loading alerts…")}</div>`;
  if (!a.available) {
    return `<div class="ad-card">${head}<p class="ad-empty" style="margin:0">Alerts aren't available on this server yet. Ask your ${esc(brand.name)} contact.</p></div>`;
  }
  const s = a.settings;
  const ro = state.viewOnly ? "disabled" : "";
  const recipients = a.recipients.length
    ? `<div class="ad-checks" id="alertRecipients">${a.recipients
        .map((r) => {
          const who = r.display_name || r.email || `${r.user_id.slice(0, 8)}…`;
          const noEmail = !r.email;
          return `<label title="${esc(noEmail ? "Signs in with SSO, so there is no address to e-mail." : r.email)}"><input type="checkbox" data-alert-recipient value="${esc(r.user_id)}" ${r.notify && !noEmail ? "checked" : ""} ${noEmail || !s.new_conversation_email ? "disabled" : ""} ${ro}> ${esc(who)}${noEmail ? ` <span class="muted">(no e-mail)</span>` : ""}</label>`;
        })
        .join("")}</div>`
    : `<div class="ad-empty">No owners, admins or operators yet.</div>`;
  return `
  <div class="ad-card">
    ${head}
    <p class="sub" style="margin:0 0 12px">Know the moment a visitor starts a chat, and let visitors know when you've replied while they were away. Alerts carry the app's name, the conversation id and a link. Never what anyone wrote.</p>
    ${a.loadError ? `<div class="formerr" role="alert"><i data-lucide="shield-alert"></i><span>${esc(a.loadError)}</span></div>` : ""}
    <div class="ad__grid">
      <div class="span2">
        <label class="ad-check" for="al_email">
          <input type="checkbox" id="al_email" ${s.new_conversation_email ? "checked" : ""} ${a.emailAvailable ? "" : "disabled"} ${ro}>
          <span><b>E-mail teammates when a new chat starts</b><span class="fldhint">${
            a.emailAvailable
              ? "Pick who gets it. Only teammates who sign in with an e-mail can be chosen."
              : "This server has no outgoing e-mail yet, so this stays off."
          }</span></span>
        </label>
        <div style="margin:8px 0 0 28px">${recipients}</div>
      </div>
      <div class="span2">
        <label class="ad-check" for="al_slack">
          <input type="checkbox" id="al_slack" ${s.new_conversation_slack ? "checked" : ""} ${a.slackAvailable ? "" : "disabled"} ${ro}>
          <span><b>Post to Slack when a new chat starts</b><span class="fldhint">${
            a.slackAvailable
              ? "Goes to a channel in your own Slack, through an incoming webhook you paste below."
              : `This server can't store a Slack webhook yet. Ask your ${esc(brand.name)} contact.`
          }</span></span>
        </label>
        ${a.slackAvailable ? `<div style="margin:8px 0 0 28px">${slackWebhookField(a)}</div>` : ""}
      </div>
      <label class="ad-check span2" for="al_followup">
        <input type="checkbox" id="al_followup" ${s.guest_followup_email ? "checked" : ""} ${a.emailAvailable ? "" : "disabled"} ${ro}>
        <span><b>E-mail visitors when a teammate replies and they've left</b><span class="fldhint">Only visitors who gave an e-mail, only when they haven't read the reply after a couple of minutes, and only a link back to the page. Admin → Widget decides when the widget asks for the address.</span></span>
      </label>
    </div>
    ${
      state.viewOnly
        ? ""
        : `<div class="ad__actions" style="border:none;padding:0;margin-top:4px"><div class="grow"></div><button class="pf-btn pf-btn--primary" id="alertsSave" type="button"><i data-lucide="check"></i>Save alerts</button></div>`
    }
  </div>`;
}

// The webhook URL is the credential (Slack treats it like a token), so it is
// sealed server-side and never shown again: Admin only learns the host.
// slackEdit shows the URL field (first setup, or "Replace"); slackRemove
// marks the stored webhook for removal on save.
function slackWebhookField(a) {
  const ro = state.viewOnly ? "disabled" : "";
  if (a.slackConfigured && !a.slackEdit && !a.slackRemove) {
    return `<p class="fldhint" style="margin:0;display:flex;align-items:center;gap:8px;flex-wrap:wrap">Webhook connected to <code>${esc(a.slackHost || "your Slack")}</code>${
      state.viewOnly ? "" : `<button class="pf-btn pf-btn--ghost" id="alertsSlackReplace" type="button" style="padding:2px 8px">Replace</button><button class="pf-btn pf-btn--ghost" id="alertsSlackRemove" type="button" style="padding:2px 8px">Remove</button>`
    }</p>`;
  }
  if (a.slackRemove) {
    return `<p class="fldhint" style="margin:0;display:flex;align-items:center;gap:8px;flex-wrap:wrap">The webhook will be removed when you save.<button class="pf-btn pf-btn--ghost" id="alertsSlackKeep" type="button" style="padding:2px 8px">Keep it</button></p>`;
  }
  return `<label class="fld" for="al_slack_url"><span class="cap">Slack webhook URL</span><input class="inp" id="al_slack_url" type="url" placeholder="https://hooks.slack.com/services/…" autocomplete="off" spellcheck="false" ${ro}><span class="fldhint">In Slack: your channel → Integrations → Add an app → Incoming Webhooks. Paste the URL it gives you. It's stored encrypted and never shown again.${
    a.slackConfigured ? ` <button class="pf-btn pf-btn--ghost" id="alertsSlackKeep" type="button" style="padding:2px 8px">Keep the current one</button>` : ""
  }</span></label>`;
}

async function ensureAlerts(force) {
  if (state.alerts && !force) return;
  const next = { available: true, settings: { new_conversation_email: false, new_conversation_slack: false, guest_followup_email: true }, recipients: [], emailAvailable: false, slackAvailable: false, slackConfigured: false, slackHost: "", slackEdit: false, slackRemove: false, loadError: "" };
  try {
    const data = (await getNotificationSettings(state.token)) || {};
    next.settings = {
      new_conversation_email: !!data.new_conversation_email,
      new_conversation_slack: !!data.new_conversation_slack,
      guest_followup_email: data.guest_followup_email !== false,
    };
    next.recipients = Array.isArray(data.recipients) ? data.recipients : [];
    next.emailAvailable = !!data.email_available;
    next.slackAvailable = !!data.slack_available;
    next.slackConfigured = !!data.slack_configured;
    next.slackHost = data.slack_webhook_host || "";
  } catch (e) {
    rethrowAuth(e);
    const code = errCode(e);
    if (code === "HTTP_404" || code === "NOT_FOUND") {
      state.alerts = { ...next, available: false };
      return;
    }
    next.loadError = code === "NETWORK_ERROR" ? e.message : "Couldn't load your alert settings. Try again in a minute.";
  }
  state.alerts = next;
}

async function doSaveAlerts(btn) {
  if (state.busy || !state.alerts || !state.alerts.available) return;
  const body = {
    new_conversation_email: !!($("al_email") && $("al_email").checked),
    new_conversation_slack: !!($("al_slack") && $("al_slack").checked),
    guest_followup_email: !!($("al_followup") && $("al_followup").checked),
    recipient_user_ids: Array.from(panel.querySelectorAll("[data-alert-recipient]:checked")).map((box) => box.value),
  };
  const a = state.alerts;
  const urlField = $("al_slack_url");
  if (a.slackRemove) body.slack_webhook_url = "";
  else if (urlField && urlField.value.trim()) body.slack_webhook_url = urlField.value.trim();
  if (body.new_conversation_slack && !a.slackConfigured && !body.slack_webhook_url) {
    state.sectionError = "Paste your Slack webhook URL before turning the Slack alert on.";
    render();
    return;
  }
  if (a.slackRemove && body.new_conversation_slack) {
    state.sectionError = "Turn the Slack alert off before removing the webhook, or keep it.";
    render();
    return;
  }
  state.busy = true;
  state.sectionError = "";
  state.notice = "";
  setBusy(btn, true, "Saving…");
  try {
    await putNotificationSettings(state.token, body);
    state.notice = body.new_conversation_email || body.new_conversation_slack ? "Alerts saved. New chats now reach your team." : "Alerts saved.";
    await ensureAlerts(true);
  } catch (e) {
    if (authGuard(e)) return;
    state.sectionError = errCode(e) === "FORBIDDEN" ? "Owner/admin only — this account can't change alerts." : e.message || "That didn't work. Please try again.";
  }
  state.busy = false;
  render();
}

function connectedAppsCard() {
  const g = state.integrations;
  const list = g.endpoints;
  return `
  <div class="ad-card">
    <div class="card-head"><i data-lucide="webhook"></i>Connected apps<span class="grow"></span><span class="ad-pill ad-pill--dim">${list.length}</span></div>
    ${g.loadError ? `<div class="formerr" role="alert"><i data-lucide="shield-alert"></i><span>${esc(g.loadError)}</span></div>` : ""}
    ${
      list.length
        ? `<div class="ad__stack">${list.map(webhookCard).join("")}</div>`
        : g.loadError
          ? ""
          : `<div class="ad-empty">No apps connected yet.${state.viewOnly ? "" : " Connect one below."}</div>`
    }
  </div>`;
}

function connectAppCard() {
  const g = state.integrations;
  const disabled = g.storageReady ? "" : "disabled";
  return `
  <div class="ad-card">
    <div class="card-head"><i data-lucide="plus"></i>Connect an app</div>
    <div class="ad__grid">
      <label class="fld" for="wh_name"><span class="cap">Name</span><input class="inp" id="wh_name" placeholder="unipy" maxlength="80" autocomplete="off" ${disabled}><span class="field-err" id="whNameErr"></span></label>
      <label class="fld" for="wh_url"><span class="cap">Endpoint URL</span><input class="inp" id="wh_url" type="url" placeholder="https://api.example.com/api/v1/chat/bridges/iris/events" autocapitalize="off" autocomplete="off" spellcheck="false" ${disabled}><span class="field-err" id="whUrlErr"></span></label>
      <fieldset class="ad-fieldgroup span2"><legend>Send these events</legend>
        <div class="ad-checks">${g.eventTypes
          .map((t) => `<label><input type="checkbox" data-wh-event value="${esc(t)}" checked ${disabled}> ${esc(eventLabel(t))}</label>`)
          .join("")}</div>
        <span class="field-err" id="whEventsErr"></span>
      </fieldset>
      <label class="ad-check span2" for="wh_has_secret">
        <input type="checkbox" id="wh_has_secret" ${disabled}>
        <span><b>The app already has a signing secret</b>
        <span class="fldhint">Paste it below and ${esc(brand.name)} signs with it, so nothing changes in the app. Leave this off and ${esc(brand.name)} makes a new secret for you to copy into the app.</span></span>
      </label>
      <label class="fld span2" for="wh_secret" id="whSecretField" hidden><span class="cap">The app's signing secret</span><input class="inp" id="wh_secret" type="password" autocomplete="off" spellcheck="false" ${disabled}><span class="fldhint">For unipy: the value of IRIS_BRIDGE_SIGNING_SECRET. At least 32 characters.</span><span class="field-err" id="whSecretErr"></span></label>
    </div>
    <div class="ad__actions" style="border:none;padding:0;margin-top:4px">
      <span class="saved-at">The address must start with https://</span>
      <div class="grow"></div>
      <button class="pf-btn pf-btn--primary" id="whConnect" type="button" ${disabled}><i data-lucide="plug"></i>Connect</button>
    </div>
  </div>`;
}

function replyKeyCard() {
  const replyKeys = Array.isArray(state.keys)
    ? state.keys.filter((k) => !k.revoked_at && Array.isArray(k.scopes) && k.scopes.includes(SCOPE_OPERATORS_ACT_AS))
    : [];
  return `
  <div class="ad-card">
    <div class="card-head"><i data-lucide="key-round"></i>Let the app reply</div>
    <p class="ad-empty" style="margin:0 0 12px">The app answers customers through ${esc(brand.name)}'s product API, as the teammate who wrote the reply. So each teammate's email in that app must match the email they sign in to ${esc(brand.name)} with. A reply key works across the whole workspace: it can answer, resolve and reopen any conversation the named teammate can see, in every app. Give it only to an app you trust like a teammate.</p>
    ${
      replyKeys.length
        ? `<p class="ad-empty" style="margin:0 0 12px">Reply keys already made: ${replyKeys
            .map((k) => `<b>${esc(k.name)}</b> <code>${esc(k.key_prefix)}…</code>`)
            .join(", ")}. Revoke old ones under <a href="#keys" data-nav="keys" style="color:var(--uc-teal-700)">API keys</a>.</p>`
        : ""
    }
    <span class="cap" style="display:block;font:700 11px/1 var(--uc-font-body);text-transform:uppercase;letter-spacing:.08em;color:var(--uc-fg2)">API address</span>
    <div class="ad-secret"><code>${esc(apiRoot)}</code><button class="ad-mini" type="button" data-copy="${esc(apiRoot)}"><i data-lucide="copy"></i>Copy</button></div>
    <span class="fldhint">For unipy: IRIS_API_BASE_URL</span>
    ${
      state.viewOnly
        ? ""
        : `<div class="ad__actions" style="border:none;padding:0;margin-top:14px">
      <span class="saved-at">The key is shown once. You can revoke it under API keys.</span>
      <div class="grow"></div>
      <button class="pf-btn pf-btn--primary" id="whReplyKey" type="button"><i data-lucide="key-round"></i>Create a reply key</button>
    </div>`
    }
  </div>`;
}

function integrationsView() {
  const intro = `
  <span class="pf-eyebrow pf-eyebrow--teal"><span class="spark"></span>Integrations</span>
  <h1>Integrations</h1>
  <p class="sub">Connect another app to this workspace. ${esc(brand.name)} sends it each new conversation, message, resolve and reopen as it happens, signed so the app can trust it.</p>
  ${banners()}
  ${state.revealedSecret ? secretCard() : ""}`;
  const g = state.integrations;
  if (g === null) return `${intro}${newChatAlertsCard()}<div class="ad-card">${loadingView("Loading integrations…")}</div>`;
  if (!g.available) {
    return `${intro}
  ${newChatAlertsCard()}
  <div class="ad-card">
    <div class="card-head"><i data-lucide="plug"></i>Not switched on<span class="grow"></span><span class="ad-pill ad-pill--dim">Off</span></div>
    <p class="ad-empty" style="margin:0">Connecting apps isn't available on this server yet. Ask your ${esc(brand.name)} contact.</p>
  </div>`;
  }
  return `${intro}
  ${newChatAlertsCard()}
  ${
    g.storageReady
      ? ""
      : `<div class="formerr" role="note"><i data-lucide="lock"></i><span>This server can't store signing secrets safely yet, so connecting apps and new secrets are switched off. Ask your ${esc(brand.name)} contact.</span></div>`
  }
  ${connectedAppsCard()}
  ${state.viewOnly ? "" : connectAppCard()}
  ${replyKeyCard()}`;
}

// ---- install ---------------------------------------------------------------------

function installView() {
  const tenantID = state.workspace ? state.workspace.tenant_id : "<your-tenant-id>";
  const applicationID = state.config?.application_id || "<application-id>";
  const environmentID = state.config?.environment_id || "<environment-id>";
  const snippet = [
    `<script`,
    `  src="${brand.cdnBase}/widget.js"`,
    `  data-api-base="${brand.apiBase}"`,
    `  data-tenant-id="${tenantID}"`,
    `  data-application-id="${applicationID}"`,
    `  data-environment-id="${environmentID}"`,
    `  data-mode="customer"`,
    `  defer><\/script>`,
  ].join("\n");
  const curl = [
    `# Verify your server key against the product API`,
    `curl -s ${brand.apiBase}/api/product/v1/me \\`,
    `  -H "Authorization: Bearer iris_sk_..."`,
  ].join("\n");
  const origins = state.config && state.config.allowed_origins && state.config.allowed_origins.length
    ? state.config.allowed_origins.map((o) => `<span class="ad-pill ad-pill--dim" style="margin-right:6px">${esc(o)}</span>`).join("")
    : `<span class="ad-empty">None yet — add the website addresses your chat should appear on under Widget.</span>`;
  const agentPrompt = buildAgentPrompt(tenantID, applicationID, environmentID);
  const mailto = `mailto:?subject=${encodeURIComponent(`Add ${brand.name} chat to our site`)}&body=${encodeURIComponent(agentPrompt)}`;
  return `
  <span class="pf-eyebrow pf-eyebrow--teal"><span class="spark"></span>Install</span>
  <h1>Add ${esc(brand.name)} to your website</h1>
  <p class="sub">Add ${esc(brand.name)} to your website or app. Not a developer? Copy the instructions below and send them to whoever runs your site.</p>
  ${banners()}
  ${applicationScopeCard("install_binding")}
  <div class="ad-card" style="border-color:var(--uc-teal-600)">
    <div class="card-head"><i data-lucide="send"></i>Send this to your developer</div>
    <p class="ad-empty" style="margin:0 0 14px">Everything a developer needs — the snippet, your workspace IDs and the setup steps — in one message. It also works as a prompt for a coding agent.</p>
    <div class="ad__actions" style="border:none;padding:0;margin:0;flex-wrap:wrap">
      <button class="pf-btn pf-btn--primary" type="button" data-copy-id="agentPrompt"><i data-lucide="copy"></i>Copy setup instructions</button>
      <a class="pf-btn pf-btn--ghost" href="${esc(mailto)}"><i data-lucide="mail"></i>Email them</a>
    </div>
  </div>
  <div class="ad-card">
    <div class="card-head"><i data-lucide="code"></i>Widget embed snippet</div>
    <p class="ad-empty" style="margin:0 0 10px">Goes just before <code>&lt;/body&gt;</code> on every page that should offer chat.</p>
    <div class="ad-code"><button class="copybtn" data-copy-id="snippet"><i data-lucide="copy"></i>Copy</button><pre id="snippet">${esc(snippet)}</pre></div>
  </div>
  <div class="ad-card">
    <div class="card-head"><i data-lucide="globe"></i>Website addresses</div>
    <p class="ad-empty" style="margin:0 0 8px">The chat widget only appears on these sites.</p>
    <div>${origins}</div>
    <div class="ad__actions" style="border:none;padding:0;margin-top:12px"><div class="grow"></div><button class="ad-mini" data-nav="widget"><i data-lucide="palette"></i>Edit under Widget</button></div>
  </div>
  <details class="ad-card ad-details">
    <summary class="card-head">Advanced: coding agent prompt, server-to-server API, developer docs</summary>
    <div class="ad-sub">
      <div class="card-head"><i data-lucide="sparkles"></i>Coding agent prompt</div>
      <p class="ad-empty" style="margin:0 0 10px">Paste this into Claude Code, Cursor, or any coding agent working on your app. It carries your workspace IDs, endpoints and the machine-readable contract — the agent does the rest.</p>
      <div class="ad-code"><button class="copybtn" data-copy-id="agentPrompt"><i data-lucide="copy"></i>Copy</button><pre id="agentPrompt" class="wrap">${esc(agentPrompt)}</pre></div>
    </div>
    <div class="ad-sub">
      <div class="card-head"><i data-lucide="terminal"></i>Server-to-server (product API)</div>
      <p class="ad-empty" style="margin:0 0 10px">Authenticate with a secret key to create conversations and post messages from your backend. Endpoints live under <code>${esc(brand.apiBase)}/api/product/v1</code>.</p>
      <div class="ad-code"><button class="copybtn" data-copy-id="curlExample"><i data-lucide="copy"></i>Copy</button><pre id="curlExample" class="wrap">${esc(curl)}</pre></div>
    </div>
    <div class="ad-sub">
      <div class="card-head"><i data-lucide="book-open"></i>Developer documentation</div>
      <p class="ad-empty" style="margin:0">Full reference: <a href="docs.html" style="color:var(--uc-teal-700)">developer docs</a> · agent-readable contract: <a href="/llms.txt" style="color:var(--uc-teal-700)">/llms.txt</a></p>
    </div>
  </details>`;
}

// buildAgentPrompt mirrors the signup success screen: a self-contained setup
// prompt for a coding agent, carrying tenant id + endpoints + the llms.txt
// contract so it works with zero additional context.
function buildAgentPrompt(tenantID, applicationID, environmentID) {
  const origin = window.location.origin;
  return [
    `Install ${brand.name} chat (${origin}) in this app.`,
    ``,
    `Workspace details:`,
    `- Tenant ID: ${tenantID}`,
    `- Application ID: ${applicationID}`,
    `- Environment ID: ${environmentID}`,
    `- API base: ${brand.apiBase}`,
    `- Widget script: ${brand.cdnBase}/widget.js`,
    ``,
    `Steps:`,
    `1. Read ${origin}/llms.txt first — it is the complete machine-readable contract (auth, REST + product API, widget embed, examples).`,
    `2. Add the widget embed snippet before </body> on the pages that should offer chat:`,
    `   <script src="${brand.cdnBase}/widget.js" data-api-base="${brand.apiBase}" data-tenant-id="${tenantID}" data-application-id="${applicationID}" data-environment-id="${environmentID}" data-mode="customer" defer></script>`,
    `3. Add every origin the widget loads on to Allowed Origins (${brand.name} Admin console -> Widget).`,
    `4. Optional, server-to-server: create a secret API key (iris_sk_*, Admin console -> API keys; shown once), store it in the secret manager, and verify it:`,
    `   curl ${brand.apiBase}/api/product/v1/me -H "Authorization: Bearer $IRIS_API_KEY"`,
    `5. Optional, signed-in app users: mint chat sessions from your backend via POST ${brand.apiBase}/api/product/v1/sessions with {"external_user_id": "<your stable user id>"}, then pass the returned token to the widget as data-token (and chat.channel_id as data-initial-channel-id).`,
    ``,
    `Docs for humans: ${origin}/docs.html`,
  ].join("\n");
}

// ---- switch from Drift (WI-P4-120) ----------------------------------------------
//
// Drift Chat shuts down on 2027-01-31. A site that has Drift installed has two
// kinds of code: the install tag (replaced by ours, with data-drift-compat so
// the widget also defines window.drift) and page code that calls drift.* —
// which keeps working unchanged. Everything here is copy + the snippet; the
// install status comes from the same widget config the Install page reads.

const DRIFT_SUNSET = "January 31, 2027";

// Two tags. The first is a queue stub, the same idea as Drift's own snippet:
// drift.* calls made before the deferred widget script runs are queued as
// [method, ...args] and replayed by the widget (widget/src/lib/driftCompat.ts).
// Without it, an inline `drift.on('ready', ...)` right after the tag would
// throw "drift is not defined". The stub leaves a Drift snippet (`init`) or an
// already-installed shim (`iris`) alone.
function driftSnippet(tenantID, applicationID, environmentID) {
  return [
    `<script>`,
    `  (function () {`,
    `    var q = (window.drift = window.driftt = window.drift || []);`,
    `    if (q.init || q.iris) return;`,
    `    ["identify", "config", "track", "reset", "debug", "show", "ping", "page", "hide", "off", "on", "load"].forEach(function (name) {`,
    `      q[name] = function () { q.push([name].concat([].slice.call(arguments))); };`,
    `    });`,
    `  })();`,
    `<\/script>`,
    `<script`,
    `  src="${brand.cdnBase}/widget.js"`,
    `  data-api-base="${brand.apiBase}"`,
    `  data-tenant-id="${tenantID}"`,
    `  data-application-id="${applicationID}"`,
    `  data-environment-id="${environmentID}"`,
    `  data-mode="customer"`,
    `  data-drift-compat="true"`,
    `  defer><\/script>`,
  ].join("\n");
}

function switchView() {
  const tenantID = state.workspace?.tenant_id || "<tenant-id>";
  const applicationID = state.config?.application_id || "<application-id>";
  const environmentID = state.config?.environment_id || "<environment-id>";
  const snippet = driftSnippet(tenantID, applicationID, environmentID);
  const status = state.config?.install_status || "not_configured";
  const statusPill =
    status === "verified"
      ? `<span class="ad-pill ad-pill--ok">Installed</span>`
      : status === "configured"
        ? `<span class="ad-pill ad-pill--warn">Snippet configured, not seen yet</span>`
        : `<span class="ad-pill ad-pill--dim">Not installed yet</span>`;
  const devPrompt = buildDriftSwitchPrompt(tenantID, applicationID, environmentID);
  const mailto = `mailto:?subject=${encodeURIComponent(`Replace Drift with ${brand.name} chat`)}&body=${encodeURIComponent(devPrompt)}`;
  const mapping = [
    ["drift.api.openChat()", "Opens the chat"],
    ["drift.api.toggleChat()", "Opens or closes the chat"],
    ["drift.api.startInteraction(...)", "Opens the chat"],
    ["drift.api.widget.show() / .hide()", "Shows or hides the launcher"],
    ["drift.identify(id, { email, name })", "Pre-fills the visitor's name and email"],
    ["drift.reset()", "Forgets the visitor on this device: saved conversation, resume token and start form"],
    ["drift.on('ready' | 'chatOpen' | 'chatClose' | 'startConversation' | 'message', fn)", "Fires as before"],
    ["drift.page(), drift.track(), drift.config(), playbooks, meetings", "Accepted, nothing happens (sales features are not part of chat)"],
  ];
  return `
  <span class="pf-eyebrow pf-eyebrow--teal"><span class="spark"></span>Switch from Drift</span>
  <h1>Move from Drift to ${esc(brand.name)}</h1>
  <p class="sub">Drift Chat and Drift Email shut down on ${DRIFT_SUNSET}. Replace one tag and your site is on ${esc(brand.name)}; the buttons and code written for Drift keep working.</p>
  ${banners()}
  ${applicationScopeCard("switch_binding")}
  <div class="ad-card" style="border-color:var(--uc-teal-600)">
    <div class="card-head"><i data-lucide="send"></i>Send this to your developer</div>
    <p class="ad-empty" style="margin:0 0 14px">Everything they need in one message: what to remove, what to paste, and how to check it. It also works as a prompt for a coding agent.</p>
    <div class="ad__actions" style="border:none;padding:0;margin:0;flex-wrap:wrap">
      <button class="pf-btn pf-btn--primary" type="button" data-copy-id="driftPrompt"><i data-lucide="copy"></i>Copy switch instructions</button>
      <a class="pf-btn pf-btn--ghost" href="${esc(mailto)}"><i data-lucide="mail"></i>Email them</a>
      <div class="grow"></div>
      ${statusPill}
    </div>
  </div>
  <div class="ad-card">
    <div class="card-head"><i data-lucide="code"></i>1. Replace the Drift tag with this one</div>
    <p class="ad-empty" style="margin:0 0 10px">Remove the Drift install snippet (the block that mentions <code>js.driftt.com</code>) and paste this in its place, just before <code>&lt;/body&gt;</code>. If Drift lives in Google Tag Manager, edit that tag's Custom HTML to this snippet and publish the container: no site deploy needed.</p>
    <div class="ad-code"><button class="copybtn" data-copy-id="driftSnippet"><i data-lucide="copy"></i>Copy</button><pre id="driftSnippet">${esc(snippet)}</pre></div>
    <p class="ad-empty" style="margin:10px 0 0">The first, short script queues any <code>drift.*</code> call your pages make before the widget has loaded, the way Drift's own snippet did. The last attribute of the second, <code>data-drift-compat</code>, is what keeps that code working. On a site that never called <code>drift.*</code>, use the plain tag from Install instead.</p>
  </div>
  <div class="ad-card">
    <div class="card-head"><i data-lucide="arrow-left-right"></i>2. Your buttons keep working</div>
    <p class="ad-empty" style="margin:0 0 10px">With the tag above, <code>window.drift</code> is ${esc(brand.name)}. Page code written for Drift maps like this:</p>
    ${tableWrap(`<table class="ad-table"><thead><tr><th>Drift call</th><th>What happens now</th></tr></thead><tbody>${mapping
      .map(([call, effect]) => `<tr><td><code>${esc(call)}</code></td><td>${esc(effect)}</td></tr>`)
      .join("")}</tbody></table>`)}
    <p class="ad-empty" style="margin:10px 0 0">New code can use the native API instead: <code>window.UniChatWidget.open()</code>, <code>.close()</code>, <code>.identify({ name, email })</code> and <code>.on('chatOpen', fn)</code>. Details in the <a href="docs.html#drift" style="color:var(--uc-teal-700)">developer docs</a>.</p>
  </div>
  <div class="ad-card">
    <div class="card-head"><i data-lucide="list-checks"></i>3. Bring over what matters</div>
    ${tableWrap(`<table class="ad-table"><thead><tr><th>In Drift</th><th>In ${esc(brand.name)}</th></tr></thead><tbody>
      <tr><td>Teammates (Settings → Manage teammates → Export as CSV)</td><td>Import the file below: each teammate gets an invite and answers in the Portal. Or invite them one by one under <a href="#team" data-nav="team" style="color:var(--uc-teal-700)">Team</a>.</td></tr>
      <tr><td>Contacts (Contacts → Download CSV)</td><td>Import the file below: a known customer who comes back keeps one conversation history.</td></tr>
      <tr><td>Conversation transcripts</td><td>Not yet. Drift has no bulk export; transcripts come only through its API and may hold patient information, so that importer waits for the production environment. Keep a copy from Drift's API before the shutdown.</td></tr>
      <tr><td>Default greeting and help-center link</td><td>Set the welcome message under <a href="#widget" data-nav="widget" style="color:var(--uc-teal-700)">Widget</a>. A link in it stays a link.</td></tr>
      <tr><td>Teammate availability</td><td>Operators set their status in the Portal; new conversations reach every operator.</td></tr>
      <tr><td>Chat on your own app, not just the website</td><td>Same tag, or mint a session from your backend (see <a href="#install" data-nav="install" style="color:var(--uc-teal-700)">Install</a>) so signed-in users are known.</td></tr>
      <tr><td>Playbooks, meeting booking, CRM sync, email campaigns</td><td>Not part of ${esc(brand.name)} today. Keep whatever you use for those on its own.</td></tr>
    </tbody></table>`)}
  </div>
  ${driftImportCard()}
  <div class="ad-card">
    <div class="card-head"><i data-lucide="calendar-clock"></i>5. Before ${esc(DRIFT_SUNSET)}</div>
    <div class="usagebars">${[
      [status !== "not_configured", "Replace the tag on one page first and send yourself a test message. The pill above reads Installed once the widget has been seen."],
      [!!(state.driftImport && state.driftImport.result && !state.driftImport.result.dryRun), "Export your teammates and contacts from Drift as CSV while the account is still open, and import them above. Drift has no bulk conversation export; transcripts come only through its API."],
      [false, "Replace the tag everywhere else, then remove the Drift snippet from any remaining pages and tag containers."],
      [false, `Cancel Drift. Conversations started on ${brand.name} are already yours.`],
    ]
      .map(
        ([done, label]) => `
      <div class="ub" style="grid-template-columns:22px 1fr">
        <i data-lucide="${done ? "check-circle-2" : "circle"}" style="width:16px;height:16px;color:${done ? "var(--uc-forest-500)" : "var(--uc-neutral-400)"}"></i>
        <span style="color:var(--uc-fg1)">${esc(label)}</span>
      </div>`,
      )
      .join("")}</div>
  </div>
  <details class="ad-card ad-details">
    <summary class="card-head">The full instructions, as text</summary>
    <div class="ad-code"><button class="copybtn" data-copy-id="driftPrompt"><i data-lucide="copy"></i>Copy</button><pre id="driftPrompt" class="wrap">${esc(devPrompt)}</pre></div>
  </details>`;
}

// ---- Import from Drift (WI-P4-120 importer slice 1) ---------------------------
//
// Two CSV files from Drift's own UI, parsed here in the browser (driftImport.js),
// shown back as a preview, checked on the server without writing (dry run), then
// imported. Teammates become pending invites through the same route the Team
// page uses; contacts become identity mappings so a returning customer keeps one
// conversation. Transcripts are not part of this card (PHI; production lane).

// Teammates go 20 per request: each live invite sends an e-mail inside the
// request and the server allows 15 s per request. Contacts are database writes.
const DRIFT_TEAMMATE_BATCH = 20;
const DRIFT_CONTACT_BATCH = 300;
// The server allows a burst of import requests per workspace, then one every
// couple of seconds; a 429 is retried after a short wait.
const DRIFT_RATE_LIMIT_RETRIES = 6;
const DRIFT_RATE_LIMIT_WAIT_MS = 2500;

async function importDriftBatch(payload) {
  for (let attempt = 0; ; attempt++) {
    try {
      return await importDrift(state.token, payload);
    } catch (e) {
      if (e.code !== "RATE_LIMITED" || attempt >= DRIFT_RATE_LIMIT_RETRIES) throw e;
      await new Promise((resolve) => setTimeout(resolve, DRIFT_RATE_LIMIT_WAIT_MS));
    }
  }
}
const DRIFT_PREVIEW_ROWS = 5;

function newDriftImportState() {
  return {
    teammates: null, // { fileName, rows, columns, skipped, error }
    contacts: null,
    defaultRole: "operator",
    paste: { teammates: false, contacts: false },
    result: null, // { dryRun, teammates: [...], contacts: [...], summary }
    busy: false,
    error: "",
  };
}

function driftImportState() {
  if (!state.driftImport) state.driftImport = newDriftImportState();
  return state.driftImport;
}

function driftImportCard() {
  const di = driftImportState();
  const ready = (di.teammates && di.teammates.rows.length) || (di.contacts && di.contacts.rows.length);
  const checked = di.result && di.result.dryRun && !di.error;
  return `
  <div class="ad-card" id="driftImportCard">
    <div class="card-head"><i data-lucide="download"></i>4. Import teammates and contacts from Drift</div>
    <p class="ad-empty" style="margin:0 0 14px">In Drift, export <strong>Settings → Manage teammates → Export as CSV</strong> and <strong>Contacts → Download CSV</strong>, then add the files here. Nothing leaves this page until you press <em>Check</em>; the check reports what would change, and only <em>Import</em> writes anything. Files are read in your browser; only e-mail, name, role, id and company are sent.</p>
    <div class="ad__grid">
      ${driftFileField("teammates", "Teammates CSV", di.teammates, di.paste.teammates)}
      ${driftFileField("contacts", "Contacts CSV", di.contacts, di.paste.contacts)}
    </div>
    ${di.teammates && di.teammates.rows.length ? `
    <label class="fld" for="drift_role" style="max-width:360px"><span class="cap">Role for teammates Drift calls something else</span>
      <select class="sel" id="drift_role">${roleOptions(di.defaultRole)}</select>
      <span class="fldhint">Drift admins and owners become admins, read-only teammates become viewers, agents and members become operators. Anything else gets this role.</span>
    </label>` : ""}
    ${driftPreview("Teammates", di.teammates, ["email", "name", "role"])}
    ${driftPreview("Contacts", di.contacts, ["external_id", "email", "name", "organization"])}
    ${di.error ? `<div class="formerr" role="alert"><i data-lucide="alert-circle"></i><span>${esc(di.error)}</span></div>` : ""}
    ${driftReport(di.result)}
    <div class="ad__actions" style="border:none;padding:0;margin-top:4px;flex-wrap:wrap;gap:8px">
      ${di.result ? `<button class="ad-mini" type="button" id="driftReset"><i data-lucide="rotate-ccw"></i>Start over</button>` : ""}
      <div class="grow"></div>
      <button class="pf-btn pf-btn--ghost" type="button" id="driftCheck" ${ready && !di.busy ? "" : "disabled"}><i data-lucide="search-check"></i>Check what would change</button>
      <button class="pf-btn pf-btn--primary" type="button" id="driftRun" ${checked && !di.busy ? "" : "disabled"} title="${checked ? "" : "Run the check first"}"><i data-lucide="download"></i>Import</button>
    </div>
  </div>`;
}

function driftFileField(kind, label, parsed, pasting) {
  const summary = parsed
    ? parsed.error
      ? `<span class="field-err" style="display:block">${esc(parsed.error)}</span>`
      : `<span class="fldhint">${esc(parsed.fileName || "pasted text")}: ${parsed.rows.length} row${parsed.rows.length === 1 ? "" : "s"}${parsed.skipped ? `, ${parsed.skipped} blank skipped` : ""}. Columns: ${esc(
          Object.entries(parsed.columns)
            .map(([field, header]) => `${header} → ${field.replace("_", " ")}`)
            .join(", "),
        )}</span>`
    : `<span class="fldhint">${kind === "teammates" ? "Needs an Email column; Name and Role are used when present." : "Needs an ID or Email column; Name and Company are kept when present."}</span>`;
  return `
      <div class="fld">
        <span class="cap">${esc(label)}</span>
        ${pasting
          ? `<textarea class="inp mono" id="drift_${kind}_text" rows="5" placeholder="Paste the CSV text here, header row first" aria-label="${esc(label)} as text"></textarea>
             <div style="display:flex;gap:8px;margin-top:6px;flex-wrap:wrap"><button class="ad-mini" type="button" data-drift-parse="${kind}"><i data-lucide="check"></i>Use this text</button><button class="ad-mini" type="button" data-drift-paste="${kind}" data-on="0"><i data-lucide="file-text"></i>Choose a file instead</button></div>`
          : `<input class="inp" id="drift_${kind}_file" type="file" accept=".csv,text/csv,text/plain" aria-label="${esc(label)} file">
             <div style="margin-top:6px"><button class="ad-mini" type="button" data-drift-paste="${kind}" data-on="1"><i data-lucide="clipboard-paste"></i>Paste the text instead</button></div>`}
        ${summary}
      </div>`;
}

function driftPreview(title, parsed, fields) {
  if (!parsed || parsed.error || !parsed.rows.length) return "";
  const rows = parsed.rows.slice(0, DRIFT_PREVIEW_ROWS);
  const more = parsed.rows.length - rows.length;
  return `
    <div class="ad-sub" style="margin-top:10px">
      <div class="ad-empty" style="padding:4px 0">${esc(title)} preview${more > 0 ? ` (first ${rows.length} of ${parsed.rows.length})` : ""}</div>
      ${tableWrap(`<table class="ad-table"><thead><tr>${fields.map((f) => `<th>${esc(f.replace("_", " "))}</th>`).join("")}</tr></thead><tbody>${rows
        .map((r) => `<tr>${fields.map((f) => `<td>${r[f] ? esc(r[f]) : `<span class="muted">—</span>`}</td>`).join("")}</tr>`)
        .join("")}</tbody></table>`)}
    </div>`;
}

function driftReport(result) {
  if (!result) return "";
  const dry = result.dryRun;
  const pill = (outcome) => `<span class="ad-pill ad-pill--${outcomeTone(outcome)}">${esc(outcomeLabel(outcome, dry))}</span>`;
  const count = (summary, keys) => keys.reduce((n, k) => n + (summary[k] || 0), 0);
  const tSum = result.summary.teammates || {};
  const cSum = result.summary.contacts || {};
  const headline = dry
    ? `Check complete: ${count(tSum, ["invite"])} teammate${count(tSum, ["invite"]) === 1 ? "" : "s"} would be invited and ${count(cSum, ["create"])} contact${count(cSum, ["create"]) === 1 ? "" : "s"} would be added. Nothing has been written yet.`
    : `Import complete: ${count(tSum, ["invite"])} teammate${count(tSum, ["invite"]) === 1 ? "" : "s"} invited and ${count(cSum, ["create"])} contact${count(cSum, ["create"]) === 1 ? "" : "s"} added.${
        count(tSum, ["failed"]) + count(cSum, ["failed"]) ? " Some rows failed; run the check again to retry just those." : ""
      }`;
  const links = result.teammates.filter((t) => t.token);
  return `
    <div class="ad-sub" style="margin-top:12px">
      <div class="ad-keyline${dry ? " ad-keyline--none" : ""}"><i data-lucide="${dry ? "search-check" : "check-circle-2"}"></i><span>${esc(headline)}</span></div>
      ${links.length ? `<p class="ad-empty" style="margin:0 0 8px">No e-mail transport is configured here, so share these accept links with your teammates directly. They are shown once.</p>${links
        .map((t) => `<div class="ad-secret"><code>${esc(driftAcceptURL(t))}</code><button class="ad-mini" data-copy="${esc(driftAcceptURL(t))}"><i data-lucide="copy"></i>Copy</button></div>`)
        .join("")}` : ""}
      ${result.teammates.length ? `<div class="ad-empty" style="padding:4px 0">Teammates</div>${tableWrap(`<table class="ad-table"><thead><tr><th>#</th><th>Email</th><th>Role</th><th>Result</th></tr></thead><tbody>${result.teammates
        .map((t) => `<tr><td class="muted">${t.row}</td><td>${esc(t.email || "—")}</td><td>${t.org_role ? `<span class="ad-pill ad-pill--dim">${esc(label("role", t.org_role))}</span>` : ""}</td><td>${pill(t.outcome)}${t.detail ? `<div class="muted">${esc(t.detail)}</div>` : ""}</td></tr>`)
        .join("")}</tbody></table>`)}` : ""}
      ${result.contacts.length ? `<div class="ad-empty" style="padding:4px 0">Contacts</div>${tableWrap(`<table class="ad-table"><thead><tr><th>#</th><th>Drift id</th><th>Result</th></tr></thead><tbody>${result.contacts
        .map((c) => `<tr><td class="muted">${c.row}</td><td class="mono">${esc(c.external_subject || "—")}</td><td>${pill(c.outcome)}${c.detail ? `<div class="muted">${esc(c.detail)}</div>` : ""}</td></tr>`)
        .join("")}</tbody></table>`)}` : ""}
    </div>`;
}

function driftAcceptURL(t) {
  return `${window.location.origin}/accept.html?tenant_id=${encodeURIComponent(state.workspace?.tenant_id || "")}&token=${encodeURIComponent(t.token)}&email=${encodeURIComponent(t.email)}`;
}

function wireDriftImport() {
  const di = driftImportState();
  for (const kind of ["teammates", "contacts"]) {
    const file = $(`drift_${kind}_file`);
    if (file)
      file.onchange = () => {
        const f = file.files && file.files[0];
        if (!f) return;
        f.text().then((text) => setDriftText(kind, text, f.name));
      };
  }
  panel.querySelectorAll("[data-drift-paste]").forEach((b) => {
    b.onclick = () => {
      di.paste[b.getAttribute("data-drift-paste")] = b.getAttribute("data-on") === "1";
      render();
    };
  });
  panel.querySelectorAll("[data-drift-parse]").forEach((b) => {
    b.onclick = () => {
      const kind = b.getAttribute("data-drift-parse");
      const text = ($(`drift_${kind}_text`) || {}).value || "";
      setDriftText(kind, text, "");
    };
  });
  const role = $("drift_role");
  if (role)
    role.onchange = () => {
      di.defaultRole = role.value;
      di.result = null; // the plan changed; check again
      render();
    };
  const check = $("driftCheck");
  if (check) check.onclick = () => runDriftImport(true, check);
  const run = $("driftRun");
  if (run) run.onclick = () => runDriftImport(false, run);
  const reset = $("driftReset");
  if (reset)
    reset.onclick = () => {
      state.driftImport = null;
      render();
    };
}

function setDriftText(kind, text, fileName) {
  const di = driftImportState();
  const parsed = kind === "teammates" ? parseTeammatesCSV(text) : parseContactsCSV(text);
  di[kind] = { ...parsed, fileName };
  di.result = null;
  di.error = "";
  render();
}

// Sends the parsed rows in chunks (the server caps each request) and stitches
// the reports back together with the original row numbers.
async function runDriftImport(dryRun, btn) {
  const di = driftImportState();
  if (di.busy) return;
  if (!dryRun && !window.confirm("Import now? Teammates get an invite e-mail and contacts are added to this workspace.")) return;
  di.busy = true;
  di.error = "";
  setBusy(btn, true, dryRun ? "Checking…" : "Importing…");
  const teammates = di.teammates && !di.teammates.error ? di.teammates.rows : [];
  const contacts = di.contacts && !di.contacts.error ? di.contacts.rows : [];
  const merged = { dryRun, teammates: [], contacts: [], summary: { teammates: {}, contacts: {} } };
  const fold = (kind, rows, offset) => {
    for (const r of rows) {
      merged[kind].push({ ...r, row: r.row + offset });
      merged.summary[kind][r.outcome] = (merged.summary[kind][r.outcome] || 0) + 1;
    }
  };
  try {
    if (teammates.length <= DRIFT_TEAMMATE_BATCH && contacts.length <= DRIFT_CONTACT_BATCH) {
      // The usual case: both files in one request, one audit summary.
      const res = await importDriftBatch({ dryRun, defaultOrgRole: di.defaultRole, teammates, contacts });
      fold("teammates", res.teammates || [], 0);
      fold("contacts", res.contacts || [], 0);
    } else {
      let offset = 0;
      for (const part of chunk(teammates, DRIFT_TEAMMATE_BATCH)) {
        const res = await importDriftBatch({ dryRun, defaultOrgRole: di.defaultRole, teammates: part });
        fold("teammates", res.teammates || [], offset);
        offset += part.length;
      }
      offset = 0;
      for (const part of chunk(contacts, DRIFT_CONTACT_BATCH)) {
        const res = await importDriftBatch({ dryRun, contacts: part });
        fold("contacts", res.contacts || [], offset);
        offset += part.length;
      }
    }
    di.result = merged;
    if (!dryRun) {
      state.notice = "Drift import finished.";
      await ensureInvites(true);
    }
  } catch (e) {
    if (authGuard(e)) return;
    di.result = merged.teammates.length || merged.contacts.length ? merged : null;
    const partial = merged.teammates.length || merged.contacts.length;
    di.error = e.code === "FORBIDDEN"
      ? "Owner/admin only — this account can't import."
      : (e.message || "The import request failed. Please try again.") + (partial ? " The rows below were processed before it stopped; run the check again to continue." : "");
  }
  di.busy = false;
  render();
}

// buildDriftSwitchPrompt is the developer-facing (and coding-agent-facing)
// version of the page above: the whole switch in one pasteable message.
function buildDriftSwitchPrompt(tenantID, applicationID, environmentID) {
  const origin = window.location.origin;
  return [
    `Replace Drift chat with ${brand.name} chat (${origin}) on this site.`,
    ``,
    `Drift shuts down on ${DRIFT_SUNSET}. ${brand.name} is a drop-in: one tag replaces the Drift snippet, and page code that calls drift.* keeps working.`,
    ``,
    `Workspace details:`,
    `- Tenant ID: ${tenantID}`,
    `- Application ID: ${applicationID}`,
    `- Environment ID: ${environmentID}`,
    `- API base: ${brand.apiBase}`,
    `- Widget script: ${brand.cdnBase}/widget.js`,
    ``,
    `Steps:`,
    `1. Find the Drift install snippet: an inline <script> that mentions js.driftt.com and ends with drift.load("...") (or a Drift tag in Google Tag Manager). Remove it.`,
    `2. In its place, before </body> on every page that offered Drift chat (or as the Custom HTML of that GTM tag), add these two tags (the first queues drift.* calls made before the widget loads; the second is the widget):`,
    `   ${driftSnippet(tenantID, applicationID, environmentID).replace(/\n\s*/g, " ")}`,
    `3. Leave existing drift.* calls alone. With data-drift-compat="true" the widget defines window.drift: drift.api.openChat / toggleChat / startInteraction open the chat, drift.api.widget.show/hide show or hide the launcher, drift.identify(id, {email, name}) pre-fills the visitor, drift.on('ready'|'chatOpen'|'chatClose'|'startConversation'|'message', fn) fire as before, and drift.page/track/config are accepted and ignored. New code can call window.UniChatWidget.open(), .close(), .identify({name, email}), .on(event, fn) directly.`,
    `4. Add every origin the widget loads on to Allowed Origins (${brand.name} Admin console -> Widget).`,
    `5. Load a page, open the chat, send a test message, and check it arrives in the ${brand.name} Portal. Then remove any remaining Drift tags.`,
    `6. Optional, signed-in users: mint a session from your backend via POST ${brand.apiBase}/api/product/v1/sessions with {"external_user_id": "<your stable user id>"} and pass the returned token as data-token.`,
    ``,
    `Machine-readable contract: ${origin}/llms.txt. Docs for humans: ${origin}/docs.html#drift`,
  ].join("\n");
}

// ---- compliance ---------------------------------------------------------------

// ---- Iris Assist: AI providers (WI-P4-108) + drafting policy (WI-P4-106) ---------
//
// A workspace can save several AI providers (an Anthropic key, an OpenAI key,
// OpenAI-compatible endpoints) and choose the default that drafts use. Keys are
// write-only: the server only ever returns their last four characters. Each
// provider carries its own "I confirm" consent (D-0019): no conversation text
// goes to a cloud provider that has not been allowed. The model is picked from
// the provider's own list, loaded with the workspace's key, so nobody has to
// guess a model ID ("claude-opus-5.5" is not one; "claude-opus-5-5" is).

// Plain copy for the connection test's closed error classes.
const TEST_FAILURE_COPY = {
  unauthorized: "The provider didn't accept this key. Check that you copied the whole key and that it's still active.",
  not_found_model: "The provider doesn't recognise that model name. Choose Edit and pick one from its list.",
  network: "Couldn't reach the provider. Check the endpoint address, or try again in a minute.",
  rate_limited: "The provider is busy, or this key has hit its usage limit. Try again shortly.",
  unknown: "The provider answered with an unexpected error. Try again, or check the provider's status page.",
};

// A word or two for a row's status when a check failed.
const SHORT_FAILURE = {
  unauthorized: "Key not accepted",
  not_found_model: "Model not found",
  network: "Can't reach it",
  rate_limited: "Provider busy",
  unknown: "Check failed",
};

// Plain copy for a model list that could not be loaded.
const MODEL_LIST_COPY = {
  unauthorized: "The provider didn't accept this key, so its models can't be listed. Check the key, or type a model ID.",
  network: "Couldn't reach the provider to list its models. Check the endpoint address, or type a model ID.",
  rate_limited: "The provider is busy right now. Try again shortly, or type a model ID.",
  unknown: "The provider didn't send a model list. You can type a model ID instead.",
};

const MODEL_OTHER = "__other__";
// Saved providers whose model list loads by itself when the page opens (each
// lookup is one call to the provider, and Admin allows a few in a row).
const AUTO_MODEL_LISTS = 1; // the default only; others list on Edit / Load models (shared rate limit)

// Model lists of saved providers for this page session, keyed by credential id
// and updated_at (a new key or model reloads): { status, list, errorClass, promise }.
const savedModelLists = new Map();
let autoListing = false;

function savedListKey(cred) {
  return `${cred.id}|${cred.updated_at || ""}`;
}

async function ensureAssistPolicy(force) {
  if (state.assist && !force) return;
  const next = {
    policy: null,
    available: true,
    saving: false,
    error: "",
    ok: false,
    providers: null, // GET /admin/integrations/ai-providers view
    providerAccess: "ok", // ok | forbidden | missing | error
    busy: "", // "" | "save" | `test:${id}` | `default:${id}` | `remove:${id}`
    providerError: "",
    providerOk: "",
    tests: {}, // credential id -> last test result this session
    form: null, // null | { mode: "add" } | { mode: "edit", id }
    picker: null, // the open form's model picker (see addPicker / editPicker)
  };
  const [policy, providers] = await Promise.allSettled([getAssistPolicy(state.token), listAIProviders(state.token)]);
  if (policy.status === "fulfilled") {
    next.policy = policy.value;
  } else {
    rethrowAuth(policy.reason);
    const code = errCode(policy.reason);
    if (code === "HTTP_404" || code === "NOT_FOUND") {
      // Routes are mounted only when Assist is switched on for this server.
      state.assist = { ...next, available: false };
      return;
    }
    next.error = policy.reason.message || "Couldn't load the Assist settings.";
  }
  if (providers.status === "fulfilled") {
    next.providers = providers.value;
    // Nothing saved yet: open the add form straight away.
    if (!credentialsOf(next.providers).length && next.providers.storage_ready && isAdminRole()) {
      next.form = { mode: "add" };
      next.picker = addPicker(firstFreeProvider(next.providers));
    }
  } else {
    if (isUnauthorized(providers.reason)) throw providers.reason;
    const code = errCode(providers.reason);
    if (isForbidden(providers.reason)) next.providerAccess = "forbidden";
    else if (code === "HTTP_404" || code === "NOT_FOUND") next.providerAccess = "missing";
    else {
      next.providerAccess = "error";
      next.providerError = providers.reason.message || "Couldn't load the AI providers.";
    }
  }
  state.assist = next;
}

function providerOptionsOf(view) {
  return (view && view.providers) || [];
}

function credentialsOf(view) {
  return (view && view.credentials) || [];
}

function providerOption(id) {
  return providerOptionsOf(state.assist && state.assist.providers).find((p) => p.id === id) || { id, label: label("provider", id), base_url: "none" };
}

function providerLabel(view, id) {
  const option = providerOptionsOf(view).find((p) => p.id === id);
  if (option) return option.label;
  if (id === "fake") return `${brand.name} test provider`;
  return label("provider", id);
}

function credentialById(id) {
  return credentialsOf(state.assist && state.assist.providers).find((c) => c.id === id) || null;
}

function credentialName(cred) {
  return cred.label || providerLabel(state.assist && state.assist.providers, cred.provider);
}

// Anthropic and OpenAI are one key each; OpenAI-compatible endpoints are one per address.
function providerTaken(view, option) {
  return option.base_url !== "required" && credentialsOf(view).some((c) => c.provider === option.id);
}

function firstFreeProvider(view) {
  const options = providerOptionsOf(view);
  const free = options.find((o) => !providerTaken(view, o)) || options[0];
  return free ? free.id : "anthropic";
}

// "Claude Opus 5.5 · claude-opus-5-5" in the picker.
function modelOptionText(model) {
  return model.name && model.name !== model.id ? `${model.name} · ${model.id}` : model.id;
}

// "Using your Anthropic key •••• 1234 · claude-opus-5-5"
function providerStatusLine(view) {
  const effective = (view && view.effective) || { source: "none" };
  const creds = credentialsOf(view);
  const cred = effective.source === "workspace" ? creds.find((c) => c.id === effective.credential_id) : null;
  if (cred) {
    return `<div class="ad-keyline"><i data-lucide="key-round"></i><span>Drafts use your <b>${esc(credentialName(cred))}</b> key <code>•••• ${esc(cred.key_last4)}</code> · ${esc(cred.model)}</span></div>`;
  }
  if (effective.source === "platform") {
    const own = creds.length
      ? "None of your saved providers is the default: choose “Make default” on one to use your own account instead."
      : "Add your own key below to use your own provider account instead.";
    return `<div class="ad-keyline"><i data-lucide="sparkles"></i><span>Drafts use ${esc(brand.name)}'s shared <b>${esc(providerLabel(view, effective.provider))}</b> access (${esc(effective.model)}). ${own}</span></div>`;
  }
  if (creds.length) {
    return `<div class="ad-keyline ad-keyline--none"><i data-lucide="plug"></i><span>None of your saved providers is the default, so drafting is off. Choose “Make default” on one below.</span></div>`;
  }
  return `<div class="ad-keyline ad-keyline--none"><i data-lucide="plug"></i><span>No AI provider yet. Add an API key from your provider below and ${esc(brand.name)} can start drafting.</span></div>`;
}

function providerPill(view) {
  const source = view && view.effective ? view.effective.source : "none";
  if (source === "workspace") return `<span class="ad-pill ad-pill--ok">Your key</span>`;
  if (source === "platform") return `<span class="ad-pill ad-pill--dim">${esc(brand.name)} key</span>`;
  return `<span class="ad-pill ad-pill--warn">Not set up</span>`;
}

// One saved provider's status: model (with its display name once the
// provider's list is known), whether the key works, and whether it may
// receive conversations.
function providerStatusChips(cred) {
  const a = state.assist;
  const cached = savedModelLists.get(savedListKey(cred));
  const listed = cached && cached.status === "ready" ? cached.list : null;
  const model = listed ? listed.find((m) => m.id === cred.model) : null;
  const chips = [`<span>${esc(model && model.name && model.name !== model.id ? `${model.name} (${model.id})` : cred.model)}</span>`];
  const test = a.tests[cred.id];
  if (test ? test.ok : !!listed) chips.push(`<span class="ad-pill ad-pill--ok">Connected ✓</span>`);
  else if (test) chips.push(`<span class="ad-pill ad-pill--warn">${esc(SHORT_FAILURE[test.error_class] || SHORT_FAILURE.unknown)}</span>`);
  else if (cached && cached.status === "error" && cached.errorClass) chips.push(`<span class="ad-pill ad-pill--warn">${esc(SHORT_FAILURE[cached.errorClass] || SHORT_FAILURE.unknown)}</span>`);
  else if (cached && cached.status === "loading") chips.push(`<span class="ad-pill ad-pill--dim">Checking…</span>`);
  if (listed && listed.length && !model) chips.push(`<span class="ad-pill ad-pill--warn">Model not offered</span>`);
  if (!cred.cloud) chips.push(`<span class="ad-pill ad-pill--dim">On your network</span>`);
  else if (cred.cloud_consent) chips.push(`<span class="ad-pill ad-pill--ok">Allowed ✓</span>`);
  else chips.push(`<span class="ad-pill ad-pill--warn">Needs your OK</span>`);
  if (cred.is_default) chips.push(`<span class="ad-pill ad-pill--ok">Default</span>`);
  return chips.join("");
}

function providerRowNote(cred) {
  const test = state.assist.tests[cred.id];
  if (test) {
    return test.ok
      ? `<div class="formok" role="status" style="margin:12px 0 0"><i data-lucide="check"></i><span>Connected. ${esc(credentialName(cred))} answered using ${esc(test.model)}.</span></div>`
      : `<div class="formerr" role="alert" style="margin:12px 0 0"><i data-lucide="plug-zap"></i><span>${esc(TEST_FAILURE_COPY[test.error_class] || TEST_FAILURE_COPY.unknown)}</span></div>`;
  }
  const cached = savedModelLists.get(savedListKey(cred));
  if (cached && cached.status === "ready" && cached.list.length && !cached.list.some((m) => m.id === cred.model)) {
    return `<p class="ad-note" style="margin:10px 0 0"><i data-lucide="info"></i>Your saved model isn't offered by this provider anymore — choose Edit and pick one.</p>`;
  }
  return "";
}

function providerRowView(cred) {
  const a = state.assist;
  const view = a.providers;
  const canEdit = isAdminRole() && a.providerAccess === "ok";
  // Rows wait while a form is open or a request runs, so a full re-render
  // never throws away what someone is typing.
  const locked = a.busy || a.form ? "disabled" : "";
  const who = cred.updated_by && cred.updated_by.name ? ` by ${esc(cred.updated_by.name)}` : "";
  const when = cred.updated_at ? ` · ${esc(formatDay(cred.updated_at))}` : "";
  const kind = cred.label ? `${esc(providerLabel(view, cred.provider))} · ` : "";
  const endpoint = cred.base_url ? ` · ${esc(cred.base_url)}` : "";
  const testing = a.busy === `test:${cred.id}`;
  const editing = a.form && a.form.mode === "edit" && a.form.id === cred.id;
  return `
    <div class="ad-card" style="margin:0" id="ap_row_${esc(cred.id)}">
      <div class="card-head"><i data-lucide="key-round"></i>${esc(credentialName(cred))}&nbsp;<code>•••• ${esc(cred.key_last4)}</code></div>
      <div style="display:flex;flex-wrap:wrap;align-items:center;gap:6px 8px;margin:-4px 0 8px;font:600 13.5px/1.45 var(--uc-font-body);color:var(--uc-fg1)" data-ap-status="${esc(cred.id)}">${providerStatusChips(cred)}</div>
      <div class="saved-at" style="font:600 12px/1.4 var(--uc-font-body);color:var(--uc-fg2)">${kind}Saved${who}${when}${endpoint}</div>
      <div data-ap-note="${esc(cred.id)}">${editing ? "" : providerRowNote(cred)}</div>
      ${
        canEdit && !editing
          ? `<div class="ad__actions" style="border:none;padding:0;margin-top:12px;flex-wrap:wrap;gap:8px">
        <button class="ad-mini" type="button" data-ap-test="${esc(cred.id)}" ${locked}>${testing ? `<span class="btnspin" style="width:11px;height:11px;border-radius:99px;border:2px solid currentColor;border-top-color:transparent;animation:ad-spin .7s linear infinite"></span>Testing…` : `<i data-lucide="plug-zap"></i>Test`}</button>
        <button class="ad-mini" type="button" data-ap-edit="${esc(cred.id)}" ${locked}><i data-lucide="sliders-horizontal"></i>Edit</button>
        ${cred.is_default ? "" : `<button class="ad-mini" type="button" data-ap-default="${esc(cred.id)}" ${locked}><i data-lucide="badge-check"></i>Make default</button>`}
        <div class="grow"></div>
        <button class="ad-mini danger" type="button" data-ap-remove="${esc(cred.id)}" ${locked}><i data-lucide="trash-2"></i>Remove</button>
      </div>`
          : ""
      }
    </div>
    ${editing ? providerFormView(cred) : ""}`;
}

function providersCardView() {
  const a = state.assist;
  const view = a.providers;
  const head = `<div class="card-head"><i data-lucide="plug"></i>AI providers${view ? `<span class="grow"></span>${providerPill(view)}` : ""}</div>`;
  if (a.providerAccess === "forbidden") {
    return `
  <div class="ad-card">${head}
    <p class="ad-empty" style="margin:0">Only workspace owners and admins can see or change the AI providers and their keys.</p>
  </div>`;
  }
  if (a.providerAccess === "missing") {
    return `
  <div class="ad-card">${head}
    <p class="ad-empty" style="margin:0">Adding your own AI provider keys isn't available on this server yet. Ask your ${esc(brand.name)} contact.</p>
  </div>`;
  }
  if (!view) {
    return `
  <div class="ad-card">${head}
    ${a.providerError ? `<div class="formerr" role="alert"><i data-lucide="shield-alert"></i><span>${esc(a.providerError)}</span></div>` : ""}
  </div>`;
  }
  const canEdit = isAdminRole() && a.providerAccess === "ok";
  const creds = credentialsOf(view);
  const storageReady = !!view.storage_ready;
  const atMax = creds.length >= (view.max_credentials || 10);
  const current = creds.find((c) => c.is_default);
  const paused = current && current.cloud && !current.cloud_consent;
  const adding = a.form && a.form.mode === "add";
  return `
  <div class="ad-card">${head}
    ${providerStatusLine(view)}
    ${paused ? `<p class="ad-note"><i data-lucide="info"></i>Drafts are paused until ${esc(credentialName(current))} is allowed to receive conversations. Choose Edit on it and tick the box.</p>` : ""}
    ${a.providerError ? `<div class="formerr" role="alert"><i data-lucide="shield-alert"></i><span>${esc(a.providerError)}</span></div>` : ""}
    ${a.providerOk ? `<div class="formok" role="status"><i data-lucide="check"></i><span>${esc(a.providerOk)}</span></div>` : ""}
    ${
      storageReady
        ? ""
        : `<div class="formerr" role="note"><i data-lucide="lock"></i><span>Saving your own keys isn't set up on this server yet, so ${esc(brand.name)} can't store them safely. Ask your ${esc(brand.name)} contact.</span></div>`
    }
    ${creds.length ? `<div class="ad__stack" id="ap_list">${creds.map(providerRowView).join("")}</div>` : ""}
    ${
      adding
        ? `<div style="margin-top:${creds.length ? 14 : 0}px">${providerFormView(null)}</div>`
        : canEdit
          ? `<div class="ad__actions" style="border:none;padding:0;margin-top:14px">
      <button class="pf-btn pf-btn--ghost" id="ap_add" type="button" ${!storageReady || atMax || a.busy || a.form ? "disabled" : ""}><i data-lucide="plus"></i>Add a provider</button>
      ${atMax ? `<span class="saved-at">This workspace keeps up to ${esc(view.max_credentials || 10)} providers. Remove one to add another.</span>` : ""}
    </div>`
          : `<div class="ad__actions" style="border:none;padding:0;margin-top:14px"><span class="saved-at">Only owners and admins can change this.</span></div>`
    }
  </div>`;
}

// The add form (cred null) or the edit form of one saved provider.
function providerFormView(cred) {
  const a = state.assist;
  const view = a.providers;
  const p = a.picker;
  const editing = !!cred;
  const option = providerOption(editing ? cred.provider : p.provider);
  const name = editing ? credentialName(cred) : option.label;
  const busy = a.busy === "save";
  const dis = busy ? "disabled" : "";
  const current = credentialsOf(view).find((c) => c.is_default);
  const providerField = editing
    ? `<div class="fld" style="margin-bottom:16px"><span class="cap" style="display:block;font:700 11px/1 var(--uc-font-body);text-transform:uppercase;letter-spacing:.08em;color:var(--uc-fg2);margin-bottom:7px">Provider</span>
        <div style="font:600 14px/1.4 var(--uc-font-body);color:var(--uc-fg1);padding:8px 0 0">${esc(providerLabel(view, cred.provider))}${cred.base_url ? ` · <code>${esc(cred.base_url)}</code>` : ""}</div>
        <span class="fldhint">To use a different provider or endpoint, add it as another provider.</span></div>`
    : `<label class="fld" for="ap_provider"><span class="cap">Provider</span>
        <select class="sel" id="ap_provider" ${dis}>
          ${providerOptionsOf(view)
            .map((o) => {
              const taken = providerTaken(view, o);
              return `<option value="${esc(o.id)}" ${o.id === p.provider ? "selected" : ""} ${taken ? "disabled" : ""}>${esc(o.label)}${taken ? " — already added" : ""}</option>`;
            })
            .join("")}
        </select>
      </label>`;
  return `
  <div class="ad-card" style="margin:0;border-color:var(--uc-teal-600)" id="ap_form">
    <div class="card-head"><i data-lucide="${editing ? "sliders-horizontal" : "plus"}"></i>${editing ? `Edit ${esc(name)}` : "Add a provider"}</div>
    <div class="ad-row2">
      ${providerField}
      <label class="fld" for="ap_key"><span class="cap">API key</span>
        <input class="inp" id="ap_key" type="password" autocomplete="new-password" spellcheck="false" value="" placeholder="${editing ? `•••• ${esc(cred.key_last4)} · leave blank to keep` : "Paste the API key from your provider"}" ${dis}>
        <span class="fldhint">Stored encrypted. After you save, nobody can view the key again — not even admins.</span>
      </label>
    </div>
    ${
      editing
        ? ""
        : `<label class="fld" for="ap_base" id="ap_base_wrap" ${option.base_url === "required" ? "" : "hidden"}><span class="cap">Endpoint address</span>
      <input class="inp" id="ap_base" type="url" autocomplete="off" spellcheck="false" value="${esc(p.baseUrl || "")}" placeholder="https://models.example.com/v1" ${dis}>
      <span class="fldhint">The https:// address your provider gave you, up to (not including) /chat/completions.</span>
    </label>`
    }
    <div id="ap_model_wrap">${modelPickerView()}</div>
    <label class="fld" for="ap_label"><span class="cap">Name (optional)</span>
      <input class="inp" id="ap_label" type="text" maxlength="60" autocomplete="off" value="${esc(editing ? cred.label || "" : "")}" placeholder="e.g. ${esc(option.label)} (team)" ${dis}>
      <span class="fldhint">Shown in this list so your team can tell keys apart.</span>
    </label>
    <label class="ad-check" for="ap_confirm">
      <input type="checkbox" id="ap_confirm" ${editing && cred.cloud_consent ? "checked" : ""} ${dis}>
      <span><b id="ap_confirm_name">I confirm our workspace may send conversation text to ${esc(name)}.</b>
      <span class="fldhint">Drafting means sending the conversation to this provider. Leave this unchecked if conversations may include patient information and you don't have a business associate agreement (BAA) with this provider. Each provider needs its own OK: this key is never used for drafts while the box is unchecked.</span></span>
    </label>
    ${
      !editing && current
        ? `<label class="ad-check" for="ap_make_default"><input type="checkbox" id="ap_make_default" ${dis}><span><b>Use this provider for drafts</b><span class="fldhint">Makes it the default instead of ${esc(credentialName(current))}.</span></span></label>`
        : ""
    }
    <div id="ap_form_msg"></div>
    <div class="ad__actions" style="border:none;padding:0;margin-top:8px">
      <button class="pf-btn pf-btn--primary" id="ap_save" type="button" ${dis}>${busy ? `<span class="btnspin"></span>Saving…` : `<i data-lucide="save"></i>Save`}</button>
      <button class="pf-btn pf-btn--ghost" id="ap_cancel" type="button" ${dis}>Cancel</button>
    </div>
  </div>`;
}

// ---- the model picker (one per open form) --------------------------------------

function addPicker(provider) {
  const option = providerOption(provider);
  return { mode: "add", provider, baseUrl: "", status: "idle", list: [], errorClass: "", message: "", value: option.default_model || "", touched: false, other: false, seq: 0 };
}

function editPicker(cred) {
  const picker = { mode: "edit", provider: cred.provider, baseUrl: cred.base_url || "", status: "idle", list: [], errorClass: "", message: "", value: cred.model, touched: false, other: false, seq: 0 };
  const cached = savedModelLists.get(savedListKey(cred));
  if (cached && cached.status === "ready") applyModelList(picker, cached.list, cred.model);
  return picker;
}

// A list arrived. Keep what the admin chose; otherwise preselect the saved
// model, else the server's default for the provider if offered, else the
// newest model. A saved model the provider no longer offers stays selected
// (with a warning) so a save never changes it silently.
function applyModelList(p, models, savedModel) {
  p.list = Array.isArray(models) ? models : [];
  p.status = "ready";
  p.errorClass = "";
  p.message = "";
  if (!p.list.length || p.list.some((m) => m.id === p.value)) return;
  if (savedModel && p.value === savedModel && !p.touched) return;
  if (p.touched && p.value) {
    p.other = true;
    return;
  }
  const option = providerOption(p.provider);
  p.value = (p.list.find((m) => m.id === option.default_model) || p.list[0]).id;
  p.other = false;
}

function resetPicker(p, provider) {
  const option = providerOption(provider);
  Object.assign(p, { provider, status: "idle", list: [], errorClass: "", message: "", value: option.default_model || "", touched: false, other: false });
  p.seq += 1;
}

function modelPickerView() {
  const a = state.assist;
  const p = a.picker;
  if (!p) return "";
  const cred = a.form && a.form.mode === "edit" ? credentialById(a.form.id) : null;
  const name = cred ? credentialName(cred) : providerOption(p.provider).label;
  const dis = a.busy ? "disabled" : "";
  const loading = p.status === "loading";
  const load = `<button class="ad-mini" id="ap_load_models" type="button" style="flex-shrink:0" ${loading || a.busy ? "disabled" : ""}><i data-lucide="refresh-cw"></i>${p.status === "ready" ? "Reload models" : "Load models"}</button>`;
  let control;
  let hint;
  if (p.status === "ready" && p.list.length) {
    const listed = p.list.some((m) => m.id === p.value);
    const savedMissing = !!cred && p.value === cred.model && !listed && !p.other;
    const other = !savedMissing && (p.other || (!listed && !!p.value));
    const options = [
      savedMissing ? `<option value="${esc(p.value)}" selected>${esc(p.value)} — not offered</option>` : "",
      ...p.list.map((m) => `<option value="${esc(m.id)}" ${!other && m.id === p.value ? "selected" : ""}>${esc(modelOptionText(m))}</option>`),
      `<option value="${MODEL_OTHER}" ${other ? "selected" : ""}>Other (type a model ID)</option>`,
    ].join("");
    control = `<select class="sel" id="ap_model_sel" style="flex:1;min-width:0" ${dis}>${options}</select>`;
    hint = savedMissing
      ? `<p class="ad-note" style="margin:8px 0 0"><i data-lucide="info"></i>Your saved model isn't offered by this provider anymore — pick one.</p>`
      : `<span class="fldhint">${p.list.length} model${p.list.length === 1 ? "" : "s"} from ${esc(name)}, newest first.</span>`;
    const otherInput = `<input class="inp" id="ap_model" type="text" autocomplete="off" spellcheck="false" value="${esc(other ? p.value : "")}" placeholder="The model ID, exactly as your provider writes it" style="margin-top:8px" ${other ? "" : "hidden"} ${dis}>`;
    return `<label class="fld" for="ap_model_sel"><span class="cap">Model</span><div style="display:flex;gap:8px;align-items:center">${control}${load}</div>${otherInput}${hint}</label>`;
  }
  const option = providerOption(p.provider);
  control = `<input class="inp" id="ap_model" type="text" autocomplete="off" spellcheck="false" style="flex:1;min-width:0" value="${esc(p.value)}" placeholder="${esc(option.default_model || "The model ID from your provider")}" ${dis}>`;
  if (loading) {
    hint = `<span class="fldhint"><span style="display:inline-block;width:10px;height:10px;border-radius:99px;border:2px solid currentColor;border-top-color:transparent;animation:ad-spin .7s linear infinite;vertical-align:-1px;margin-right:6px"></span>Loading models from ${esc(name)}…</span>`;
  } else if (p.status === "error") {
    hint = `<span class="fldhint" style="color:var(--uc-terra-700)">${esc(p.message || MODEL_LIST_COPY[p.errorClass] || MODEL_LIST_COPY.unknown)}</span>`;
  } else if (p.status === "ready") {
    hint = `<span class="fldhint">${esc(name)} didn't list any models for this key. Type the model ID instead.</span>`;
  } else {
    hint = `<span class="fldhint">${cred ? `Choose Load models to pick from ${esc(name)}'s list.` : `Paste your key, then choose Load models to pick from ${esc(name)}'s list.`}</span>`;
  }
  return `<label class="fld" for="ap_model"><span class="cap">Model</span><div style="display:flex;gap:8px;align-items:center">${control}${load}</div>${hint}</label>`;
}

function policyCardView() {
  const a = state.assist;
  const p = a.policy || {};
  const mode = p.mode || "manual";
  // A provider read refused by the server means this session is not an owner
  // or admin, whatever the token claims: show everything read-only.
  const canEdit = isAdminRole() && a.providerAccess !== "forbidden";
  // Each saved provider carries its own consent. This box is the consent for
  // the platform's shared provider: it shows only while drafts use that (or on
  // a server without workspace keys, where the platform is all there is), and
  // never when the provider list failed to load.
  const effective = (a.providers && a.providers.effective) || {};
  const sharedConsent =
    a.providerAccess === "missing" || a.providerAccess === "forbidden" || (a.providerAccess === "ok" && effective.source === "platform" && effective.cloud);
  return `
  <div class="ad-card">
    <div class="card-head"><i data-lucide="sparkles"></i>How drafting works<span class="grow"></span>${p.stored ? `<span class="ad-pill ad-pill--ok">Customised</span>` : `<span class="ad-pill ad-pill--dim">Defaults</span>`}</div>
    ${a.error ? `<div class="formerr" role="alert"><i data-lucide="shield-alert"></i><span>${esc(a.error)}</span></div>` : ""}
    ${a.ok ? `<div class="formok" role="status"><i data-lucide="check"></i><span>Drafting settings saved.</span></div>` : ""}
    <label class="fld" for="as_mode"><span class="cap">When to write a draft</span>
      <select class="sel" id="as_mode" ${canEdit ? "" : "disabled"}>
        <option value="manual" ${mode === "manual" ? "selected" : ""}>Only when a teammate clicks “Draft”</option>
        <option value="auto_draft" ${mode === "auto_draft" ? "selected" : ""}>Automatically, for every new customer message</option>
      </select>
      <span class="fldhint">Either way a person reviews every draft before anything is sent.</span>
    </label>
    ${
      sharedConsent
        ? `<label class="ad-check" for="as_cloud">
      <input type="checkbox" id="as_cloud" ${p.cloud_allowed ? "checked" : ""} ${canEdit ? "" : "disabled"}>
      <span><b>I confirm our workspace may send conversation text to ${a.providerAccess === "ok" ? `${esc(brand.name)}'s shared AI provider` : "a cloud AI provider"}.</b>
      <span class="fldhint">Leave this unchecked if conversations may include patient information and no business associate agreement (BAA) covers the provider.</span></span>
    </label>`
        : ""
    }
    <label class="fld" for="as_instructions"><span class="cap">House style for drafts (optional, 500 characters)</span>
      <textarea class="inp" id="as_instructions" rows="3" maxlength="500" ${canEdit ? "" : "disabled"} placeholder="e.g. Warm and brief. Never promise dates. Sign off as the ${esc(brand.company)} support team.">${esc(p.instructions || "")}</textarea>
    </label>
    <div class="ad__actions" style="border:none;padding:0;margin-top:4px">
      <span class="saved-at">${p.updated_at && p.stored ? `Last updated ${esc(formatStamp(p.updated_at))}` : "Never saved — drafts are written only when a teammate asks."}</span>
      <div class="grow"></div>
      ${canEdit ? `<button class="pf-btn pf-btn--primary" id="as_save" type="button"><i data-lucide="save"></i>Save</button>` : `<span class="saved-at">Only owners and admins can change this.</span>`}
    </div>
  </div>`;
}

function assistView() {
  if (state.assist === null) return loadingView("Loading Assist…");
  const a = state.assist;
  const intro = `
  <span class="pf-eyebrow pf-eyebrow--teal"><span class="spark"></span>Assist</span>
  <h1>${esc(brand.name)} Assist</h1>
  <p class="sub">${esc(brand.name)} can draft replies for your team using an AI model from a provider you choose. Drafts are private to your team — customers never see one until a teammate sends it.</p>
  ${banners()}`;
  if (!a.available) {
    return `${intro}
  <div class="ad-card">
    <div class="card-head"><i data-lucide="sparkles"></i>Not switched on<span class="grow"></span><span class="ad-pill ad-pill--dim">Off</span></div>
    <p class="ad-empty" style="margin:0">${esc(brand.name)} Assist is switched off for this server. Ask your ${esc(brand.name)} contact to turn it on.</p>
  </div>`;
  }
  return `${intro}
  ${providersCardView()}
  ${policyCardView()}
  <div class="ad-card">
    <div class="card-head"><i data-lucide="eye-off"></i>What your team sees</div>
    <p class="ad-empty" style="margin:0">In the Operator Portal a <b>Draft with ${esc(brand.name)}</b> button sits next to Send. The draft appears in the conversation marked private, with a note on where it came from. Every request is logged before any text leaves ${esc(brand.name)}, and each teammate is limited to a handful of drafts per minute.</p>
  </div>`;
}

// ---- AI providers: actions ------------------------------------------------------

async function savePolicy(body) {
  const policy = await putAssistPolicy(state.token, body);
  state.assist.policy = policy;
  return policy;
}

// The open form's typed values.
function readProviderForm() {
  const a = state.assist;
  const cred = a.form && a.form.mode === "edit" ? credentialById(a.form.id) : null;
  return {
    provider: cred ? cred.provider : ($("ap_provider") || {}).value || "",
    baseUrl: cred ? cred.base_url || "" : (($("ap_base") || {}).value || "").trim(),
    apiKey: (($("ap_key") || {}).value || "").trim(),
    model: currentModel(),
    label: (($("ap_label") || {}).value || "").trim(),
    cloudConsent: !!($("ap_confirm") || {}).checked,
    makeDefault: !!($("ap_make_default") || {}).checked,
  };
}

function currentModel() {
  const select = $("ap_model_sel");
  if (select && select.value !== MODEL_OTHER) return select.value;
  return (($("ap_model") || {}).value || "").trim();
}

// In-place updates: the open form keeps whatever was typed into it.
function refreshModelPicker() {
  const wrap = $("ap_model_wrap");
  if (!wrap) return;
  wrap.innerHTML = modelPickerView();
  wireModelPicker();
  mountIcons();
}

function refreshProviderRow(id) {
  const cred = credentialById(id);
  if (!cred) return;
  const status = document.querySelector(`[data-ap-status="${CSS.escape(id)}"]`);
  if (status) status.innerHTML = providerStatusChips(cred);
  const a = state.assist;
  const editing = a.form && a.form.mode === "edit" && a.form.id === id;
  const note = document.querySelector(`[data-ap-note="${CSS.escape(id)}"]`);
  if (note) note.innerHTML = editing ? "" : providerRowNote(cred);
  mountIcons();
}

function showFormMessage(message) {
  const slot = $("ap_form_msg");
  if (slot) slot.innerHTML = message ? `<div class="formerr" role="alert" style="margin:4px 0 12px"><i data-lucide="shield-alert"></i><span>${esc(message)}</span></div>` : "";
  mountIcons();
}

// A saved provider's model list, at most one request at a time per key.
function fetchSavedModels(cred) {
  const key = savedListKey(cred);
  const existing = savedModelLists.get(key);
  if (existing && existing.status === "loading") return existing.promise;
  const entry = { status: "loading", list: [], errorClass: "" };
  entry.promise = listSavedProviderModels(state.token, cred.id).then(
    (result) => {
      if (result && result.ok) Object.assign(entry, { status: "ready", list: result.models || [] });
      else Object.assign(entry, { status: "error", errorClass: (result && result.error_class) || "unknown" });
      return result;
    },
    (e) => {
      // Not a provider answer (busy server, rate limit): try again next time.
      savedModelLists.delete(key);
      throw e;
    },
  );
  savedModelLists.set(key, entry);
  return entry.promise;
}

// Saved providers check their model list (and so their key) when the page
// opens: the default first, a few at most.
async function autoLoadSavedModelLists() {
  const a = state.assist;
  if (autoListing || !a || !a.providers || a.providerAccess !== "ok" || !isAdminRole()) return;
  const pending = credentialsOf(a.providers)
    .slice(0, AUTO_MODEL_LISTS)
    .filter((c) => !savedModelLists.has(savedListKey(c)));
  if (!pending.length) return;
  autoListing = true;
  try {
    for (const cred of pending) {
      if (state.assist !== a) break;
      const request = fetchSavedModels(cred);
      refreshProviderRow(cred.id);
      try {
        await request;
      } catch (e) {
        if (authGuard(e)) return;
      }
      if (state.assist !== a) break;
      refreshProviderRow(cred.id);
      syncEditPicker(cred);
    }
  } finally {
    autoListing = false;
  }
}

// An edit form waiting on the list that just arrived.
function syncEditPicker(cred) {
  const a = state.assist;
  const p = a.picker;
  if (!p || !a.form || a.form.mode !== "edit" || a.form.id !== cred.id || p.status === "ready" || p.ownList) return;
  const cached = savedModelLists.get(savedListKey(cred));
  if (!cached || cached.status === "loading") return;
  if (cached.status === "ready") applyModelList(p, cached.list, cred.model);
  else Object.assign(p, { status: "error", errorClass: cached.errorClass, message: "" });
  refreshModelPicker();
}

async function loadPickerModels() {
  const a = state.assist;
  const p = a.picker;
  if (!p || p.status === "loading") return;
  const cred = a.form && a.form.mode === "edit" ? credentialById(a.form.id) : null;
  const form = readProviderForm();
  const option = providerOption(p.provider);
  if (!cred && !form.apiKey) {
    Object.assign(p, { status: "error", errorClass: "", message: "Paste the API key first, then load models." });
    refreshModelPicker();
    return;
  }
  if (!cred && option.base_url === "required" && !form.baseUrl) {
    Object.assign(p, { status: "error", errorClass: "", message: "Enter the endpoint address first, then load models." });
    refreshModelPicker();
    return;
  }
  const seq = ++p.seq;
  p.status = "loading";
  // A key typed into an edit form lists that key's models, not the saved key's.
  p.ownList = !!(cred && form.apiKey);
  if (!cred) p.baseUrl = form.baseUrl;
  refreshModelPicker();
  try {
    let result;
    if (form.apiKey) {
      result = await listProviderModels(state.token, { provider: p.provider, baseUrl: form.baseUrl, apiKey: form.apiKey });
    } else {
      // An explicit reload asks again; a list already on its way is awaited.
      const cached = savedModelLists.get(savedListKey(cred));
      if (cached && cached.status !== "loading") savedModelLists.delete(savedListKey(cred));
      result = await fetchSavedModels(cred);
      refreshProviderRow(cred.id);
    }
    if (state.assist !== a || a.picker !== p || p.seq !== seq) return;
    if (result && result.ok) applyModelList(p, result.models, cred && cred.model);
    else Object.assign(p, { status: "error", errorClass: (result && result.error_class) || "unknown", message: "" });
  } catch (e) {
    if (authGuard(e)) return;
    if (state.assist !== a || a.picker !== p || p.seq !== seq) return;
    Object.assign(p, {
      status: "error",
      errorClass: "",
      message: errCode(e) === "RATE_LIMITED" ? "Too many lookups in a row. Try again in a minute, or type a model ID." : e.message || "Couldn't load the models. You can type a model ID instead.",
    });
  }
  refreshModelPicker();
}

function wireModelPicker() {
  const p = state.assist && state.assist.picker;
  if (!p) return;
  const select = $("ap_model_sel");
  const input = $("ap_model");
  if (select) {
    select.onchange = () => {
      p.touched = true;
      if (select.value === MODEL_OTHER) {
        p.other = true;
        p.value = input ? input.value.trim() : "";
      } else {
        p.other = false;
        p.value = select.value;
      }
      refreshModelPicker();
      if (p.other && $("ap_model")) $("ap_model").focus();
    };
  }
  if (input) {
    input.oninput = () => {
      p.touched = true;
      p.value = input.value.trim();
    };
  }
  const load = $("ap_load_models");
  if (load) load.onclick = () => loadPickerModels();
}

function openProviderForm(form) {
  const a = state.assist;
  if (a.busy) return;
  const cred = form.mode === "edit" ? credentialById(form.id) : null;
  if (form.mode === "edit" && !cred) return;
  a.form = form;
  a.picker = cred ? editPicker(cred) : addPicker(firstFreeProvider(a.providers));
  a.providerOk = "";
  a.providerError = "";
  render();
  // A saved key lists its provider's models straight away.
  if (cred && a.picker.status !== "ready") loadPickerModels();
}

function closeProviderForm() {
  const a = state.assist;
  if (a.busy) return;
  a.form = null;
  a.picker = null;
  render();
}

function wireProviderForm() {
  const a = state.assist;
  if (!a.form || !a.picker) return;
  const p = a.picker;
  const providerSelect = $("ap_provider");
  if (providerSelect) {
    // Switching provider in the add form starts that provider's picker afresh
    // and never touches a saved provider. The typed key stays in its field but
    // is not sent anywhere until the admin chooses Load models or Save.
    providerSelect.onchange = () => {
      resetPicker(p, providerSelect.value);
      const option = providerOption(providerSelect.value);
      const baseWrap = $("ap_base_wrap");
      if (baseWrap) baseWrap.hidden = option.base_url !== "required";
      const labelInput = $("ap_label");
      if (labelInput) labelInput.placeholder = `e.g. ${option.label} (team)`;
      const confirm = $("ap_confirm");
      if (confirm) confirm.checked = false; // consent belongs to the provider it names
      const confirmName = $("ap_confirm_name");
      if (confirmName) confirmName.textContent = `I confirm our workspace may send conversation text to ${option.label}.`;
      showFormMessage("");
      refreshModelPicker();
    };
  }
  const base = $("ap_base");
  if (base) {
    base.oninput = () => {
      const confirm = $("ap_confirm");
      if (confirm) confirm.checked = false;
      if (p.status !== "idle" && base.value.trim() !== p.baseUrl) {
        resetPicker(p, p.provider);
        refreshModelPicker();
      }
    };
  }
  wireModelPicker();
  const cancel = $("ap_cancel");
  if (cancel) cancel.onclick = () => closeProviderForm();
  const save = $("ap_save");
  if (save) save.onclick = () => saveProviderForm(save);
}

async function saveProviderForm(button) {
  const a = state.assist;
  if (a.busy || !a.form) return;
  const form = a.form;
  const cred = form.mode === "edit" ? credentialById(form.id) : null;
  const values = readProviderForm();
  const option = providerOption(values.provider);
  if (!cred && !values.apiKey) return showFormMessage("Paste the API key from your provider, then save.");
  if (!cred && option.base_url === "required" && !values.baseUrl) return showFormMessage("Enter the endpoint address your provider gave you.");
  if (!values.model && option.model_required) return showFormMessage("Pick a model — choose Load models to see your provider's list, or type a model ID.");
  showFormMessage("");
  a.busy = "save";
  const original = button.innerHTML;
  setBusy(button, true, "Saving…");
  try {
    const picker = a.picker;
    const view = cred
      ? await updateAIProvider(state.token, cred.id, {
          ...values,
          // Only a changed box is sent; the form's updated_at guards the rest.
          cloudConsent: values.cloudConsent !== !!cred.cloud_consent ? values.cloudConsent : undefined,
          expectedUpdatedAt: cred.updated_at,
        })
      : await addAIProvider(state.token, values);
    a.providers = view;
    // The list the admin picked from belongs to the saved key now.
    const saved = cred
      ? credentialById(cred.id)
      : credentialsOf(view).find((c) => c.provider === values.provider && (option.base_url !== "required" || (c.base_url || "").toLowerCase() === values.baseUrl.replace(/\/+$/, "").toLowerCase()));
    if (saved && picker && picker.status === "ready" && picker.list.length) {
      savedModelLists.set(savedListKey(saved), { status: "ready", list: picker.list, errorClass: "" });
    }
    if (saved) delete a.tests[saved.id];
    a.busy = "";
    a.form = null;
    a.picker = null;
    a.providerError = "";
    a.providerOk = cred ? `${credentialName(saved || cred)} saved.` : `${saved ? credentialName(saved) : option.label} added.`;
    render();
  } catch (e) {
    if (authGuard(e)) return;
    a.busy = "";
    button.disabled = false;
    button.innerHTML = original;
    mountIcons();
    showFormMessage(
      isForbidden(e)
        ? "Only owners and admins can change this."
        : errCode(e) === "CHANGED"
          ? "Someone changed this provider since you opened it. Cancel, then choose Edit again to see the latest."
          : e.message || "Couldn't save. Please try again.",
    );
  }
}

async function runProviderAction(id, busy, action) {
  const a = state.assist;
  if (a.busy || a.form) return;
  a.busy = `${busy}:${id}`;
  a.providerError = "";
  a.providerOk = "";
  render();
  try {
    await action();
  } catch (e) {
    if (authGuard(e)) return;
    a.providerError = isForbidden(e)
      ? "Only owners and admins can change this."
      : errCode(e) === "RATE_LIMITED"
        ? "Too many checks in a row. Try again in a minute."
        : e.message || "That didn't work. Please try again.";
  }
  a.busy = "";
  render();
}

function wireProviderCard() {
  const a = state.assist;
  if (!a || a.providerAccess !== "ok" || !a.providers) return;
  const add = $("ap_add");
  if (add) add.onclick = () => openProviderForm({ mode: "add" });
  panel.querySelectorAll("[data-ap-edit]").forEach((btn) => {
    btn.onclick = () => openProviderForm({ mode: "edit", id: btn.getAttribute("data-ap-edit") });
  });
  panel.querySelectorAll("[data-ap-test]").forEach((btn) => {
    const id = btn.getAttribute("data-ap-test");
    btn.onclick = () =>
      runProviderAction(id, "test", async () => {
        delete a.tests[id];
        a.tests[id] = await testAIProvider(state.token, id);
      });
  });
  panel.querySelectorAll("[data-ap-default]").forEach((btn) => {
    const id = btn.getAttribute("data-ap-default");
    btn.onclick = () =>
      runProviderAction(id, "default", async () => {
        a.providers = await setDefaultAIProvider(state.token, id);
        const cred = credentialById(id);
        a.providerOk = `Drafts now use ${cred ? credentialName(cred) : "this provider"}.${cred && cred.cloud && !cred.cloud_consent ? " They stay paused until it's allowed to receive conversations." : ""}`;
      });
  });
  panel.querySelectorAll("[data-ap-remove]").forEach((btn) => {
    const id = btn.getAttribute("data-ap-remove");
    btn.onclick = () => {
      const cred = credentialById(id);
      if (!cred) return;
      const platform = a.providers.platform && a.providers.platform.available;
      const fallback = cred.is_default
        ? platform
          ? ` Drafts will use ${brand.name}'s shared access once an admin allows it.`
          : " Drafting stops until you make another provider the default."
        : "";
      if (!window.confirm(`Remove the ${credentialName(cred)} key?${fallback}`)) return;
      runProviderAction(id, "remove", async () => {
        a.providers = await removeAIProvider(state.token, id);
        delete a.tests[id];
        // Removing the default also withdraws the shared-access consent.
        if (cred.is_default) a.policy = await getAssistPolicy(state.token);
        a.providerOk = `${credentialName(cred)} key removed.`;
      });
    };
  });
  wireProviderForm();
  autoLoadSavedModelLists();
}

function wireAssist() {
  if (!state.assist || !state.assist.available) return;
  wireProviderCard();
  const save = $("as_save");
  if (!save) return;
  save.onclick = async () => {
    if (state.assist.saving) return;
    state.assist.saving = true;
    state.assist.error = "";
    state.assist.ok = false;
    setBusy(save, true, "Saving…");
    const body = {
      mode: ($("as_mode") || {}).value || "manual",
      instructions: (($("as_instructions") || {}).value || "").trim(),
    };
    // Consent for saved providers lives on each provider. This card's box is
    // the shared provider's consent and is sent only when it was changed, so
    // a save of the mode or house style never re-sends a consent (a stale
    // page can never re-grant one).
    const shared = $("as_cloud");
    const loaded = !!(state.assist.policy && state.assist.policy.cloud_allowed);
    if (shared && shared.checked !== loaded) body.cloud_allowed = shared.checked;
    try {
      await savePolicy(body);
      state.assist.saving = false;
      state.assist.ok = true;
    } catch (e) {
      if (authGuard(e)) return;
      state.assist.saving = false;
      state.assist.error = isForbidden(e)
        ? "Only owners and admins can change this."
        : e.message || "Couldn't save the drafting settings. Please try again.";
    }
    render();
  };
}

function complianceView() {
  return `
  <span class="pf-eyebrow pf-eyebrow--teal"><span class="spark"></span>Compliance</span>
  <h1>Compliance & retention</h1>
  <p class="sub">${esc(brand.name)} is built for regulated conversations: every workspace's data is kept apart, everything is encrypted in transit and at rest, and admin actions are logged.</p>
  ${banners()}
  <div class="ad-card">
    <div class="card-head"><i data-lucide="archive"></i>How long we keep things</div>
    ${retentionCard()}
  </div>
  <div class="ad-card">
    <div class="card-head"><i data-lucide="lock"></i>Encryption & isolation</div>
    <div class="ad-kv ad-kv--2">
      <div class="kv"><b>In transit</b><span>TLS 1.3</span></div>
      <div class="kv"><b>At rest</b><span>AES-256</span></div>
      <div class="kv"><b>Workspace isolation</b><span>Every workspace's data is isolated at the database level</span></div>
      <div class="kv"><b>Audit</b><span>A tamper-proof log of admin, key and setup actions</span></div>
    </div>
  </div>`;
}

// "2190 days" reads as "6 years" when it divides evenly.
function formatRetention(days) {
  const n = Number(days);
  if (!Number.isFinite(n) || n <= 0) return "—";
  if (n % 365 === 0) {
    const years = n / 365;
    return `${years} year${years === 1 ? "" : "s"}`;
  }
  return `${n} day${n === 1 ? "" : "s"}`;
}

function retentionCard() {
  if (state.retention === null) return loadingView("Loading retention policy…");
  if (!state.retention) return `<div class="ad-empty">Retention policy unavailable for this account.</div>`;
  const r = state.retention;
  return `<div class="ad-kv">
    <div class="kv"><b>Notifications</b><span>${esc(formatRetention(r.notification_retention_days))}</span></div>
    <div class="kv"><b>Audit log</b><span>${esc(formatRetention(r.audit_log_retention_days))}</span></div>
    ${r.updated_at ? `<div class="kv"><b>Last updated</b><span>${esc(formatDay(r.updated_at))}</span></div>` : ""}
  </div>`;
}

// ---- widget editor (behavior preserved from the focused editor) ----------------

const widgetView = () => {
  const intro = `
  <span class="pf-eyebrow pf-eyebrow--teal"><span class="spark"></span>Widget</span>
  <h1>Customize your chat widget</h1>
  <p class="sub">How the ${esc(brand.name)} chat widget looks and behaves on your website.</p>
  ${banners()}
  ${state.saveError ? `<div class="formerr" role="alert"><i data-lucide="shield-alert"></i><span>${esc(state.saveError)}</span></div>` : ""}
  ${state.loadError ? `<div class="formerr" role="alert"><i data-lucide="info"></i><span>${esc(state.loadError)}</span></div>` : ""}
  ${state.saveOk ? `<div class="formok"><i data-lucide="check"></i><span>Saved. Your widget configuration is up to date.</span></div>` : ""}
  ${applicationScopeCard("widget_binding")}`;
  if (state.loading || !state.config) {
    return `${intro}
  <div class="ad-card">${loadingView("Loading widget settings…")}</div>`;
  }
  const c = state.config;
  const b = c.branding;
  return `${intro}
  <div class="preview" id="preview"></div>

  <div class="ad-card">
  <div class="ad__grid">
    <div class="ad__section span2" style="margin-top:0;padding-top:0;border-top:none">Branding</div>
    <label class="fld" for="f_display"><span class="cap">Display name</span><input class="inp" id="f_display" placeholder="Acme Health" value="${esc(b.display_name)}"></label>
    <label class="fld" for="f_title"><span class="cap">Widget title</span><input class="inp" id="f_title" placeholder="Chat with us" value="${esc(b.widget_title)}"></label>

    <label class="fld" for="f_primary"><span class="cap">Primary color</span>
      <div class="colorrow">
        <input type="color" id="f_primary_c" value="${esc(normHex(b.primary_color, DEFAULT_CONFIG.branding.primary_color))}" aria-label="Primary color picker">
        <input class="inp" id="f_primary" value="${esc(b.primary_color)}" placeholder="#0f766e" autocapitalize="off" autocomplete="off" spellcheck="false">
      </div>
    </label>
    <label class="fld" for="f_accent"><span class="cap">Accent color</span>
      <div class="colorrow">
        <input type="color" id="f_accent_c" value="${esc(normHex(b.accent_color, DEFAULT_CONFIG.branding.accent_color))}" aria-label="Accent color picker">
        <input class="inp" id="f_accent" value="${esc(b.accent_color)}" placeholder="#bb5a3c" autocapitalize="off" autocomplete="off" spellcheck="false">
      </div>
    </label>

    <label class="fld span2" for="f_logo"><span class="cap">Logo URL</span><input class="inp" id="f_logo" type="url" placeholder="https://cdn.example.com/logo.svg" value="${esc(b.logo_url)}" autocapitalize="off" autocomplete="off" spellcheck="false"></label>

    <div class="ad__section span2">Behavior</div>
    <label class="fld span2" for="f_welcome"><span class="cap">Welcome message</span><textarea class="inp" id="f_welcome" placeholder="Hi! How can we help you today?">${esc(c.welcome_message)}</textarea></label>

    <div class="span2">
      <label class="ad-check" for="f_note_on">
        <input type="checkbox" id="f_note_on" ${c.composer_note === "" ? "" : "checked"}>
        <span><b>Show a note under the message box</b><span class="fldhint">A one-line reminder customers see while they type. It doesn't block anything they send.</span></span>
      </label>
      <label class="fld" for="f_note"><span class="cap">Note under the message box</span><input class="inp" id="f_note" maxlength="${COMPOSER_NOTE_MAX}" value="${esc(c.composer_note || DEFAULT_COMPOSER_NOTE)}" placeholder="${esc(DEFAULT_COMPOSER_NOTE)}" ${c.composer_note === "" ? "disabled" : ""}><span class="fldhint">One line, up to ${COMPOSER_NOTE_MAX} characters.</span></label>
    </div>

    <div class="ad__section span2">When nobody's online</div>
    <div class="span2">
      <label class="ad-check" for="f_offline_capture">
        <input type="checkbox" id="f_offline_capture" ${c.offline_email_capture === false ? "" : "checked"}>
        <span><b>Ask visitors for an e-mail when nobody from your team is online</b><span class="fldhint">The widget shows the line below and, once the visitor has left an address, promises to reply there. When a teammate answers, the visitor gets a short e-mail with a link back to the page. The e-mail never contains the reply itself.</span></span>
      </label>
      <label class="fld" for="f_offline_message"><span class="cap">What visitors see when you're away</span><input class="inp" id="f_offline_message" maxlength="${OFFLINE_MESSAGE_MAX}" value="${esc(c.offline_message || "")}" placeholder="${esc(DEFAULT_OFFLINE_MESSAGE)}"><span class="fldhint">One line, up to ${OFFLINE_MESSAGE_MAX} characters. Leave empty for the standard line.</span></label>
    </div>

    <label class="fld" for="f_launcher"><span class="cap">Launcher position</span>
      <select class="sel" id="f_launcher">
        ${LAUNCHER_OPTIONS.map((o) => `<option value="${o.id}" ${c.launcher_position === o.id ? "selected" : ""}>${esc(o.label)}</option>`).join("")}
      </select>
    </label>
    <div></div>

    <label class="fld span2" for="f_origins"><span class="cap">Website addresses</span><textarea class="inp mono" id="f_origins" placeholder="https://www.yourclinic.com&#10;https://app.yourclinic.com">${esc((c.allowed_origins || []).join("\n"))}</textarea><span class="fldhint">The chat widget only appears on these sites. One per line, e.g. https://www.yourclinic.com</span></label>
  </div>

  <label class="checkrow span2" for="f_published">
    <input type="checkbox" id="f_published" ${c.is_published ? "checked" : ""}>
    <span class="lbl"><b>Published</b><span>When on, these settings are live on your website.</span></span>
  </label>

  <div class="ad__actions">
    <span class="saved-at" id="savedAt">${c.updated_at ? `Last updated ${esc(formatStamp(c.updated_at))}` : "Not saved yet"}</span>
    <div class="grow"></div>
    <button class="pf-btn pf-btn--primary pf-btn--lg" id="saveBtn" type="button"><i data-lucide="save"></i>Save changes</button>
  </div>
  </div>`;
};

// Which app the Widget / Install / API keys settings apply to. Hidden when the
// workspace has exactly one app (nothing to choose).
function applicationScopeCard(selectId) {
  const options = applicationEnvironmentOptions(currentWidgetBinding());
  if ((options.match(/<option/g) || []).length < 2) return "";
  return `
  <div class="ad-card">
    <div class="card-head"><i data-lucide="boxes"></i>Which app</div>
    <label class="fld" for="${selectId}" style="margin:0"><span class="cap">App</span><select class="sel" id="${selectId}">${options}</select><span class="fldhint">Settings on this page apply to this app only.</span></label>
  </div>`;
}

// ---- render + wiring -----------------------------------------------------------

function render() {
  document.querySelector(".ad__body").classList.toggle("ad__body--center", state.view !== "console");
  if (state.view === "console") {
    panel.classList.add("ad__panel--wide");
    topMeta.innerHTML = `${headerIdentity()}${
      portalUrl()
        ? `<a class="signout" href="${esc(portalUrl())}" target="_blank" rel="noopener" aria-label="Open the Operator Portal" title="Open the Operator Portal"><i data-lucide="headset"></i><span>Open Portal</span></a>`
        : ""
    }<button class="signout" id="signOut" type="button" aria-label="Sign out" title="Sign out"><i data-lucide="log-out"></i><span>Sign out</span></button>`;
    panel.innerHTML = `
      <div class="ad__console">
        <nav class="ad__nav" id="nav">${navView()}</nav>
        <div class="ad__content" id="content" ${state.busy ? 'aria-busy="true"' : ""}>${sectionView()}</div>
      </div>`;
    wireConsole();
  } else if (state.view === "wrongDoor") {
    panel.classList.remove("ad__panel--wide");
    topMeta.innerHTML = `<span>Signed in as <b>${esc(state.email || "a teammate")}</b></span>`;
    panel.innerHTML = wrongDoorView();
  } else {
    panel.classList.remove("ad__panel--wide");
    topMeta.innerHTML = "";
    panel.innerHTML = loginView();
    wireLogin();
  }

  const signOut = $("signOut");
  if (signOut) signOut.onclick = () => doSignOut();

  mountIcons();
}

// "{workspace} · {name} [Owner]" — the member row for this session when the
// members list has loaded, the email until then.
function headerIdentity() {
  const userId = String(claimsFromToken(state.token).user_id || "");
  const me = Array.isArray(state.members) && userId ? state.members.find((m) => m.user_id === userId) : null;
  const who = (me && me.display_name) || state.email || "owner";
  const workspace = state.workspace && state.workspace.name ? `${esc(state.workspace.name)} · ` : "";
  const pill = me && me.org_role ? ` <span class="ad-pill ad-pill--dim">${esc(label("role", me.org_role))}</span>` : "";
  return `<span class="who" title="${esc(state.email)}">${workspace}<b>${esc(who)}</b>${pill}</span>`;
}

// ---- account (your own sign-in) ------------------------------------------------

function accountView() {
  return `
  <span class="pf-eyebrow pf-eyebrow--teal"><span class="spark"></span>Account</span>
  <h1>Your sign-in</h1>
  <p class="sub">You're signed in as <b>${esc(state.email || "this account")}</b>. The same email and password work for Admin and the Operator Portal.</p>
  ${state.accountError ? `<div class="formerr" role="alert"><i data-lucide="shield-alert"></i><span>${esc(state.accountError)}</span></div>` : ""}
  ${state.accountNotice ? `<div class="formok"><i data-lucide="check"></i><span>${esc(state.accountNotice)}</span></div>` : ""}
  <div class="ad-card">
    <div class="card-head"><i data-lucide="key-round"></i>Change password</div>
    <div style="max-width:420px">
      <label class="fld" for="acc_current"><span class="cap">Current password</span><input class="inp" id="acc_current" type="password" autocomplete="current-password"><span class="field-err" id="accCurrentErr"></span></label>
      <label class="fld" for="acc_new"><span class="cap">New password</span><input class="inp" id="acc_new" type="password" placeholder="At least 12 characters" autocomplete="new-password"><span class="field-err" id="accNewErr"></span></label>
      <label class="fld" for="acc_new2"><span class="cap">Confirm new password</span><input class="inp" id="acc_new2" type="password" autocomplete="new-password"><span class="field-err" id="accNew2Err"></span></label>
    </div>
    <div class="ad__actions" style="border:none;padding:0;margin-top:4px">
      ${isSsoSession() ? `<span class="saved-at">Signed in with single sign-on? Your password lives with your identity provider, not here.</span>` : ""}
      <div class="grow"></div>
      <button class="pf-btn pf-btn--primary" id="accBtn" type="button"><i data-lucide="save"></i>Update password</button>
    </div>
  </div>
  <div class="ad-card">
    <div class="card-head"><i data-lucide="life-buoy"></i>Locked out?</div>
    <p class="ad-empty" style="margin:0">Anyone can ask for a reset link from the <a href="reset.html" style="color:var(--uc-teal-700)">forgot password</a> page. Admins can also send a teammate a reset link from <b>Team → Members</b>.</p>
  </div>`;
}

// Native email + password sessions are "native_owner" / "native_member";
// anything else came through an identity provider.
function isSsoSession() {
  const source = String(claimsFromToken(state.token).auth_source || "");
  return !!source && !source.startsWith("native_");
}

function sectionView() {
  switch (state.section) {
    case "applications":
      return applicationsView();
    case "usage":
      return usageView();
    case "billing":
      return billingView();
    case "team":
      return teamView();
    case "keys":
      return keysView();
    case "integrations":
      return integrationsView();
    case "widget":
      return widgetView();
    case "install":
      return installView();
    case "switch":
      return switchView();
    case "assist":
      return assistView();
    case "compliance":
      return complianceView();
    case "account":
      return accountView();
    default:
      return overviewView();
  }
}

function wireConsole() {
  // nav (also used by checklist/install "open" buttons)
  panel.querySelectorAll("[data-nav]").forEach((btn) => {
    btn.onclick = () => gotoSection(btn.getAttribute("data-nav"));
  });

  // copy buttons (inline value or by element id)
  panel.querySelectorAll("[data-copy]").forEach((btn) => {
    btn.onclick = () => copyText(btn.getAttribute("data-copy"), btn);
  });
  panel.querySelectorAll("[data-copy-id]").forEach((btn) => {
    btn.onclick = () => {
      const el = $(btn.getAttribute("data-copy-id"));
      if (el) copyText(el.textContent, btn);
    };
  });

  if (state.section === "applications") wireApplications();
  else if (state.section === "team") wireTeam();
  else if (state.section === "account") wireAccount();
  else if (state.section === "keys") wireKeys();
  else if (state.section === "integrations") wireIntegrations();
  else if (state.section === "widget") wireEditor();
  else if (state.section === "install") wireInstall();
  else if (state.section === "switch") wireSwitch();
  else if (state.section === "assist") wireAssist();
  else if (state.section === "billing") wireBilling();
}

function gotoSection(id) {
  if (!SECTIONS.some((s) => s.id === id)) return;
  state.section = id;
  state.sectionError = "";
  state.notice = "";
  // Never let a revealed API-key secret survive navigation away from Keys.
  state.revealedSecret = null;
  window.location.hash = id;
  render();
  loadSection();
}

async function copyText(text, btn) {
  try {
    await navigator.clipboard.writeText(text);
    if (btn) {
      const old = btn.innerHTML;
      btn.innerHTML = `<i data-lucide="check"></i>Copied`;
      mountIcons();
      setTimeout(() => {
        btn.innerHTML = old;
        mountIcons();
      }, 1400);
    }
  } catch {
    /* clipboard denied — the text is selectable */
  }
}

// ---- console data loading -------------------------------------------------------

// Every read a section needs, as independent promises. Nothing here is allowed
// to leave a section on a spinner: the loads are settled (not raced), the first
// failure becomes a banner, and the page chrome renders regardless.
function sectionLoads() {
  // Workspace + members feed the header identity on every page (both cached).
  // Billing is probed once per session so the nav item is hidden up front on
  // deployments where it isn't mounted, rather than vanishing after a click.
  const common = [ensureWorkspace(), ensureMembers(state.section === "team")];
  if (state.billingAvailable === null && state.section !== "billing") common.push(ensureBilling());
  switch (state.section) {
    case "overview":
      return [...common, ensureUsage(), ensureKeys(), ensureApplications().then(() => ensureConfig())];
    case "applications":
      return [...common, ensureApplications(true), ensureApplicationAccess(true)];
    case "usage":
      return [...common, ensureUsageSummary(true), ensureUsage(true), ensureApplications()];
    case "billing":
      return [...common, ensureBilling(true)];
    case "team":
      return [...common, ensureInvites(true)];
    case "keys":
      return [...common, ensureKeys(true), ensureApplications()];
    case "integrations":
      return [...common, ensureIntegrations(true), ensureAlerts(true), ensureKeys(true), ensureApplications()];
    case "widget":
      return [...common, ensureApplications().then(() => ensureConfig(true))];
    case "install":
    case "switch":
      return [...common, ensureApplications().then(() => ensureConfig())];
    case "assist":
      return [...common, ensureAssistPolicy(true)];
    case "compliance":
      return [...common, ensureRetention()];
    default:
      return common;
  }
}

async function loadSection() {
  const section = state.section;
  const results = await Promise.allSettled(sectionLoads());
  // The user may have moved on while these were in flight.
  if (state.view !== "console" || state.section !== section) return;
  const failures = results.filter((r) => r.status === "rejected").map((r) => r.reason);
  const unauthorized = failures.find(isUnauthorized);
  if (unauthorized) {
    authGuard(unauthorized);
    return;
  }
  const forbidden = failures.find(isForbidden);
  if (forbidden) {
    showWrongDoor();
    return;
  }
  if (failures.length) {
    state.sectionError = failures[0].message || "Couldn't load this section.";
  }
  render();
}

// Reads that degrade on older deployments still have to surface auth failures.
function rethrowAuth(e) {
  if (isUnauthorized(e) || isForbidden(e)) throw e;
}

async function ensureWorkspace(force) {
  if (state.workspace && !force) return;
  try {
    state.workspace = await getWorkspace(state.token);
  } catch (e) {
    rethrowAuth(e);
    // Older deployments may not expose /admin/workspace yet — degrade gracefully.
    state.workspace = { name: "Your workspace", tenant_id: "", status: "active", created_at: "" };
  }
}

async function ensureUsage(force) {
  if (state.usage !== null && !force) return;
  state.usage = await listUsage(state.token);
}

async function ensureUsageSummary(force) {
  if (state.usageSummary !== null && !force) return;
  try {
    state.usageSummary = await getUsageSummary(state.token);
  } catch (e) {
    rethrowAuth(e);
    // Older deployments may not expose /admin/usage/summary yet — degrade.
    state.usageSummary = undefined;
  }
}

async function ensureBilling(force) {
  if (state.billing !== null && !force) return;
  try {
    state.billing = await getBillingState(state.token);
    state.billingAvailable = true;
  } catch (e) {
    rethrowAuth(e);
    // Billing is off on this deployment (route not mounted) — degrade to the
    // honest note and drop the nav item so nobody clicks into a dead end.
    state.billing = undefined;
    state.billingAvailable = false;
  }
}

async function ensureMembers(force) {
  if (state.members !== null && !force) return;
  state.members = await listMembers(state.token);
}

async function ensureInvites(force) {
  if (state.invites !== null && !force) return;
  state.invites = await listInvites(state.token);
}

async function ensureKeys(force) {
  if (state.keys !== null && !force) return;
  state.keys = await listKeys(state.token);
}

async function ensureApplications(force) {
  if (state.applications !== null && !force) return;
  state.applications = await listApplications(state.token);
}

async function ensureApplicationAccess(force) {
  if (state.applicationAccess !== null && !force) return;
  state.applicationAccess = await listApplicationAccess(state.token);
}

// Webhook endpoints for the Integrations page. A 404 means the routes aren't
// mounted on this server (older deployment); other failures stay on the card
// so the page never sits on a spinner. Inline test results and open delivery
// tables survive a reload of the list.
async function ensureIntegrations(force) {
  if (state.integrations && !force) return;
  const prev = state.integrations || {};
  const next = {
    available: true,
    endpoints: [],
    eventTypes: DEFAULT_WEBHOOK_EVENTS,
    storageReady: true,
    loadError: "",
    tests: prev.tests || {}, // webhook_id -> { result } | { error }
    deliveries: prev.deliveries || {}, // webhook_id -> { open, loading, items, error }
  };
  try {
    const data = (await listWebhooks(state.token)) || {};
    next.endpoints = Array.isArray(data.endpoints) ? data.endpoints : [];
    if (Array.isArray(data.event_types) && data.event_types.length) next.eventTypes = data.event_types;
    next.storageReady = data.storage_ready !== false;
  } catch (e) {
    rethrowAuth(e);
    const code = errCode(e);
    if (code === "HTTP_404" || code === "NOT_FOUND") {
      state.integrations = { ...next, available: false };
      return;
    }
    next.loadError = code === "NETWORK_ERROR" ? e.message : "Couldn't load your connected apps. Try again in a minute.";
  }
  state.integrations = next;
}

async function ensureRetention(force) {
  if (state.retention !== null && !force) return;
  try {
    state.retention = await getRetentionPolicy(state.token);
  } catch (e) {
    rethrowAuth(e);
    state.retention = undefined; // renders the unavailable note
  }
}

async function ensureConfig(force) {
  const binding = parseApplicationEnvironmentBinding();
  const matchesBinding =
    state.config?.application_id === binding.applicationID &&
    state.config?.environment_id === binding.environmentID;
  if (state.config && matchesBinding && !force) return;
  try {
    const data = await getAdminWidgetConfig(
      state.token,
      binding.applicationID,
      binding.environmentID,
    );
    state.config = mergeConfig(data, binding);
  } catch (e) {
    rethrowAuth(e);
    state.loadError = e.message || "Couldn't load the widget configuration.";
    state.config = mergeConfig(null, binding);
  }
}

// ---- team actions ----------------------------------------------------------------

function wireTeam() {
  const btn = $("invBtn");
  if (btn) btn.onclick = () => doInvite(btn);

  const dismiss = $("dismissInvLink");
  if (dismiss)
    dismiss.onclick = () => {
      state.inviteLink = null;
      render();
    };

  panel.querySelectorAll("[data-revoke-invite]").forEach((b) => {
    b.onclick = () => doRevokeInvite(b.getAttribute("data-revoke-invite"), b);
  });
  panel.querySelectorAll("[data-role-user]").forEach((sel) => {
    sel.onchange = () => {
      const name = sel.getAttribute("data-role-name") || "this teammate";
      if (!window.confirm(`Change ${name}'s role to ${label("role", sel.value)}?`)) {
        sel.value = sel.getAttribute("data-role-current") || sel.value;
        return;
      }
      doChangeRole(sel.getAttribute("data-role-user"), sel.value, sel);
    };
  });
  panel.querySelectorAll("[data-remove-member]").forEach((b) => {
    b.onclick = () => doRemoveMember(b.getAttribute("data-remove-member"), b);
  });
  panel.querySelectorAll("[data-reset-member]").forEach((b) => {
    b.onclick = () => doSendMemberReset(b.getAttribute("data-reset-member"), b.getAttribute("data-reset-email"), b);
  });
  const dismissReset = $("dismissResetLink");
  if (dismissReset)
    dismissReset.onclick = () => {
      state.resetLink = null;
      render();
    };
}

async function doSendMemberReset(userId, email, btn) {
  if (state.busy || !window.confirm(`Send ${email} a password reset link? Any earlier reset link for them stops working.`)) return;
  state.busy = true;
  state.sectionError = "";
  state.notice = "";
  setBusy(btn, true, "Sending…");
  try {
    const link = await sendMemberPasswordReset(state.token, userId);
    if (link && link.reset_url) {
      // No mailer in this environment: surface the manual link once.
      state.resetLink = { email: link.email || email, url: link.reset_url };
      state.notice = `Reset link created for ${link.email || email}.`;
    } else {
      state.resetLink = null;
      state.notice = `Reset email sent to ${(link && link.email) || email}.`;
    }
  } catch (e) {
    if (authGuard(e)) return;
    state.sectionError = mapResetError(e);
  }
  state.busy = false;
  render();
}

function mapResetError(e) {
  const code = (e.code || "").toUpperCase();
  if (code === "FORBIDDEN") return "That member can't be reset from here — owners reset their own password from Account, and suspended members must be re-invited.";
  if (code === "NOT_FOUND") return "That member has no email + password sign-in (SSO accounts reset with their identity provider).";
  if (code === "EMAIL_DELIVERY_FAILED") return "We couldn't send the reset email. Please try again.";
  return e.message || "Couldn't send the reset link. Please try again.";
}

// ---- account actions --------------------------------------------------------

function wireAccount() {
  const btn = $("accBtn");
  if (!btn) return;
  ["acc_current", "acc_new", "acc_new2"].forEach((id) => {
    const el = $(id);
    if (el)
      el.oninput = () => {
        $("accCurrentErr").textContent = "";
        $("accNewErr").textContent = "";
        $("accNew2Err").textContent = "";
      };
  });
  const last = $("acc_new2");
  if (last)
    last.onkeydown = (e) => {
      if (e.key === "Enter") btn.click();
    };
  btn.onclick = () => doChangePassword(btn);
}

async function doChangePassword(btn) {
  if (state.accountBusy) return;
  const current = ($("acc_current") || {}).value || "";
  const next = ($("acc_new") || {}).value || "";
  const confirm = ($("acc_new2") || {}).value || "";
  let ok = true;
  if (!current) {
    $("accCurrentErr").textContent = "Enter your current password.";
    ok = false;
  }
  if (next.length < 12) {
    $("accNewErr").textContent = "Use at least 12 characters.";
    ok = false;
  }
  if (next !== confirm) {
    $("accNew2Err").textContent = "Passwords don't match.";
    ok = false;
  }
  if (!ok) return;

  state.accountBusy = true;
  state.accountError = "";
  state.accountNotice = "";
  setBusy(btn, true, "Updating…");
  try {
    // Passwords are read straight from the fields and handed to the API — never
    // stored in state, persisted, or logged.
    await changePassword(state.token, current, next);
    state.accountNotice = "Password updated. Use it next time you sign in to Admin or the Portal.";
  } catch (e) {
    if (authGuard(e)) return;
    const code = (e.code || "").toUpperCase();
    state.accountError =
      code === "UNAUTHORIZED"
        ? "That current password isn't right."
        : code === "FORBIDDEN"
          ? "This session can't change a password here — SSO accounts manage theirs with the identity provider."
          : code === "BAD_REQUEST"
            ? e.message || "Check the new password and try again."
            : e.message || "Couldn't update the password. Please try again.";
  }
  state.accountBusy = false;
  render();
}

async function doInvite(btn) {
  if (state.busy) return;
  const email = ($("inv_email") || {}).value || "";
  const role = ($("inv_role") || {}).value || "operator";
  if (!isEmail(email)) {
    $("invErr").textContent = "Enter a valid email.";
    return;
  }
  state.busy = true;
  state.sectionError = "";
  state.notice = "";
  setBusy(btn, true, "Sending…");
  try {
    const created = await createInvite({ token: state.token, email: email.trim(), orgRole: role });
    if (created && created.token) {
      // No mailer in this environment: surface the manual accept link once.
      const url = `${window.location.origin}/accept.html?tenant_id=${encodeURIComponent(created.tenant_id)}&token=${encodeURIComponent(created.token)}&email=${encodeURIComponent(created.email)}`;
      state.inviteLink = { email: created.email, url };
      state.notice = `Invite created for ${created.email}.`;
    } else {
      state.inviteLink = null;
      state.notice = `Invite email sent to ${email.trim()}.`;
    }
    await ensureInvites(true);
  } catch (e) {
    if (authGuard(e)) return;
    state.sectionError = mapTeamError(e);
  }
  state.busy = false;
  render();
}

function mapTeamError(e) {
  const code = (e.code || "").toUpperCase();
  if (code === "CONFLICT") return "There's already a pending invite or account for that email.";
  if (code === "EMAIL_DELIVERY_FAILED") return "We couldn't send the invite email. Please try again.";
  if (code === "FORBIDDEN") return "Owner/admin only — this account can't manage the team.";
  return e.message || "Team action failed. Please try again.";
}

async function doRevokeInvite(invitationId, btn) {
  if (state.busy || !window.confirm("Revoke this invite? The link stops working immediately.")) return;
  state.busy = true;
  setBusy(btn, true, "…");
  try {
    await revokeInvite(state.token, invitationId);
    state.notice = "Invite revoked.";
    await ensureInvites(true);
  } catch (e) {
    if (authGuard(e)) return;
    state.sectionError = mapTeamError(e);
  }
  state.busy = false;
  render();
}

async function doChangeRole(userId, role, sel) {
  if (state.busy) return;
  state.busy = true;
  sel.disabled = true;
  try {
    await changeMemberRole(state.token, userId, role);
    state.notice = "Role updated.";
    await ensureMembers(true);
  } catch (e) {
    if (authGuard(e)) return;
    state.sectionError = mapTeamError(e);
    await ensureMembers(true).catch(() => {});
  }
  state.busy = false;
  render();
}

async function doRemoveMember(userId, btn) {
  if (state.busy || !window.confirm("Remove this member? They lose access immediately.")) return;
  state.busy = true;
  setBusy(btn, true, "…");
  try {
    await removeMember(state.token, userId);
    state.notice = "Member removed.";
    await ensureMembers(true);
  } catch (e) {
    if (authGuard(e)) return;
    state.sectionError = mapTeamError(e);
  }
  state.busy = false;
  render();
}

// ---- application actions --------------------------------------------------------

function wireApplications() {
  const button = $("appCreateBtn");
  if (!button) return;
  // Slug follows the name until the admin edits it by hand (same as signup).
  const nameInput = $("app_name");
  const slugInput = $("app_slug");
  let slugEdited = false;
  if (nameInput && slugInput) {
    nameInput.oninput = () => {
      if (!slugEdited) slugInput.value = slugify(nameInput.value);
    };
    slugInput.oninput = () => {
      slugEdited = slugInput.value.trim() !== "";
    };
  }
  button.onclick = async () => {
    if (state.busy) return;
    const name = $("app_name").value.trim();
    const slug = $("app_slug").value.trim();
    const origins = parseOrigins($("app_origins").value);
    if (!name || !slug) {
      state.sectionError = "Application name and slug are required.";
      render();
      return;
    }
    state.busy = true;
    state.sectionError = "";
    setBusy(button, true, "Creating…");
    try {
      await createApplication({ token: state.token, name, slug, allowedOrigins: origins });
      state.notice = `${name} created. Configure its widget and verify install health before publishing.`;
      await ensureApplications(true);
    } catch (e) {
      if (authGuard(e)) return;
      state.sectionError = e.message || "Couldn't create the application.";
    }
    state.busy = false;
    render();
  };
  panel.querySelectorAll("[data-access-mode]").forEach((select) => {
    select.onchange = () => {
      const userID = select.getAttribute("data-access-mode");
      const locked = select.value === "workspace";
      panel.querySelectorAll(`[data-access-app="${userID}"]`).forEach((checkbox) => { checkbox.disabled = locked; });
      const checks = panel.querySelector(`[data-access-checks="${userID}"]`);
      if (checks) {
        checks.classList.toggle("is-locked", locked);
        checks.title = locked ? "Switch to 'Only selected' to choose" : "";
      }
    };
  });
  panel.querySelectorAll("[data-save-access]").forEach((saveButton) => {
    saveButton.onclick = async () => {
      if (state.busy) return;
      const userID = saveButton.getAttribute("data-save-access");
      const mode = panel.querySelector(`[data-access-mode="${userID}"]`).value;
      const applicationIds = [...panel.querySelectorAll(`[data-access-app="${userID}"]:checked`)].map((checkbox) => checkbox.value);
      state.busy = true;
      setBusy(saveButton, true, "Saving…");
      try {
        await setApplicationAccess(state.token, userID, mode, applicationIds);
        state.notice = "Application access updated. Revocation applies immediately.";
        await ensureApplicationAccess(true);
      } catch (e) {
        if (authGuard(e)) return;
        state.sectionError = e.message || "Couldn't update application access.";
      }
      state.busy = false;
      render();
    };
  });
}

// ---- key actions ----------------------------------------------------------------

function wireKeys() {
  const btn = $("keyBtn");
  if (btn) btn.onclick = () => doCreateKey(btn);

  const dismiss = $("dismissSecret");
  if (dismiss)
    dismiss.onclick = () => {
      state.revealedSecret = null;
      render();
    };

  panel.querySelectorAll("[data-rotate-key]").forEach((b) => {
    b.onclick = () => doRotateKey(b.getAttribute("data-rotate-key"), b);
  });
  panel.querySelectorAll("[data-revoke-key]").forEach((b) => {
    b.onclick = () => doRevokeKey(b.getAttribute("data-revoke-key"), b);
  });
}

async function doCreateKey(btn) {
  if (state.busy) return;
  const name = (($("key_name") || {}).value || "").trim();
  const keyType = ($("key_type") || {}).value || "secret";
  const binding = (($("key_binding") || {}).value || "").split("|");
  const applicationId = binding[0] || "";
  const environmentId = binding[1] || "";
  const actAs = Boolean(($("key_act_as") || {}).checked);
  if (!name) {
    $("keyErr").textContent = "Give the key a name.";
    return;
  }
  if (actAs && keyType !== "secret") {
    $("keyErr").textContent = "Only a secret key can reply as a team member.";
    return;
  }
  state.busy = true;
  state.sectionError = "";
  state.notice = "";
  setBusy(btn, true, "Creating…");
  try {
    const created = await createKey({ token: state.token, name, keyType, applicationId, environmentId, scopes: actAs ? REPLY_KEY_SCOPES : undefined });
    state.revealedSecret = { title: `Secret for “${name}”`, secret: created.secret };
    await ensureKeys(true);
  } catch (e) {
    if (authGuard(e)) return;
    state.sectionError = mapKeyError(e);
  }
  state.busy = false;
  render();
}

function mapKeyError(e) {
  const code = (e.code || "").toUpperCase();
  if (code === "FORBIDDEN") return "Owner/admin only — this account can't manage API keys.";
  if (code === "BAD_REQUEST") return e.message || "The key request was rejected. Check the fields.";
  return e.message || "Key action failed. Please try again.";
}

async function doRotateKey(keyId, btn) {
  if (state.busy || !window.confirm("Rotate this key? The old secret stops working immediately.")) return;
  state.busy = true;
  setBusy(btn, true, "…");
  try {
    const rotated = await rotateKey(state.token, keyId);
    state.revealedSecret = { title: "New secret (rotated)", secret: rotated.secret };
    state.notice = "Key rotated.";
    await ensureKeys(true);
  } catch (e) {
    if (authGuard(e)) return;
    state.sectionError = mapKeyError(e);
  }
  state.busy = false;
  render();
}

async function doRevokeKey(keyId, btn) {
  if (state.busy || !window.confirm("Revoke this key? Requests using it will fail immediately.")) return;
  state.busy = true;
  setBusy(btn, true, "…");
  try {
    await revokeKey(state.token, keyId);
    state.notice = "Key revoked.";
    await ensureKeys(true);
  } catch (e) {
    if (authGuard(e)) return;
    state.sectionError = mapKeyError(e);
  }
  state.busy = false;
  render();
}

// ---- integration actions ------------------------------------------------------------

function webhookById(id) {
  const g = state.integrations;
  return (g && g.endpoints.find((ep) => ep.webhook_id === id)) || null;
}

function mapWebhookError(e) {
  const code = errCode(e);
  if (code === "FORBIDDEN") return "Owner/admin only — this account can't manage connected apps.";
  if (code === "LIMIT_REACHED") return e.message || "This workspace already has the most connected apps allowed. Remove one first.";
  if (code === "NOT_CONFIGURED") return `This server can't store signing secrets safely yet. Ask your ${brand.name} contact.`;
  if (code === "RATE_LIMITED") return "Too many tries in a row. Wait a minute and try again.";
  if (code === "NOT_FOUND") return "That app isn't connected any more. Reload the page.";
  return e.message || "That didn't work. Please try again.";
}

// The one-time secret card sits at the top of the page; bring it into view.
function showRevealedSecret() {
  const card = $("dismissSecret");
  if (card && card.scrollIntoView) card.scrollIntoView({ block: "center", behavior: "smooth" });
}

function wireIntegrations() {
  const dismiss = $("dismissSecret");
  if (dismiss)
    dismiss.onclick = () => {
      state.revealedSecret = null;
      render();
    };
  const alertsSave = $("alertsSave");
  if (alertsSave) alertsSave.onclick = () => doSaveAlerts(alertsSave);
  const slackToggle = (edit, remove) => () => {
    state.alerts.slackEdit = edit;
    state.alerts.slackRemove = remove;
    render();
    if (edit && $("al_slack_url")) $("al_slack_url").focus();
  };
  const slackReplace = $("alertsSlackReplace");
  if (slackReplace) slackReplace.onclick = slackToggle(true, false);
  const slackRemove = $("alertsSlackRemove");
  if (slackRemove) slackRemove.onclick = slackToggle(false, true);
  const slackKeep = $("alertsSlackKeep");
  if (slackKeep) slackKeep.onclick = slackToggle(false, false);
  const alertEmail = $("al_email");
  if (alertEmail) {
    alertEmail.onchange = () => {
      panel.querySelectorAll("[data-alert-recipient]").forEach((box) => {
        if (!box.closest("label").title.startsWith("Signs in with SSO")) box.disabled = !alertEmail.checked || state.viewOnly;
      });
    };
  }
  const g = state.integrations;
  if (!g || !g.available) return;

  const connect = $("whConnect");
  if (connect) connect.onclick = () => doConnectWebhook(connect);
  const hasSecret = $("wh_has_secret");
  if (hasSecret) {
    hasSecret.onchange = () => {
      const field = $("whSecretField");
      if (field) field.hidden = !hasSecret.checked;
      if (hasSecret.checked && $("wh_secret")) $("wh_secret").focus();
    };
  }
  [
    ["wh_name", "whNameErr"],
    ["wh_url", "whUrlErr"],
  ].forEach(([inputId, errId]) => {
    const input = $(inputId);
    if (input) input.oninput = () => ($(errId).textContent = "");
  });
  panel.querySelectorAll("[data-wh-event]").forEach((box) => {
    box.onchange = () => ($("whEventsErr").textContent = "");
  });

  const replyKey = $("whReplyKey");
  if (replyKey) replyKey.onclick = () => doCreateReplyKey(replyKey);

  panel.querySelectorAll("[data-wh-test]").forEach((b) => {
    b.onclick = () => doTestWebhook(b.getAttribute("data-wh-test"), b);
  });
  panel.querySelectorAll("[data-wh-toggle]").forEach((b) => {
    b.onclick = () => doToggleWebhook(b.getAttribute("data-wh-toggle"), b);
  });
  panel.querySelectorAll("[data-wh-rotate]").forEach((b) => {
    b.onclick = () => doRotateWebhook(b.getAttribute("data-wh-rotate"), b);
  });
  panel.querySelectorAll("[data-wh-remove]").forEach((b) => {
    b.onclick = () => doRemoveWebhook(b.getAttribute("data-wh-remove"), b);
  });
  panel.querySelectorAll("[data-wh-deliveries]").forEach((b) => {
    b.onclick = () => toggleWebhookDeliveries(b.getAttribute("data-wh-deliveries"));
  });
  panel.querySelectorAll("[data-wh-retry]").forEach((b) => {
    b.onclick = () => doRetryDelivery(b.getAttribute("data-wh-retry"), b.getAttribute("data-wh-delivery"), b);
  });
}

async function doConnectWebhook(btn) {
  if (state.busy) return;
  const name = (($("wh_name") || {}).value || "").trim();
  const url = (($("wh_url") || {}).value || "").trim();
  const eventTypes = Array.from(panel.querySelectorAll("[data-wh-event]"))
    .filter((box) => box.checked)
    .map((box) => box.value);
  let ok = true;
  if (!name) {
    $("whNameErr").textContent = "Give the app a name.";
    ok = false;
  }
  if (!/^https:\/\/\S+$/i.test(url)) {
    $("whUrlErr").textContent = "Enter the full address, starting with https://";
    ok = false;
  }
  if (!eventTypes.length) {
    $("whEventsErr").textContent = "Pick at least one event.";
    ok = false;
  }
  const useExisting = Boolean(($("wh_has_secret") || {}).checked);
  const signingSecret = useExisting ? (($("wh_secret") || {}).value || "").trim() : "";
  if (useExisting && signingSecret.length < 32) {
    $("whSecretErr").textContent = "Paste the app's secret (at least 32 characters).";
    ok = false;
  }
  if (!ok) return;
  state.busy = true;
  state.sectionError = "";
  state.notice = "";
  setBusy(btn, true, "Connecting…");
  try {
    const created = await createWebhook(state.token, { name, url, eventTypes, signingSecret });
    if (created.secret_supplied) {
      state.notice = `${name} is connected with the app's own secret. Send a test event to check it.`;
    } else {
      state.revealedSecret = {
        title: `Signing secret for “${name}”`,
        secret: created.signing_secret,
        hint: "Paste it into the app's settings now. For unipy this is IRIS_BRIDGE_SIGNING_SECRET.",
      };
      state.notice = `${name} is connected. Send a test event once the app has its secret.`;
    }
    await ensureIntegrations(true);
  } catch (e) {
    if (authGuard(e)) return;
    state.sectionError = mapWebhookError(e);
  }
  state.busy = false;
  render();
  if (state.revealedSecret) showRevealedSecret();
}

async function doTestWebhook(id, btn) {
  if (state.busy) return;
  const tests = state.integrations.tests;
  state.busy = true;
  delete tests[id];
  setBusy(btn, true, "Sending…");
  try {
    tests[id] = { result: (await testWebhook(state.token, id)) || {} };
  } catch (e) {
    if (authGuard(e)) return;
    tests[id] = { error: mapWebhookError(e) };
  }
  state.busy = false;
  render();
}

async function doToggleWebhook(id, btn) {
  const ep = webhookById(id);
  if (state.busy || !ep) return;
  const resume = !ep.is_active;
  state.busy = true;
  state.sectionError = "";
  state.notice = "";
  setBusy(btn, true, "…");
  try {
    await updateWebhook(state.token, id, { is_active: resume });
    state.notice = resume ? `${ep.name} resumed. Anything waiting goes out now.` : `${ep.name} paused. New events wait here until you resume.`;
    await ensureIntegrations(true);
  } catch (e) {
    if (authGuard(e)) return;
    state.sectionError = mapWebhookError(e);
  }
  state.busy = false;
  render();
}

async function doRotateWebhook(id, btn) {
  const ep = webhookById(id);
  if (state.busy || !ep) return;
  if (!window.confirm(`Make a new signing secret for ${ep.name}? The old one keeps working for 24 hours, so there is time to update the app.`)) return;
  state.busy = true;
  state.sectionError = "";
  state.notice = "";
  setBusy(btn, true, "…");
  try {
    const rotated = await rotateWebhookSecret(state.token, id);
    const until = rotated.endpoint && rotated.endpoint.previous_secret_expires_at;
    state.revealedSecret = {
      title: `New signing secret for “${ep.name}”`,
      secret: rotated.signing_secret,
      hint: `Paste it into the app's settings now. For unipy this is IRIS_BRIDGE_SIGNING_SECRET.${until ? ` The old secret works until ${formatStamp(until)}.` : ""}`,
    };
    await ensureIntegrations(true);
  } catch (e) {
    if (authGuard(e)) return;
    state.sectionError = mapWebhookError(e);
  }
  state.busy = false;
  render();
  if (state.revealedSecret) showRevealedSecret();
}

async function doRemoveWebhook(id, btn) {
  const ep = webhookById(id);
  if (state.busy || !ep) return;
  if (!window.confirm(`Remove ${ep.name}? It stops getting events, and anything still waiting to send is dropped.`)) return;
  state.busy = true;
  state.sectionError = "";
  state.notice = "";
  setBusy(btn, true, "…");
  try {
    await deleteWebhook(state.token, id);
    delete state.integrations.tests[id];
    delete state.integrations.deliveries[id];
    state.notice = `${ep.name} removed.`;
    await ensureIntegrations(true);
  } catch (e) {
    if (authGuard(e)) return;
    state.sectionError = mapWebhookError(e);
  }
  state.busy = false;
  render();
}

async function loadWebhookDeliveries(id) {
  const d = state.integrations.deliveries[id];
  if (!d) return;
  d.loading = true;
  d.error = "";
  render();
  try {
    const data = await listWebhookDeliveries(state.token, id);
    d.items = Array.isArray(data) ? data : Array.isArray(data && data.deliveries) ? data.deliveries : [];
  } catch (e) {
    if (authGuard(e)) return;
    d.error = mapWebhookError(e);
  }
  d.loading = false;
  render();
}

function toggleWebhookDeliveries(id) {
  const all = state.integrations.deliveries;
  const d = all[id] || (all[id] = { open: false, loading: false, items: null, error: "" });
  d.open = !d.open;
  if (d.open) loadWebhookDeliveries(id);
  else render();
}

async function doRetryDelivery(id, deliveryId, btn) {
  if (state.busy || !deliveryId) return;
  state.busy = true;
  state.sectionError = "";
  state.notice = "";
  setBusy(btn, true, "…");
  try {
    await retryWebhookDelivery(state.token, id, deliveryId);
    state.notice = "Queued to send again.";
    await ensureIntegrations(true);
  } catch (e) {
    if (authGuard(e)) return;
    state.sectionError = mapWebhookError(e);
  }
  state.busy = false;
  await loadWebhookDeliveries(id);
}

async function doCreateReplyKey(btn) {
  if (state.busy) return;
  const existing = Array.isArray(state.keys)
    ? state.keys.filter((k) => !k.revoked_at && Array.isArray(k.scopes) && k.scopes.includes(SCOPE_OPERATORS_ACT_AS))
    : [];
  if (existing.length && !window.confirm("This workspace already has a reply key. Make another one?")) return;
  const { applicationID, environmentID } = parseApplicationEnvironmentBinding(defaultApplicationEnvironmentBinding());
  state.busy = true;
  state.sectionError = "";
  state.notice = "";
  setBusy(btn, true, "Creating…");
  try {
    const created = await createKey({
      token: state.token,
      name: REPLY_KEY_NAME,
      keyType: "secret",
      applicationId: applicationID,
      environmentId: environmentID,
      scopes: REPLY_KEY_SCOPES,
    });
    state.revealedSecret = {
      title: `Secret for “${REPLY_KEY_NAME}”`,
      secret: created.secret,
      hint: "Paste it into the app's settings now. For unipy: IRIS_API_KEY",
    };
    await ensureKeys(true);
  } catch (e) {
    if (authGuard(e)) return;
    state.sectionError = mapKeyError(e);
  }
  state.busy = false;
  render();
  if (state.revealedSecret) showRevealedSecret();
}

// ---- login / logout ---------------------------------------------------------------

function wireLogin() {
  const email = $("f_email");
  const pass = $("f_pass");
  const btn = $("loginBtn");
  if (!email) return;

  email.oninput = () => {
    state.email = email.value;
    $("emailErr").textContent = "";
  };
  pass.oninput = () => {
    $("passErr").textContent = "";
  };
  pass.onkeydown = (e) => {
    if (e.key === "Enter") btn.click();
  };

  btn.onclick = () => doLogin(email.value, pass.value, btn);
}

async function doLogin(email, password, btn) {
  if (state.loginBusy) return;
  state.email = email;
  state.loginError = "";
  let ok = true;
  if (!isEmail(email)) {
    $("emailErr").textContent = "Enter a valid email.";
    ok = false;
  }
  if (!password) {
    $("passErr").textContent = "Enter your password.";
    ok = false;
  }
  if (!ok) return;

  state.loginBusy = true;
  setBusy(btn, true, "Signing in…");
  try {
    // NOTE: password is read straight from the field and handed to the API —
    // never stored in state, persisted, or logged.
    const session = await ownerLogin(email.trim(), password);
    const token = session && session.token;
    if (!token) {
      throw Object.assign(new Error("Sign-in did not return a session token."), {
        code: "NO_TOKEN",
      });
    }
    state.token = token;
    state.role = String((session.session && session.session.role) || roleFromToken(token) || "");
    const access = accessFromScopes(state.role, session.session && session.session.scopes);
    state.viewOnly = access === VIEW_ONLY_ACCESS;
    sessionStorage.setItem(TOKEN_KEY, token);
    sessionStorage.setItem(EMAIL_KEY, email.trim());
    sessionStorage.setItem(ROLE_KEY, state.role);
    if (access) sessionStorage.setItem(ACCESS_KEY, access);
    else sessionStorage.removeItem(ACCESS_KEY);
    enterConsole();
  } catch (e) {
    state.loginError = mapAuthError(e);
    state.loginBusy = false;
    render();
  }
}

function mapAuthError(e) {
  const code = (e.code || "").toUpperCase();
  if (code === "UNAUTHORIZED" || code === "FORBIDDEN" || code === "HTTP_401") {
    return "Invalid email or password.";
  }
  if (code === "BAD_REQUEST") return e.message || "Check your details and try again.";
  return e.message || "Could not sign in. Please try again.";
}

function doSignOut(message) {
  state.token = "";
  state.role = "";
  state.viewOnly = false;
  state.assist = null;
  state.integrations = null;
  state.config = null;
  state.widgetBinding = "";
  state.workspace = null;
  state.usage = null;
  state.usageSummary = null;
  state.billing = null;
  state.members = null;
  state.invites = null;
  state.keys = null;
  state.retention = null;
  state.applications = null;
  state.applicationAccess = null;
  state.revealedSecret = null;
  state.inviteLink = null;
  state.loginError = message || "";
  state.saveError = "";
  state.saveOk = false;
  state.loadError = "";
  state.sectionError = "";
  state.notice = "";
  state.loginBusy = false;
  state.busy = false;
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(ROLE_KEY);
  sessionStorage.removeItem(ACCESS_KEY);
  state.view = "login";
  render();
}

function enterConsole() {
  state.loginBusy = false;
  if (!isAdminRole()) {
    showWrongDoor();
    return;
  }
  state.view = "console";
  render();
  loadSection();
}

// ---- widget editor wiring (unchanged behavior) -------------------------------------

function mergeConfig(data, binding = {}) {
  const d = data || {};
  const b = d.branding || {};
  return {
    application_id: d.application_id || binding.applicationID || "",
    environment_id: d.environment_id || binding.environmentID || "",
    branding: {
      display_name: b.display_name || "",
      logo_url: b.logo_url || "",
      primary_color: b.primary_color || DEFAULT_CONFIG.branding.primary_color,
      accent_color: b.accent_color || DEFAULT_CONFIG.branding.accent_color,
      widget_title: b.widget_title || "",
    },
    welcome_message: d.welcome_message || "",
    // null (or a server that doesn't send it) = the product default note.
    composer_note: typeof d.composer_note === "string" ? d.composer_note : null,
    offline_email_capture: d.offline_email_capture !== false,
    offline_message: typeof d.offline_message === "string" ? d.offline_message : "",
    launcher_position: d.launcher_position || DEFAULT_CONFIG.launcher_position,
    allowed_origins: Array.isArray(d.allowed_origins) ? d.allowed_origins : [],
    is_published: !!d.is_published,
    updated_at: d.updated_at || "",
  };
}

function wireEditor() {
  if (state.loading || !state.config) return;
  const b = state.config.branding;

  const binding = $("widget_binding");
  if (binding) binding.onchange = () => changeWidgetBinding(binding.value);

  bindText("f_display", (v) => (b.display_name = v));
  bindText("f_title", (v) => (b.widget_title = v));
  bindText("f_logo", (v) => (b.logo_url = v), { preview: true });
  bindText("f_welcome", (v) => (state.config.welcome_message = v));

  const noteOn = $("f_note_on");
  const note = $("f_note");
  if (noteOn && note) {
    noteOn.onchange = () => {
      note.disabled = !noteOn.checked;
      state.config.composer_note = noteOn.checked ? composerNoteValue(note.value) : "";
      clearSaveState();
    };
    note.oninput = () => {
      state.config.composer_note = composerNoteValue(note.value);
      clearSaveState();
    };
  }

  const offlineCapture = $("f_offline_capture");
  if (offlineCapture) {
    offlineCapture.onchange = () => {
      state.config.offline_email_capture = offlineCapture.checked;
      clearSaveState();
    };
  }
  bindText("f_offline_message", (v) => (state.config.offline_message = v));

  bindColorPair("f_primary", "f_primary_c", (v) => (b.primary_color = v));
  bindColorPair("f_accent", "f_accent_c", (v) => (b.accent_color = v));

  const launcher = $("f_launcher");
  if (launcher) launcher.onchange = () => (state.config.launcher_position = launcher.value);

  const origins = $("f_origins");
  if (origins)
    origins.oninput = () => {
      state.config.allowed_origins = parseOrigins(origins.value);
    };

  const pub = $("f_published");
  if (pub) pub.onchange = () => (state.config.is_published = pub.checked);

  const saveBtn = $("saveBtn");
  if (saveBtn) saveBtn.onclick = () => doSave(saveBtn);

  paintPreview();
}

function wireInstall() {
  const binding = $("install_binding");
  if (binding) binding.onchange = () => changeWidgetBinding(binding.value);
}

function wireSwitch() {
  const binding = $("switch_binding");
  if (binding) binding.onchange = () => changeWidgetBinding(binding.value);
  wireDriftImport();
}

async function changeWidgetBinding(value) {
  if (!value || value === state.widgetBinding) return;
  state.widgetBinding = value;
  state.config = null;
  state.loadError = "";
  state.saveError = "";
  state.saveOk = false;
  render();
  await ensureConfig(true);
  render();
}

function bindText(id, set, opts = {}) {
  const el = $(id);
  if (!el) return;
  el.oninput = () => {
    set(el.value);
    clearSaveState();
    if (opts.preview) paintPreview();
  };
}

function bindColorPair(textId, colorId, set) {
  const text = $(textId);
  const color = $(colorId);
  if (!text || !color) return;
  text.oninput = () => {
    set(text.value);
    if (isHexish(text.value)) {
      const hex = normHex(text.value, color.value);
      color.value = hex;
    }
    clearSaveState();
    paintPreview();
  };
  color.oninput = () => {
    text.value = color.value;
    set(color.value);
    clearSaveState();
    paintPreview();
  };
}

// A shown note saves as its text; the default text (or a blank box) saves as
// null so the workspace keeps following the product default.
function composerNoteValue(text) {
  const value = String(text || "").trim();
  return value && value !== DEFAULT_COMPOSER_NOTE ? value : null;
}

function parseOrigins(text) {
  return text
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
}

function clearSaveState() {
  if (state.saveOk || state.saveError) {
    state.saveOk = false;
    state.saveError = "";
    const ok = panel.querySelector(".formok");
    if (ok) ok.remove();
    const err = panel.querySelector(".formerr");
    if (err) err.remove();
  }
}

function paintPreview() {
  const box = $("preview");
  if (!box || !state.config) return;
  const b = state.config.branding;
  const primary = normHex(b.primary_color, DEFAULT_CONFIG.branding.primary_color);
  const accent = normHex(b.accent_color, DEFAULT_CONFIG.branding.accent_color);
  const title = b.widget_title || b.display_name || `${brand.name} chat`;
  const logo = b.logo_url
    ? `<img src="${esc(b.logo_url)}" alt="" onerror="this.style.display='none'">`
    : `<i data-lucide="message-circle" style="color:#fff;width:15px;height:15px"></i>`;
  box.innerHTML = `
    <div class="pv-head"><i data-lucide="palette"></i>Live preview</div>
    <div class="pv-bar" style="background:${esc(primary)}">
      <span class="pv-logo">${logo}</span>
      <span class="pv-title">${esc(title)}</span>
    </div>
    <div class="pv-swatches">
      <span class="pv-chip"><span class="dot" style="background:${esc(primary)}"></span>${esc(b.primary_color || primary)}</span>
      <span class="pv-chip"><span class="dot" style="background:${esc(accent)}"></span>${esc(b.accent_color || accent)}</span>
      <span class="pv-chip" style="margin-left:auto">Launcher <span class="pv-launch" style="background:${esc(accent)}"></span></span>
    </div>`;
  mountIcons();
}

async function doSave(btn) {
  if (state.saveBusy || !state.config) return;
  state.saveBusy = true;
  state.saveError = "";
  state.saveOk = false;
  setBusy(btn, true, "Saving…");

  const c = state.config;
  const body = {
    application_id: c.application_id,
    environment_id: c.environment_id,
    branding: {
      display_name: c.branding.display_name,
      logo_url: c.branding.logo_url,
      primary_color: c.branding.primary_color,
      accent_color: c.branding.accent_color,
      widget_title: c.branding.widget_title,
    },
    welcome_message: c.welcome_message,
    composer_note: c.composer_note,
    offline_email_capture: c.offline_email_capture !== false,
    offline_message: (c.offline_message || "").trim(),
    launcher_position: c.launcher_position,
    allowed_origins: c.allowed_origins,
    is_published: c.is_published,
  };

  try {
    await putWidgetConfig(state.token, body);
    state.config.updated_at = new Date().toISOString();
    state.saveOk = true;
    state.saveBusy = false;
    render();
  } catch (e) {
    if (authGuard(e)) return;
    const code = (e.code || "").toUpperCase();
    if (code === "FORBIDDEN") {
      state.saveError = "Owner/admin only — this account can't change the widget config.";
    } else if (code === "BAD_REQUEST") {
      state.saveError = e.message || "Some values were rejected. Check the fields and try again.";
    } else {
      state.saveError = e.message || "Couldn't save changes. Please try again.";
    }
    state.saveBusy = false;
    render();
  }
}

function setBusy(btn, busy, label) {
  if (!btn) return;
  btn.disabled = busy;
  if (busy) btn.innerHTML = `<span class="btnspin"></span>${esc(label || "Working…")}`;
}

// ---- boot ------------------------------------------------------------------------

applyBrand();
window.addEventListener("hashchange", () => {
  const s = sectionFromHash();
  if (s && state.view === "console" && s !== state.section) {
    state.section = s;
    render();
    loadSection();
  }
});

if (state.token) {
  enterConsole();
} else {
  render();
}
